const React = require('react');
const ReactDOM = require('react-dom');
const { createElement: h, useEffect, useMemo, useRef, useState } = React;
const WorkflowNode = require('./WorkflowNode');
const ActionBadge = require('./ActionBadge');
// (Moved to EditorModals.js)
const NodeIcon = require('./NodeIcon');
const { normalizeConditionRule, OPERATOR_LABELS, operatorRequiresValue } = require('../lib/conditionRule');
const { getApiBasePath } = require('../lib/apiClient');
const dragData = require('../lib/dragData');
require('./WorkflowCanvas.css');

// Extracted modules
const { anchorDir, smoothPath, smoothPathThroughPoints, bezierMidpoint, bezierPointAt } = require('../lib/connectionPaths');
const { renderStandbyFlowDots, renderStandbyFlowGradient } = require('../lib/standbyFlowDot');
const { fallbackSizes, FUTURE_CONNECTOR_BUTTON_HEIGHT, cloneTemplateNode, getFutureNodeSize, getFutureNodePosition, compactSetVariableTitle, compactConditionTitle, buildGeneratedAgentTemplate, remapClassifierConnections, computeDownstreamFlow, autoLayoutWorkflow, LAYOUT_PRESETS, buildEditorContextNodes, autoSetVariableSourceForTarget, computeSetVariableAutofill, buildSetVariableForOutput } = require('../lib/canvasUtils');
const {
  isClassifierNode, isRouterNode, isClassifierLikeNode, getClassifierLabels, getHighestConfidenceClassifierAnchor,
  isClassifierAnchor, getClassifierInputAnchors, getClassifierAnchorIndex,
  getClassifierAnchorOffset, getCompactClassifierAnchorOffset, anchorPoint,
  isConditionNode, isLoopNode, isCustomNode, isDuplicableNode, isAgentNode,
  isToolAgentNode, isVariablesNode,
  getConditionAxes, getConditionBranchStateFromAnchor,
  buildDuplicateNodeTitle, LOCKED_DUPLICATE_FIELDS, getLockedDuplicateFields,
  clonePlainData, getDuplicateSourceNodeId, isLockedDuplicateNode,
  buildInheritedDuplicateNode, resolveEffectiveNodeById, buildEffectiveNodes,
  LockIcon, canAcceptIncomingConnection, getNodeAnchors, getIncomingAnchors,
  canStartConnectionFromAnchor, getAnchorTitle, getAnchorClass, getAnchorLabel,
  getConnectionClassName, getConnectionPreviewClassName, getFutureCandidateAnchors,
  getStartNodeOutput, isTerminalNode, formatDurationMs,
  getValueAtPropPath, formatOutputHoverText, getConnectionActionGroupStyle,
  ExecutionBadge, NodeWarningBadge, NodeTokenBadge, looksLikeErrorOutput,
  normalizeSourceAnchorForNode, normalizeTargetAnchorForNode,
  nearestAnchorForPoint, pickFreeIncomingAnchor, isResizableNode
} = require('../lib/nodeTypes');

// Extracted sub-components
const WorkflowMiniMap = require('./WorkflowMiniMap');
const RemoteCursorsLayer = require('./RemoteCursorsLayer');
const NodeOutputPanel = require('./NodeOutputPanel');
const { CodingNodeCard, CodingLines } = require('./CodingLines');
const VariablesSidePanel = require('./VariablesSidePanel');
const { sortVariableNodesByExecutionOrder } = require('../lib/variableOrder');
const LogChatPopup = require('./LogChatPopup');
const DoctorChatPopup = require('./DoctorChatPopup');
const WorkflowCopilotPopup = require('./WorkflowCopilotPopup');
const WorkflowCopilotLauncher = require('./WorkflowCopilotLauncher');
const { applyCopilotEdits } = require('../lib/workflowCopilotEdits');
// DoctorAiPopup removed (hidden feature)

// Extracted sub-components
const CanvasFooter = require('./CanvasFooter');
const RunLogPanel = require('./RunLogPanel');
const ConfirmDialog = require('./ConfirmDialog');
const EditorModals = require('./EditorModals');
const UiPageRenderer = require('./UiPageRenderer');
const uiPageStorage = require('../lib/uiPageStorage');
const nodeClipboard = require('../lib/nodeClipboard');
const WizardIntroModal = require('./WizardIntroModal');

// Walks any value (string / array / nested object) and pushes every
// `@VarName` reference into out. Mirrors the server's extractAtRefsFromString
// rules: leading `@` preceded by start/non-name char, name chars
// [A-Za-z0-9_ ] (internal spaces ok), drop trailing `++`/`--` and dotted tail.
var VAR_AT_OPENER = /(^|[^A-Za-z0-9_])@(?=[A-Za-z_])/g;
function pushAtRefs(str, out) {
  if (typeof str !== 'string' || str.indexOf('@') < 0) return;
  VAR_AT_OPENER.lastIndex = 0;
  var match;
  while ((match = VAR_AT_OPENER.exec(str)) !== null) {
    var pos = match.index + match[1].length + 1; // first char after @
    var lastNameCharIdx = -1;
    while (pos < str.length) {
      var ch = str[pos];
      if (/[A-Za-z0-9_]/.test(ch)) { lastNameCharIdx = pos; pos += 1; continue; }
      if (ch === ' ') {
        var look = pos + 1;
        while (look < str.length && str[look] === ' ') look += 1;
        if (look < str.length && /[A-Za-z0-9_]/.test(str[look])) { pos = look; continue; }
        break;
      }
      break;
    }
    if (lastNameCharIdx < 0) continue;
    var name = str.slice(match.index + match[1].length + 1, lastNameCharIdx + 1).trim();
    if (name) out[name] = true;
    VAR_AT_OPENER.lastIndex = lastNameCharIdx + 1;
  }
}

// Walk strings inside `node.code` for runtime `vars["X"]` / `vars['X']` reads.
var VARS_BRACKET_REGEX = /\bvars\s*\[\s*(['"])([^'"\\]+)\1\s*\]/g;
function pushVarsBracketRefs(code, out) {
  if (typeof code !== 'string' || code.indexOf('vars') < 0) return;
  VARS_BRACKET_REGEX.lastIndex = 0;
  var match;
  while ((match = VARS_BRACKET_REGEX.exec(code)) !== null) {
    var name = String(match[2] || '').trim();
    if (name) out[name] = true;
  }
}

// Returns { reads: {VarName: true}, writes: {VarName: true} } for a node.
// `writes` is populated when the node is a set-variable whose pv.variable
// names the target, or a condition node whose pv.variable names the var
// being tested (a read). `reads` covers every other reference: @VarName in
// any pv/conditionRule string, vars["X"] inside custom-node code, and the
// bare-name forms used by some node types (condition pv.variable, classifier/
// router input etc.) which historically may or may not carry an "@" prefix.
// Note: a custom-node "code" string with `vars["X"] = ...` would technically
// be a write too, but custom code is auto-generated from gp and we don't
// statically know assignment vs read — treat all code-side refs as reads
// for highlighting purposes (still correct for "node touches this variable").
function collectVarRefsForNode(node) {
  var reads = {};
  var writes = {};
  if (!node || typeof node !== 'object') return { reads: reads, writes: writes };
  var icon = String(node.icon || node.type || '').toLowerCase();
  if (icon === 'variables') return { reads: reads, writes: writes };

  var pv = node.paramValues || {};

  // Strip a leading "@" and a trailing dotted-path/++/-- so a stored value
  // like "@Emails.length" / "EmailIndex" / "@Counter++" all reduce to the
  // bare variable title.
  function stripVarRefName(raw) {
    var s = String(raw || '').trim();
    if (!s) return '';
    if (s.charAt(0) === '@') s = s.slice(1);
    // drop dotted path, then ++/--
    var dot = s.indexOf('.');
    if (dot >= 0) s = s.slice(0, dot);
    if (s.endsWith('++') || s.endsWith('--')) s = s.slice(0, -2);
    return s.trim();
  }

  if (icon === 'set-variable') {
    // pv.variable on set-variable is the write target. It's stored without
    // the "@" prefix in the UI, but planner output sometimes includes it —
    // accept either.
    var target = stripVarRefName(pv.variable);
    if (target) writes[target] = true;
  }

  if (icon === 'condition') {
    // pv.variable on condition is the variable being TESTED — that's a
    // read. Older flows store it with "@", newer with bare name; accept
    // either. Walking pv as a string would miss the bare-name case.
    var condVar = stripVarRefName(pv.variable);
    if (condVar) reads[condVar] = true;
  }

  function walk(value) {
    if (value == null) return;
    if (typeof value === 'string') { pushAtRefs(value, reads); return; }
    if (Array.isArray(value)) { for (var i = 0; i < value.length; i++) walk(value[i]); return; }
    if (typeof value === 'object') {
      for (var k in value) {
        if (!Object.prototype.hasOwnProperty.call(value, k)) continue;
        // Skip the variable-slot keys we've already handled directly above.
        // For set-variable: pv.variable is the write target.
        // For condition: pv.variable is the tested variable (recorded as a
        // read above) and is sometimes stored without "@", so the string
        // walker would skip it.
        if ((icon === 'set-variable' || icon === 'condition') && k === 'variable') continue;
        walk(value[k]);
      }
    }
  }
  walk(pv);
  walk(node.conditionRule || node.cr);

  if (typeof node.code === 'string') pushVarsBracketRefs(node.code, reads);
  return { reads: reads, writes: writes };
}

const MissingFieldsDialog = require('./MissingFieldsDialog');

// Render the silent-check stream as a single short live-reasoning line.
// Picks the latest content from an open reasoning block if one is streaming,
// strips tag markers, collapses whitespace, and truncates so the badge stays
// small. Returns '' when there is nothing useful to show yet.
var SILENT_CHECK_LIVE_MAX_CHARS = 180;
var SILENT_CHECK_TAG_ORDER = [
  'think',
  'base_from_original_prompt_and_approved_changes',
  'before_i_decide'
];
function formatSilentCheckStream(rawText) {
  var text = String(rawText || '');
  if (!text) return '';

  // Prefer the most recently opened reasoning block. If the close tag is
  // missing the model is still writing inside it — show that live content.
  var liveContent = '';
  for (var i = SILENT_CHECK_TAG_ORDER.length - 1; i >= 0; i -= 1) {
    var tag = SILENT_CHECK_TAG_ORDER[i];
    var open = text.lastIndexOf('<' + tag + '>');
    if (open === -1) continue;
    var after = text.slice(open + tag.length + 2);
    var close = after.indexOf('</' + tag + '>');
    liveContent = close === -1 ? after : after.slice(0, close);
    if (liveContent.trim()) break;
  }

  // Fall back to the tail of the raw text if no tag block has opened yet.
  if (!liveContent.trim()) liveContent = text;

  var clean = liveContent.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  if (clean.length <= SILENT_CHECK_LIVE_MAX_CHARS) return clean;
  return '…' + clean.slice(clean.length - SILENT_CHECK_LIVE_MAX_CHARS + 1);
}

// Extracted hooks
const useCanvasPanZoom = require('../hooks/useCanvasPanZoom');
const useNodeMeasurement = require('../hooks/useNodeMeasurement');
const useNodeDrag = require('../hooks/useNodeDrag');
const useNodeSelection = require('../hooks/useNodeSelection');
const useConnectionManager = require('../hooks/useConnectionManager');
const useNodeEditing = require('../hooks/useNodeEditing');
const useGroupManagement = require('../hooks/useGroupManagement');
const useAiFlowGeneration = require('../hooks/useAiFlowGeneration');
const useAutoBuildWorkflow = require('../hooks/useAutoBuildWorkflow');
const useCanvasDrop = require('../hooks/useCanvasDrop');
const useHoverPreview = require('../hooks/useHoverPreview');

function WorkflowCanvas(props) {
  var workflowState = props.workflowState;
  var setWorkflowState = props.setWorkflowState;
  var onSetVariableLivePreview = typeof props.onSetVariableLivePreview === 'function' ? props.onSetVariableLivePreview : function() {};
  var objectTemplates = props.objectTemplates || [];
  var executionState = props.executionState || null;
  var publicEventStreamEnabled = !!props.publicEventStreamEnabled;
  var publicEventStreamStatus = props.publicEventStreamStatus ? String(props.publicEventStreamStatus) : 'idle';
  var publicEventStreamLastEventAt = props.publicEventStreamLastEventAt ? String(props.publicEventStreamLastEventAt) : '';
  var publicEventStreamError = props.publicEventStreamError ? String(props.publicEventStreamError) : '';
  var onManualNodeAutoFix = props.onManualNodeAutoFix || null;
  var historyEntries = props.historyEntries || [];
  var historyPos = props.historyPos || 0;
  var onUndo = props.onUndo || null;
  var onRedo = props.onRedo || null;
  var onJumpHistory = props.onJumpHistory || null;
  var onSaveNodeAsTemplate = props.onSaveNodeAsTemplate || null;
  var onRerunFromNode = props.onRerunFromNode || null;
  var onRunNodeIsolated = props.onRunNodeIsolated || null;
  var onRunWorkflow = props.onRunWorkflow || null;
  var onPauseWorkflow = props.onPauseWorkflow || null;
  var isExecutionPaused = !!props.isExecutionPaused;
  var isRunDisabled = !!props.isRunDisabled;
  var workflowId = props.workflowId ? String(props.workflowId) : '';
  // Read-only (Live view): render the graph but disable every edit affordance
  // (drag, connect, drop, add, delete, sidebar/param edits). Used to show the
  // published live snapshot without letting it be mutated.
  var readOnly = !!props.readOnly;
  var isEmbed = !!props.isEmbed;
  var isLoading = !!props.isLoading;
  // Brief "Rendering…" phase that bridges the gap between data being loaded
  // (isLoading flips false) and the node DOM actually being painted. Without
  // it, a real saved workflow flashes the empty "Build your workflow"
  // placeholder for a frame while React commits the nodes. Cleared on the next
  // paint frame once nodes exist (see effect below).
  var [isRendering, setIsRendering] = useState(false);

  var [logChatOpen, setLogChatOpen] = useState(false);
  // Workflow Copilot — bottom-sheet chat that edits the workflow via AI.
  // Toggled from the canvas footer; purely local state (no App.js lift needed).
  var [copilotOpen, setCopilotOpen] = useState(false);
  // Message typed into the launcher pill before the sidebar opened — the popup
  // reads it once on mount and auto-sends. A ref (not state): it must not
  // re-trigger renders and is only meaningful at open time.
  var copilotInitialTextRef = useRef('');
  // Doctor chat open state is controlled by App.js when it passes
  // `doctorChatOpen` + `setDoctorChatOpen` props (so the Header toolbar
  // button can toggle it). Falls back to local state for any caller that
  // doesn't lift it (kept for safety; in this codebase App.js always lifts).
  var [doctorChatOpenLocal, setDoctorChatOpenLocal] = useState(false);
  var doctorChatOpen = typeof props.doctorChatOpen === 'boolean'
    ? props.doctorChatOpen
    : doctorChatOpenLocal;
  var setDoctorChatOpen = typeof props.setDoctorChatOpen === 'function'
    ? props.setDoctorChatOpen
    : setDoctorChatOpenLocal;
  // viewMode controls whether the canvas shows the workflow graph or an
  // iframe overlay of the UI page built for this workflow. Only meaningful
  // when props.existingUiPageId is set (which only happens for standalone
  // workflows — see App.js gating). uiOverlayPage caches the FULL page
  // record (with html) loaded on demand the first time the user toggles
  // to 'ui'.
  var [viewMode, setViewModeRaw] = useState('workflow');
  function setViewMode(next) {
    setViewModeRaw(next);
    if (typeof props.onViewModeChange === 'function') props.onViewModeChange(next);
  }
  var [uiOverlayPage, setUiOverlayPage] = useState(null);
  var [uiDeleteConfirmOpen, setUiDeleteConfirmOpen] = useState(false);
  var [uiOverlayLoading, setUiOverlayLoading] = useState(false);
  // Tracks how many workflow calls the iframe runtime is currently making.
  // Incremented on 'ui-workflow-start', decremented on 'ui-workflow-end'.
  // Used to show a spinning indicator on the "Workflow" toggle tab.
  var [uiWorkflowRunning, setUiWorkflowRunning] = useState(0);
  var [doctorOpen, setDoctorOpen] = useState(false);
  // Set to the source node's title when the user drags out a branch from a node
  // that has never run — the AI builder needs that node's output shape, so we
  // ask them to run the workflow first instead of silently snapping back.
  var [branchNeedsRunTitle, setBranchNeedsRunTitle] = useState(null);
  var [historyOpen, setHistoryOpen] = useState(false);
  var [showNodeDetails, setShowNodeDetails] = useState(true);
  var [hoveredPreviewNodeId, setHoveredPreviewNodeId] = useState(null);
  // Id of the node the cursor is currently over for the downstream-flow
  // highlight (dim everything, emphasise the path that leads out of this
  // node to the end of the graph). Separate from hoveredPreviewNodeId so
  // the expanded-detail preview and the flow highlight stay independent.
  var [hoveredFlowNodeId, setHoveredFlowNodeId] = useState(null);
  // Variable name being hovered in the side panel — drives the auto pan/zoom
  // and the ring highlight on every node referencing that variable.
  var [hoveredVariableName, setHoveredVariableName] = useState(null);
  // Node id to focus when hovering a Run Log line — drives the same pan/zoom
  // preview as a variable hover (via useHoverPreview). null = inactive.
  var [runLogFocusNodeId, setRunLogFocusNodeId] = useState(null);
  var [dropActive, setDropActive] = useState(false);
  var [futureDropTargetId, setFutureDropTargetId] = useState(null);
  var [canvasTool, setCanvasTool] = useState('hand'); // 'hand' | 'pointer'
  var canvasToolRef = useRef('hand');
  var [alertMsg, setAlertMsg] = useState(null);
  var alertTimerRef = useRef(null);
  var [codingNodeIds, setCodingNodeIds] = useState({});
  // Shown after auto-coding finishes — asks the user whether to start the
  // QA pass (toggle Check Silent Errors on + run the workflow). Format runs
  // immediately either way so the freshly-built canvas always looks clean.
  var [qaConfirmOpen, setQaConfirmOpen] = useState(false);
  // Set when the QA flow hits "start" but the workflow has empty required
  // fields. Shown as a separate dialog listing each missing field so the
  // user knows exactly what to fill in before retrying.
  var [qaMissingFields, setQaMissingFields] = useState(null);
  // One-time "how to" intro shown over the empty canvas on a user's first
  // visit. Opened by the effect below (never re-opens once dismissed — the
  // seen-flag lives in localStorage, see WizardIntroModal).
  var [wizardIntroOpen, setWizardIntroOpen] = useState(false);
  // Imperative handle on CanvasFooter so the QA flow can click the actual
  // run <button> instead of calling onRunWorkflow() directly. Hitting the
  // same DOM element a real user click hits keeps the run path identical.
  var canvasFooterRef = useRef(null);
  // Which Format preset the *next* button click applies. Each click advances it
  // (default → tighter → serpentine → …); programmatic callers pass an explicit
  // id instead and leave this untouched.
  var formatModeRef = useRef(0);
  var [plannerOpen, setPlannerOpen] = useState(false);
  var [variablesPanelCollapsedByUser, setVariablesPanelCollapsedByUser] = useState(false);
  var [expandedCardNodeIds, setExpandedCardNodeIds] = useState({});
  var [timerNowMs, setTimerNowMs] = useState(Date.now());
  var canvasRef = useRef(null);

  // Eagerly fetch the full UI page record whenever the page ID appears or
  // changes, or when the reload key bumps (planner saved new content).
  // NOT gated by viewMode — the page is pre-fetched so toggling to UI is
  // instant with no "Loading…" flash. The iframe stays mounted (hidden via
  // display:none) when viewMode is 'workflow' so it preserves form state,
  // workflow results, etc. between toggles.
  var uiPageReloadKey = props.uiPageReloadKey || 0;
  useEffect(function() {
    var pageId = props.existingUiPageId || '';
    if (!pageId) {
      setViewMode('workflow');
      setUiOverlayPage(null);
      return;
    }
    setUiOverlayLoading(true);
    uiPageStorage.loadUiPage(pageId).then(function(page) {
      setUiOverlayPage(page || null);
      setUiOverlayLoading(false);
    }).catch(function() {
      setUiOverlayPage(null);
      setUiOverlayLoading(false);
    });
  }, [props.existingUiPageId, uiPageReloadKey]);

  // Listen for postMessage from the iframe runtime so we can show a
  // spinning indicator on the "Workflow" toggle tab while a workflow
  // triggered from the UI page is in flight.  The `source` marker
  // ensures we only react to messages from our own runtime (not
  // browser extensions, HMR, or other iframes).
  useEffect(function() {
    function onMessage(event) {
      var data = event && event.data;
      if (!data || typeof data !== 'object') return;
      if (data.source !== 'alleflow-ui-runtime') return;
      if (data.type === 'ui-workflow-start') {
        setUiWorkflowRunning(function(c) { return c + 1; });
      } else if (data.type === 'ui-workflow-end') {
        setUiWorkflowRunning(function(c) { return Math.max(0, c - 1); });
      }
    }
    window.addEventListener('message', onMessage);
    return function() { window.removeEventListener('message', onMessage); };
  }, []);

  // Reset the running counter whenever the iframe content changes (the old
  // runtime is gone, so any in-flight calls will never send an "end" message).
  useEffect(function() {
    setUiWorkflowRunning(0);
  }, [uiOverlayPage]);

  // Auto-start the public event stream when the UI overlay first becomes
  // available so that workflow executions triggered from the iframe appear
  // live on the canvas (same as the "listen" feature but automatic).
  // We intentionally do NOT stop listening when the user toggles back to
  // the Workflow tab — they need to observe execution there while the
  // iframe (kept mounted but hidden) is still usable.
  var uiAutoListenRef = useRef(false);
  useEffect(function() {
    if (uiOverlayPage && !publicEventStreamEnabled && !uiAutoListenRef.current) {
      uiAutoListenRef.current = true;
      if (typeof props.onTogglePublicEventStream === 'function') {
        props.onTogglePublicEventStream(true);
      }
    }
  }, [!!uiOverlayPage, publicEventStreamEnabled]);


  var nodeOutputByIdRef = useRef({});
  var variableValuesByNameRef = useRef({});
  var workflowStateRef = useRef(workflowState);
  var rectByIdRef = useRef({});
  var normalZoomRef = useRef(null); // zoom saved before execution; restored when all nodes finish

  // ── Pan/Zoom hook ──────────────────────────────────────────────────────────
  var panZoom = useCanvasPanZoom(canvasRef);
  var panPosRef = panZoom.panPosRef;
  var zoomRef = panZoom.zoomRef;

  // Remote Support: stream the local mouse position (world coords) to the
  // live session. The client throttles; conversion inverts the canvas
  // transform (screen = pan + zoom * world).
  var onCollabCursorMove = props.onCollabCursorMove;
  useEffect(function() {
    if (typeof onCollabCursorMove !== 'function') return;
    var el = canvasRef.current;
    if (!el) return;
    function onMove(event) {
      var rect = el.getBoundingClientRect();
      var zoom = zoomRef.current || 1;
      onCollabCursorMove(
        (event.clientX - rect.left - panPosRef.current.x) / zoom,
        (event.clientY - rect.top - panPosRef.current.y) / zoom
      );
    }
    el.addEventListener('mousemove', onMove);
    return function() { el.removeEventListener('mousemove', onMove); };
  }, [onCollabCursorMove]);
  var zoomLevel = panZoom.zoomLevel;
  var worldRef = panZoom.worldRef;
  var zoomLayerRef = panZoom.zoomLayerRef;
  var spaceHeldRef = panZoom.spaceHeldRef;
  var applyWorldStyle = panZoom.applyWorldStyle;
  var subscribeToPan = panZoom.subscribeToPan;
  var changeZoom = panZoom.changeZoom;
  var autoPanAnimateTo = panZoom.autoPanAnimateTo;
  var startPanning = panZoom.startPanning;
  var startPanningTouch = panZoom.startPanningTouch;
  var cancelPanningTouch = panZoom.cancelPanningTouch;
  var startPinch = panZoom.startPinch;
  var centerOnNode = panZoom.centerOnNode;
  var fitToView = panZoom.fitToView;
  var autoPanAnimRef = panZoom.autoPanAnimRef;
  var pinchRef = panZoom.pinchRef;

  function isNodeDetailsExpanded(nodeId) {
    return !!(showNodeDetails || hoveredPreviewNodeId === nodeId);
  }

  var rawNodes = workflowState.nodes || [];
  var nodes = useMemo(function() {
    return buildEffectiveNodes(rawNodes);
  }, [rawNodes]);
  var connections = workflowState.connections || [];

  // When loading finishes for a workflow that has nodes, show a brief
  // "Rendering…" overlay until the next paint frame so the empty placeholder
  // never flashes while React commits the node DOM. Skipped for genuinely
  // empty workflows (nothing to render → show the placeholder immediately).
  var prevIsLoadingRef = useRef(isLoading);
  useEffect(function() {
    var justLoaded = prevIsLoadingRef.current && !isLoading;
    prevIsLoadingRef.current = isLoading;
    if (!justLoaded || rawNodes.length === 0) return;
    setIsRendering(true);
    var raf2 = 0;
    var raf1 = requestAnimationFrame(function() {
      raf2 = requestAnimationFrame(function() { setIsRendering(false); });
    });
    return function() {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isLoading, rawNodes.length]);

  // Report the rendering phase up so the toolbar (Save button) can stay
  // disabled until the workflow is actually painted, not just loaded. Always
  // report false on unmount so the flag can never get stuck if the canvas is
  // torn down mid-frame.
  useEffect(function() {
    if (typeof props.onRenderingChange === 'function') props.onRenderingChange(isRendering);
    return function() {
      if (typeof props.onRenderingChange === 'function') props.onRenderingChange(false);
    };
  }, [isRendering, props.onRenderingChange]);

  // ── Node Measurement hook ──────────────────────────────────────────────────
  var measurement = useNodeMeasurement(zoomRef, nodes.length, showNodeDetails, hoveredPreviewNodeId);
  var sizes = measurement.sizes;
  var nodeRefs = measurement.nodeRefs;
  var setNodeRef = measurement.setNodeRef;
  var measureNodes = measurement.measureNodes;

  // Keep refs current so event-handler closures can read the latest values
  var nodeStatusById = executionState && executionState.nodeStatusById ? executionState.nodeStatusById : {};
  var nodeFixById = executionState && executionState.nodeFixById ? executionState.nodeFixById : {};
  var nodeTimingById = executionState && executionState.nodeTimingById ? executionState.nodeTimingById : {};
  var nodeTokensById = executionState && executionState.nodeTokensById ? executionState.nodeTokensById : {};
  var traversedConnectionIds = executionState && executionState.traversedConnectionIds ? executionState.traversedConnectionIds : {};
  var nodeOutputById = executionState && executionState.nodeOutputById ? executionState.nodeOutputById : {};
  var nodeCallCountById = executionState && executionState.nodeCallCountById ? executionState.nodeCallCountById : {};
  var variableValuesByName = executionState && executionState.variableValuesByName ? executionState.variableValuesByName : {};
  var accessedVariableNames = executionState && executionState.accessedVariableNames ? executionState.accessedVariableNames : {};
  var accessedVariableNodeId = executionState && executionState.accessedVariableNodeId ? executionState.accessedVariableNodeId : {};
  var listenIndicatorTone = publicEventStreamEnabled ? publicEventStreamStatus : 'idle';
  var listenIndicatorLabel = 'Listen off';
  var listenIndicatorDetail = 'Public listener is disabled.';
  if (publicEventStreamEnabled) {
    if (publicEventStreamStatus === 'connected') {
      listenIndicatorLabel = 'Listening live';
      listenIndicatorDetail = publicEventStreamLastEventAt
        ? 'Last event ' + new Date(publicEventStreamLastEventAt).toLocaleTimeString()
        : 'Connected and waiting for events.';
    } else if (publicEventStreamStatus === 'connecting') {
      listenIndicatorLabel = 'Connecting';
      listenIndicatorDetail = 'Opening live event stream.';
    } else {
      listenIndicatorLabel = 'Disconnected';
      listenIndicatorDetail = publicEventStreamError || 'Listener lost connection.';
    }
  }
  var isExecutionRunning = !!(executionState && executionState.status === 'running');
  var isVariablesPanelCollapsed = variablesPanelCollapsedByUser;

  // The "Build your workflow" placeholder is shown only for a genuinely empty,
  // editable, hydrated canvas. Reused by the render below and by the one-time
  // intro trigger so the wizard appears exactly when the placeholder does.
  var showEmptyState = !readOnly && rawNodes.length === 0 && !isExecutionRunning && !isLoading && !isRendering;

  // First-visit only: pop the "how to" intro the first time the empty canvas is
  // shown. The seen-flag in localStorage keeps it from ever re-appearing.
  useEffect(function() {
    if (showEmptyState && !WizardIntroModal.hasSeenWizardIntro()) {
      setWizardIntroOpen(true);
    }
  }, [showEmptyState]);

  // Auto-close log chat when a new execution starts
  useEffect(function() {
    if (isExecutionRunning) {
      setLogChatOpen(false);
      setDoctorChatOpen(false);
    }
  }, [isExecutionRunning]);

  // Center the canvas on the Start node whenever a new workflow is loaded.
  // The workflowId prop changes immediately on navigation, but the actual
  // workflowState (rawNodes) arrives a moment later. We mark "needs centering"
  // when the id changes and clear it once we successfully center, so this
  // only ever fires on the initial load of a flow (not when the user later
  // edits the canvas).
  var pendingCenterWorkflowIdRef = useRef('');
  var pendingCenterStartingNodesRef = useRef(null);
  var lastSeenWorkflowIdRef = useRef('');
  if (lastSeenWorkflowIdRef.current !== workflowId) {
    lastSeenWorkflowIdRef.current = workflowId;
    pendingCenterWorkflowIdRef.current = workflowId;
    // Snapshot the rawNodes reference at navigation time. The parent's
    // workflowState lags one render behind the new workflowId on flow
    // switches (and on instant cache opens), so we have to wait until the
    // canvas re-renders with the *new* nodes before attempting to center —
    // otherwise we'd center on stale nodes (or no nodes) and prematurely
    // clear the pending flag.
    pendingCenterStartingNodesRef.current = rawNodes;
  }
  useEffect(function() {
    if (!workflowId) return;
    if (pendingCenterWorkflowIdRef.current !== workflowId) return;
    if (!Array.isArray(rawNodes) || rawNodes.length === 0) return;
    if (rawNodes === pendingCenterStartingNodesRef.current) return;
    var startNode = null;
    for (var i = 0; i < rawNodes.length; i++) {
      var n = rawNodes[i];
      if (n && n.icon === 'start') { startNode = n; break; }
    }
    // Fall back to the first node if there is no explicit start node.
    var target = startNode || rawNodes[0];
    var pos = target && target.position ? target.position : null;
    if (!pos || typeof pos.left !== 'number' || typeof pos.top !== 'number') return;
    // Defer one frame so the canvas has its final measured size after any
    // layout swaps (e.g. opening the workbook panel). We also wait until
    // the canvas element has a non-zero size — on instant-cache opens the
    // first paint can fire before the parent flex layout has settled.
    var attemptsLeft = 5;
    var rafId = 0;
    function tryCenter() {
      var canvas = canvasRef.current;
      if (!canvas) return;
      if ((canvas.offsetWidth === 0 || canvas.offsetHeight === 0) && attemptsLeft > 0) {
        attemptsLeft -= 1;
        rafId = requestAnimationFrame(tryCenter);
        return;
      }
      // On narrow viewports (touch/mobile) drop the initial zoom so neighbour
      // nodes are visible without panning. Center is computed in the same
      // frame so the Start node stays anchored to the canvas midpoint.
      if (canvas.offsetWidth < 640) changeZoom(0.6);
      centerOnNode({ left: pos.left, top: pos.top });
      pendingCenterWorkflowIdRef.current = '';
      pendingCenterStartingNodesRef.current = null;
    }
    rafId = requestAnimationFrame(tryCenter);
    return function() { if (rafId) cancelAnimationFrame(rafId); };
  }, [workflowId, rawNodes]);

  // Keep canvasToolRef in sync
  canvasToolRef.current = canvasTool;

  nodeOutputByIdRef.current = nodeOutputById;
  // Runtime variable values live keyed by NAME (not by node id) — keep a ref so
  // the AI Flow Builder balloon can read a variable's last run value when it
  // builds single-node context (otherwise it sees only the empty static value
  // of a Temporary var and asks the user to paste a shape it already has).
  variableValuesByNameRef.current = variableValuesByName;
  workflowStateRef.current = workflowState;

  var rawNodeById = useMemo(function() {
    var map = {};
    rawNodes.forEach(function(node) {
      if (!node || !node.id) return;
      map[String(node.id)] = node;
    });
    return map;
  }, [rawNodes]);

  function getClassifierHandleStyle(node, anchor) {
    if (!(isClassifierLikeNode(node) && isClassifierAnchor(anchor))) return null;

    if (isNodeDetailsExpanded(node.id)) {
      var measuredEntry = sizes[node.id] || fallbackSizes[node.id];
      var anchorIdx = getClassifierAnchorIndex(anchor);
      var topOffset;
      if (measuredEntry && measuredEntry.classifierRowOffsets && anchorIdx >= 0 && anchorIdx < measuredEntry.classifierRowOffsets.length) {
        topOffset = measuredEntry.classifierRowOffsets[anchorIdx];
      } else {
        topOffset = getClassifierAnchorOffset(anchor);
      }
      return { top: topOffset + 'px', right: '-16px', transform: 'translateY(-50%)' };
    }

    var measuredSize = sizes[node.id] || fallbackSizes[node.id] || { width: 220, height: 80 };
    var rect = {
      top: 0,
      left: 0,
      right: measuredSize.width,
      bottom: measuredSize.height,
      cx: measuredSize.width / 2,
      cy: measuredSize.height / 2
    };

    return {
      top: getCompactClassifierAnchorOffset(rect, anchor, node) + 'px',
      right: '-16px',
      transform: 'translateY(-50%)'
    };
  }

  function nodeClass(id) {
    var classes = 'node-position node-draggable';
    if (nodeDrag.draggingId === id) classes += ' node-dragging';
    if (nodeDrag.touchDraggingId === id) classes += ' node-long-press-active';
    return classes;
  }

  function getRect(node) {
    var position = node.position || { left: 0, top: 0 };
    var measuredSize = sizes[node.id] || fallbackSizes[node.id] || { width: 180, height: 80 };
    // node.size is a stale width/height saved when the user resized a wide card
    // pre-tile-redesign. Tile nodes aren't resizable anymore, so ignore any
    // saved explicit size for them — otherwise connection anchors land at the
    // old 220px right-edge instead of the visible 132px tile edge.
    var explicitSize = (isResizableNode(node) && node.size) ? node.size : {};
    var width = Number(explicitSize.width);
    var height = Number(explicitSize.height);
    if (!Number.isFinite(width) || width <= 0) width = measuredSize.width;
    if (!Number.isFinite(height) || height <= 0) height = measuredSize.height;
    var result = {
      left: position.left,
      top: position.top,
      width: width,
      height: height,
      right: position.left + width,
      bottom: position.top + height,
      cx: position.left + width / 2,
      cy: position.top + height / 2
    };
    if (measuredSize && measuredSize.classifierRowOffsets) {
      result.classifierRowOffsets = measuredSize.classifierRowOffsets;
    }
    return result;
  }

  // Refs for sharing mutable state with hooks (avoids wrapper functions)
  var nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  var selectedNodeIdsRef = useRef({});
  var getRectRef = useRef(getRect);
  getRectRef.current = getRect;

  // ── Node Selection hook (must come before drag hook so we can pass setters) ──

  var nodeSelection = useNodeSelection({
    canvasRef: canvasRef,
    canvasToolRef: canvasToolRef,
    spaceHeldRef: spaceHeldRef,
    panPosRef: panPosRef,
    zoomRef: zoomRef,
    workflowStateRef: workflowStateRef,
    getRect: getRect
  });
  var selectedNodeIds = nodeSelection.selectedNodeIds;
  var setSelectedNodeIds = nodeSelection.setSelectedNodeIds;
  var lastSelectedNodeId = nodeSelection.lastSelectedNodeId;
  var setLastSelectedNodeId = nodeSelection.setLastSelectedNodeId;
  var selectedConnectionId = nodeSelection.selectedConnectionId;
  var rawSetSelectedConnectionId = nodeSelection.setSelectedConnectionId;
  var [connectionClickPos, setConnectionClickPos] = useState(null);
  var [hoveredConnectionId, setHoveredConnectionId] = useState(null);
  var setSelectedConnectionId = function(id) {
    if (!id) setConnectionClickPos(null);
    rawSetSelectedConnectionId(id);
  };
  function selectConnectionAtEvent(connectionId, event) {
    if (event && canvasRef.current) {
      var rect = canvasRef.current.getBoundingClientRect();
      var z = zoomRef.current || 1;
      var clientX = event.clientX;
      var clientY = event.clientY;
      if (clientX === undefined && event.changedTouches && event.changedTouches[0]) {
        clientX = event.changedTouches[0].clientX;
        clientY = event.changedTouches[0].clientY;
      }
      if (typeof clientX === 'number' && typeof clientY === 'number') {
        setConnectionClickPos({
          x: (clientX - rect.left - panPosRef.current.x) / z,
          y: (clientY - rect.top - panPosRef.current.y) / z
        });
      }
    }
    rawSetSelectedConnectionId(connectionId);
  }
  var marquee = nodeSelection.marquee;
  var startMarquee = nodeSelection.startMarquee;
  selectedNodeIdsRef.current = selectedNodeIds;

  // Notify parent (App) when the single-selected node changes, so the right
  // sidebar can show that node's params. Only fires for single-selection;
  // multi-select / no-selection clears it. We emit the node id (parent looks
  // up the live node from workflowState so edits stay reactive).
  //
  // Touch gate: on coarse-pointer devices a single tap should only highlight
  // the node on the canvas; the params panel opens on a second tap of the
  // same node within DOUBLE_TAP_MS. Double-taps are detected in
  // handleNodeTap below from physical touch timestamps (reported by
  // useNodeDrag when a clean tap completes) — NOT in this effect. Detecting
  // them here proved unreliable: each tap's timestamp was taken only after a
  // full canvas re-render, and post-run renders (exec badges + output panels
  // on every node) are slow enough that the second tap routinely fell
  // outside the window. Mouse / pen users keep one-click open via this
  // effect.
  var lastTapRef = useRef({ id: null, endedAt: 0 });
  var DOUBLE_TAP_MS = 450;
  function isCoarsePointer() {
    return typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(pointer: coarse)').matches;
  }
  useEffect(function() {
    if (typeof props.onSelectionChange !== 'function') return;
    var ids = Object.keys(selectedNodeIds);
    var nextId = ids.length === 1 ? ids[0] : null;

    // Always allow clearing (deselect → close panel) regardless of pointer.
    if (!nextId) {
      lastTapRef.current = { id: null, endedAt: 0 };
      props.onSelectionChange(null);
      return;
    }
    // Coarse pointer: selection changes alone never open the panel — taps
    // own that (handleNodeTap). This also keeps programmatic selects (e.g.
    // copilot node highlighting) from disturbing an open panel.
    if (isCoarsePointer()) return;
    props.onSelectionChange(nextId);
  }, [selectedNodeIds]);

  // Touch tap on a node (from useNodeDrag: finger went down and up on the
  // node without moving, long-pressing, or being cancelled). `tapStartedAt`
  // is the tap's touchstart time, so the gap we compare is finger-up →
  // finger-down between the two taps — a slower second press can't overrun
  // the window, and render latency never counts against it.
  function handleNodeTap(nodeId, tapStartedAt) {
    if (typeof props.onSelectionChange !== 'function') return;
    if (!isCoarsePointer()) return; // mouse/pen: the effect above already opened
    var prev = lastTapRef.current;
    var isSecondTap = prev.id === nodeId && (tapStartedAt - prev.endedAt) <= DOUBLE_TAP_MS;
    lastTapRef.current = { id: nodeId, endedAt: Date.now() };
    if (isSecondTap) {
      props.onSelectionChange(nodeId);
    } else {
      // First tap: keep the canvas highlight but make sure no stale panel
      // remains open from a previous selection.
      props.onSelectionChange(null);
    }
  }

  // ── Node Drag hook ─────────────────────────────────────────────────────────
  var nodeDrag = useNodeDrag({
    panPosRef: panPosRef,
    zoomRef: zoomRef,
    setWorkflowState: setWorkflowState,
    workflowStateRef: workflowStateRef,
    measureNodes: measureNodes,
    nodesRef: nodesRef,
    selectedNodeIdsRef: selectedNodeIdsRef,
    getRectRef: getRectRef,
    onLongPressSelect: function(nodeId) {
      var lpSel = {}; lpSel[nodeId] = true;
      setSelectedNodeIds(lpSel);
      setSelectedConnectionId(null);
      // Touch-selecting by itself never opens the params panel, and it closes
      // a stale one (e.g. long-press-dragging node B while node A's panel is
      // open). On a double-tap, handleNodeTap runs right after this in the
      // same touchend and re-opens — React batches both, the open wins.
      if (isCoarsePointer() && typeof props.onSelectionChange === 'function') {
        props.onSelectionChange(null);
      }
    },
    onNodeTap: handleNodeTap,
    isReadOnly: readOnly,
    // Long-press fired → node-drag takes over the touch; abort any canvas pan
    // that the same touch had started so the canvas doesn't drift.
    onTouchDragArmed: function() { cancelPanningTouch(); }
  });

  // ── Connection Manager hook ────────────────────────────────────────────────
  var connManager = useConnectionManager({
    canvasRef: canvasRef,
    panPosRef: panPosRef,
    zoomRef: zoomRef,
    setWorkflowState: setWorkflowState,
    workflowStateRef: workflowStateRef
  });
  var connectionDrag = connManager.connectionDrag;
  var setConnectionDrag = connManager.setConnectionDrag;
  var connectionTargetNodeId = connManager.connectionTargetNodeId;
  var setConnectionTargetNodeId = connManager.setConnectionTargetNodeId;
  var connectionTargetIdRef = connManager.connectionTargetIdRef;
  var propPickerState = connManager.propPickerState;
  var setPropPickerState = connManager.setPropPickerState;

  // Selecting a node opens the sidebar params editor. Dismiss the
  // connection-time prop picker so it doesn't linger floating off-canvas
  // while the user is editing parameters in the sidebar.
  useEffect(function() {
    if (Object.keys(selectedNodeIds).length > 0 && propPickerState) {
      setPropPickerState(null);
    }
  }, [selectedNodeIds]);
  // Read-only (Live) mode: disable drawing/reconnecting/deleting connections.
  var connNoop = function() {};
  var startConnectionDrag = readOnly ? connNoop : connManager.startConnectionDrag;
  var startReconnectDrag = readOnly ? connNoop : connManager.startReconnectDrag;
  var startConnectionDragTouch = readOnly ? connNoop : connManager.startConnectionDragTouch;
  var startReconnectDragTouch = readOnly ? connNoop : connManager.startReconnectDragTouch;
  var deleteConnection = readOnly ? connNoop : connManager.deleteConnection;
  var toggleConnectionNoWait = connManager.toggleConnectionNoWait;
  var toggleConnectionNoData = connManager.toggleConnectionNoData;
  var addConnectionBend = connManager.addConnectionBend;
  var toggleConnectionBendAtPoint = connManager.toggleConnectionBendAtPoint;
  var removeConnectionBend = connManager.removeConnectionBend;
  var startBendDrag = connManager.startBendDrag;
  var startBendDragTouch = connManager.startBendDragTouch;
  var handlePropPickerSelect = connManager.handlePropPickerSelect;
  var closePropPicker = connManager.closePropPicker;

  // Collapsed group maps
  var collapsedGroups = useMemo(function() {
    var groups = Array.isArray(workflowState.groups) ? workflowState.groups : [];
    return groups.filter(function(g) { return g.collapsed; });
  }, [workflowState.groups]);

  var nodeIdToGroupId = useMemo(function() {
    var map = {};
    collapsedGroups.forEach(function(g) {
      if (g.expanded) return; // expanded groups show their nodes
      g.nodeIds.forEach(function(nid) { map[nid] = g.id; });
    });
    return map;
  }, [collapsedGroups]);

  var groupById = useMemo(function() {
    var map = {};
    collapsedGroups.forEach(function(g) { map[g.id] = g; });
    return map;
  }, [collapsedGroups]);

  var rectById = useMemo(function() {
    var map = {};
    nodes.forEach(function(node) {
      map[node.id] = getRect(node);
    });
    // Add rects for collapsed group cards (skip expanded groups)
    collapsedGroups.forEach(function(g) {
      if (g.expanded) return;
      var pos = g.position || { left: 0, top: 0 };
      var measured = sizes[g.id] || fallbackSizes[g.id];
      var w = measured ? measured.width : 250;
      var ht = measured ? measured.height : 80;
      map[g.id] = {
        left: pos.left, top: pos.top,
        width: w, height: ht,
        right: pos.left + w, bottom: pos.top + ht,
        cx: pos.left + w / 2, cy: pos.top + ht / 2
      };
    });
    return map;
  }, [nodes, sizes, collapsedGroups]);
  rectByIdRef.current = rectById;

  var nodeById = useMemo(function() {
    var map = {};
    nodes.forEach(function(node) {
      map[node.id] = node;
    });
    // Add pseudo-nodes for collapsed groups so connection resolution works (skip expanded)
    collapsedGroups.forEach(function(g) {
      if (g.expanded) return;
      map[g.id] = {
        id: g.id,
        kind: 'workflow-node',
        nodeType: 'card',
        icon: 'group',
        title: g.name,
        position: g.position
      };
    });
    return map;
  }, [nodes, collapsedGroups]);

  // ── Node Editing hook ──────────────────────────────────────────────────────
  var nodeEditing = useNodeEditing({
    setWorkflowState: setWorkflowState,
    rawNodeById: rawNodeById,
    nodeById: nodeById,
    nodeOutputByIdRef: nodeOutputByIdRef,
    onManualNodeAutoFix: onManualNodeAutoFix,
    setSelectedNodeIds: setSelectedNodeIds,
    setSelectedConnectionId: setSelectedConnectionId,
    selectedNodeIdsRef: selectedNodeIdsRef,
    onEditCustomNode: props.onEditCustomNode,
    readOnly: readOnly,
    strictReadOnly: isEmbed
  });
  var isNodeLocked = nodeEditing.isNodeLocked;
  var editingAgentNodeId = nodeEditing.editingAgentNodeId;
  var editingClassifierNodeId = nodeEditing.editingClassifierNodeId;
  var pendingAgentDrop = nodeEditing.pendingAgentDrop;
  var setPendingAgentDrop = nodeEditing.setPendingAgentDrop;
  var pendingClassifierDrop = nodeEditing.pendingClassifierDrop;
  var setPendingClassifierDrop = nodeEditing.setPendingClassifierDrop;
  var handleParamChange = nodeEditing.handleParamChange;
  var handleNodeTitleChange = nodeEditing.handleNodeTitleChange;
  var deleteNode = nodeEditing.deleteNode;
  var duplicateCustomNode = nodeEditing.duplicateCustomNode;
  var unlockDuplicateNode = nodeEditing.unlockDuplicateNode;
  var openAgentEditor = nodeEditing.openAgentEditor;
  var closeAgentEditor = nodeEditing.closeAgentEditor;
  var saveAgentParams = nodeEditing.saveAgentParams;
  var openClassifierEditor = nodeEditing.openClassifierEditor;
  var closeClassifierEditor = nodeEditing.closeClassifierEditor;
  var saveClassifierParams = nodeEditing.saveClassifierParams;
  var confirmAgentDrop = nodeEditing.confirmAgentDrop;
  var cancelAgentDrop = nodeEditing.cancelAgentDrop;
  var confirmClassifierDrop = nodeEditing.confirmClassifierDrop;
  var cancelClassifierDrop = nodeEditing.cancelClassifierDrop;
  var handleManualFix = nodeEditing.handleManualFix;
  var handleOpenEditWithOutput = nodeEditing.handleOpenEditWithOutput;

  // variant: 'error' (default) or 'success' (green confirmation toast).
  function showAlert(msg, variant) {
    if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    setAlertMsg({ text: msg, variant: variant === 'success' ? 'success' : 'error' });
    alertTimerRef.current = setTimeout(function() { setAlertMsg(null); alertTimerRef.current = null; }, 3500);
  }

  // ── Group Management hook ──────────────────────────────────────────────────
  var groupMgmt = useGroupManagement({
    setWorkflowState: setWorkflowState,
    workflowStateRef: workflowStateRef,
    getRect: getRect,
    sizes: sizes,
    showAlert: showAlert,
    setSelectedNodeIds: setSelectedNodeIds,
    selectedNodeIdsRef: selectedNodeIdsRef
  });
  var DEFAULT_GROUP_COLOR = groupMgmt.DEFAULT_GROUP_COLOR;
  var GROUP_COLOR_OPTIONS = groupMgmt.GROUP_COLOR_OPTIONS;
  var normalizeGroupColor = groupMgmt.normalizeGroupColor;
  var hexToRgba = groupMgmt.hexToRgba;
  var collapsingAnim = groupMgmt.collapsingAnim;
  var expandingAnim = groupMgmt.expandingAnim;
  var repellingNodeIds = groupMgmt.repellingNodeIds;
  var groupColorMenuId = groupMgmt.groupColorMenuId;
  var setGroupColorMenuId = groupMgmt.setGroupColorMenuId;
  var editingGroupId = groupMgmt.editingGroupId;
  var setEditingGroupId = groupMgmt.setEditingGroupId;
  var groupSelectedNodes = groupMgmt.groupSelectedNodes;
  var ungroupNodes = groupMgmt.ungroupNodes;
  var deleteGroup = groupMgmt.deleteGroup;
  var toggleExpandGroup = groupMgmt.toggleExpandGroup;
  var collapseAllExpandedGroups = groupMgmt.collapseAllExpandedGroups;
  var renameGroup = groupMgmt.renameGroup;
  var setGroupColor = groupMgmt.setGroupColor;
  var getGroupCardRect = groupMgmt.getGroupCardRect;

  // Keyboard shortcuts: delete (node/edge), undo/redo, Ctrl+C copy, Ctrl+V
  // paste (works across workflows — the clipboard lives in localStorage).
  useEffect(function() {
    function onKeyDown(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        if ((e.ctrlKey || e.metaKey) && /^[cv]$/i.test(e.key)) {
          console.log('[copy-paste] Ctrl+' + e.key.toUpperCase() + ' ignored: focus is in a text field (' + e.target.tagName + ')');
        }
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && !readOnly) {
        var selIdList = Object.keys(selectedNodeIds);
        if (selIdList.length > 1) {
          e.preventDefault();
          nodeEditing.deleteSelectedNodes();
          return;
        }
        if (selIdList.length === 1) {
          e.preventDefault();
          nodeEditing.deleteNode(selIdList[0]);
          return;
        }
        if (selectedConnectionId) {
          e.preventDefault();
          deleteConnection(selectedConnectionId);
          return;
        }
      }
      var mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (readOnly) return;
        if (e.shiftKey) { if (onRedo) onRedo(); }
        else if (onUndo) onUndo();
        return;
      }
      if (mod && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        if (!readOnly && onRedo) onRedo();
        return;
      }
      if (mod && (e.key === 'c' || e.key === 'C')) {
        // Let the native copy win when the user has text highlighted.
        var textSel = window.getSelection && window.getSelection();
        if (textSel && String(textSel).length) {
          console.log('[copy-paste] Ctrl+C ignored: text is highlighted, letting native copy run');
          return;
        }
        var state = workflowStateRef.current || {};
        var selIds = Object.keys(selectedNodeIdsRef.current || {});
        console.log('[copy-paste] Ctrl+C pressed. Selected node ids:', selIds);
        var copiedCount = nodeClipboard.copySelection(state.nodes, state.connections, selectedNodeIdsRef.current || {});
        console.log('[copy-paste] Copied', copiedCount, 'node(s) to clipboard');
        if (copiedCount > 0) {
          showAlert(copiedCount === 1 ? 'Node copied — Ctrl+V to paste (any workflow)' : copiedCount + ' nodes copied — Ctrl+V to paste (any workflow)', 'success');
        }
        return;
      }
      if (mod && (e.key === 'v' || e.key === 'V')) {
        console.log('[copy-paste] Ctrl+V pressed. readOnly:', readOnly);
        if (readOnly) return;
        var clip = nodeClipboard.readClipboard();
        console.log('[copy-paste] Clipboard content:', clip ? clip.nodes.length + ' node(s), ' + (clip.connections || []).length + ' connection(s)' : 'EMPTY');
        if (!clip) return;
        // Paste centered in the visible canvas (same math as sidebar add).
        var canvasEl = canvasRef.current;
        var bounds = canvasEl ? canvasEl.getBoundingClientRect() : { width: 800, height: 600 };
        var z = zoomRef.current;
        var pasteCenter = {
          left: (bounds.width / 2 - panPosRef.current.x) / z,
          top: (bounds.height / 2 - panPosRef.current.y) / z
        };
        var payload = null;
        setWorkflowState(function(prev) {
          payload = nodeClipboard.buildPastePayload(clip, prev.nodes || [], pasteCenter);
          if (!payload.nodes.length) return prev;
          return Object.assign({}, prev, {
            nodes: (prev.nodes || []).concat(payload.nodes),
            connections: (prev.connections || []).concat(payload.connections)
          });
        });
        console.log('[copy-paste] Pasted', payload ? payload.nodes.length : 0, 'node(s) onto the canvas');
        if (payload && payload.nodes.length) {
          var nextSel = {};
          payload.nodes.forEach(function(n) { nextSel[n.id] = true; });
          setSelectedNodeIds(nextSel);
          setSelectedConnectionId(null);
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return function() { window.removeEventListener('keydown', onKeyDown); };
  }, [selectedNodeIds, selectedConnectionId, readOnly, onUndo, onRedo]);

  // Double-click from sidebar: add template to canvas center
  var addTemplateHandlerRef = useRef(null);
  addTemplateHandlerRef.current = function(event) {
    var templateId = event.detail && event.detail.templateId;
    if (!templateId) return;
    var template = findTemplateById(templateId);
    if (!template) return;

    if (template.icon === 'variables') {
      addVariableNode();
      return;
    }

    // Calculate center of visible canvas
    var canvasEl = canvasRef.current;
    var bounds = canvasEl ? canvasEl.getBoundingClientRect() : { width: 800, height: 600 };
    var z = zoomRef.current;
    var dropPosition = {
      left: Math.round((bounds.width / 2 - panPosRef.current.x) / z - 110),
      top: Math.round((bounds.height / 2 - panPosRef.current.y) / z - 24)
    };
    var newId = template.id + '-' + Date.now().toString(36);

    if (template.icon === 'agent') {
      setPendingAgentDrop({ template: template, dropPosition: dropPosition, newId: newId });
      return;
    }
    if (template.icon === 'classifier') {
      setPendingClassifierDrop({ template: template, dropPosition: dropPosition, newId: newId });
      return;
    }

    setWorkflowState(function(prev) {
      var nextNodes = buildNodesForTemplateDrop(prev.nodes, template, newId, dropPosition);
      return Object.assign({}, prev, { nodes: nextNodes });
    });
  };
  useEffect(function() {
    function handleAddTemplate(event) {
      if (addTemplateHandlerRef.current) addTemplateHandlerRef.current(event);
    }
    window.addEventListener('add-template-to-canvas', handleAddTemplate);
    return function() { window.removeEventListener('add-template-to-canvas', handleAddTemplate); };
  }, []);

  // Format / auto-layout the workflow. Re-arranges flow nodes into a clean
  // left-to-right layered grid following the connection graph, then re-aims
  // connector anchors so edges no longer slice across unrelated nodes. The
  // parent's history effect picks the new state up automatically, so this is
  // undoable like any other edit.
  // World-space bounding box of every flow node (variables sidebar nodes are
  // excluded — they live in a fixed panel, not the grid). Uses measured sizes
  // with the same fallbacks autoLayoutWorkflow uses, so the box matches the
  // layout. Returns null when there's nothing to frame.
  function computeFlowBounds(nodeList) {
    var minL = Infinity, minT = Infinity, maxR = -Infinity, maxB = -Infinity;
    var found = false;
    (nodeList || []).forEach(function(n) {
      if (!n || n.icon === 'variables' || !n.position) return;
      var s = sizes[n.id] || {};
      var w = s.width || 150;
      var hgt = s.height || 110;
      var l = n.position.left || 0;
      var t = n.position.top || 0;
      if (l < minL) minL = l;
      if (t < minT) minT = t;
      if (l + w > maxR) maxR = l + w;
      if (t + hgt > maxB) maxB = t + hgt;
      found = true;
    });
    if (!found) return null;
    return { left: minL, top: minT, width: maxR - minL, height: maxB - minT };
  }

  // The Format button cycles through LAYOUT_PRESETS on each click. Pass an
  // explicit preset id (e.g. 'default' from auto-build) to apply that mode
  // without advancing the manual cycle.
  function handleFormatLayout(presetId) {
    // A manual button click passes no id (it cycles); programmatic callers
    // (e.g. auto-build) pass an explicit id and keep the user's viewport.
    var isManual = typeof presetId !== 'string';
    var preset;
    if (!isManual) {
      preset = LAYOUT_PRESETS.find(function(p) { return p.id === presetId; }) || LAYOUT_PRESETS[0];
    } else {
      preset = LAYOUT_PRESETS[formatModeRef.current % LAYOUT_PRESETS.length];
      formatModeRef.current = (formatModeRef.current + 1) % LAYOUT_PRESETS.length;
    }
    // Pass the live measured sizes so Classify / Router / Agent cards (which
    // can be 2-3x taller than a basic tile) get enough vertical room and
    // wide cards push the next column further right.
    var layoutOptions = Object.assign({ sizes: sizes }, preset.options);
    var current = workflowStateRef.current || {};
    var laid = autoLayoutWorkflow(current.nodes || [], current.connections || [], layoutOptions);
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        nodes: laid.nodes,
        connections: laid.connections
      });
    });
    // Manual Format clicks zoom/pan out so the freshly arranged workflow fits
    // entirely in view. The new positions are already known (laid.nodes), so we
    // frame it now — the camera animation runs alongside the nodes settling in.
    if (isManual) {
      var bounds = computeFlowBounds(laid.nodes);
      if (bounds) fitToView(bounds);
    }
  }

  // Center the canvas on the Start node on initial load
  useEffect(function() {
    var canvas = canvasRef.current;
    if (!canvas) return;
    var startNode = nodes.find(function(n) { return n && n.icon === 'start'; });
    if (!startNode) return;
    var pos = startNode.position || { left: 0, top: 0 };
    var nodeW = (sizes[startNode.id] && sizes[startNode.id].width) || 220;
    var nodeH = (sizes[startNode.id] && sizes[startNode.id].height) || 48;
    centerOnNode(pos, nodeW, nodeH);
  }, []);


  useEffect(function() {
    if (!executionState || executionState.status !== 'running') return;
    var timerId = setInterval(function() {
      setTimerNowMs(Date.now());
    }, 200);
    return function() {
      clearInterval(timerId);
    };
  }, [executionState && executionState.status]);

  function distanceFromPointToRect(point, rect) {
    var dx = 0;
    if (point.x < rect.left) dx = rect.left - point.x;
    else if (point.x > rect.right) dx = point.x - rect.right;

    var dy = 0;
    if (point.y < rect.top) dy = rect.top - point.y;
    else if (point.y > rect.bottom) dy = point.y - rect.bottom;

    return Math.sqrt(dx * dx + dy * dy);
  }

  function findTargetNodeAtPoint(dropPoint, sourceId) {
    var capturePadding = 20;
    var snapDistance = 34;
    var bestNode = null;
    var bestScore = Number.POSITIVE_INFINITY;

    for (var i = 0; i < nodes.length; i += 1) {
      var node = nodes[i];
      if (node.id === sourceId) continue;
      if (nodeIdToGroupId[node.id]) continue;
      if (!canAcceptIncomingConnection(node)) continue;
      var nodeRect = rectById[node.id];
      if (!nodeRect) continue;

      var isInCaptureZone =
        dropPoint.x >= nodeRect.left - capturePadding &&
        dropPoint.x <= nodeRect.right + capturePadding &&
        dropPoint.y >= nodeRect.top - capturePadding &&
        dropPoint.y <= nodeRect.bottom + capturePadding;

      var edgeDistance = distanceFromPointToRect(dropPoint, nodeRect);
      if (!isInCaptureZone && edgeDistance > snapDistance) continue;
      var cx = (nodeRect.left + nodeRect.right) / 2;
      var cy = (nodeRect.top + nodeRect.bottom) / 2;
      var centerDx = dropPoint.x - cx;
      var centerDy = dropPoint.y - cy;
      var centerDistance = Math.sqrt(centerDx * centerDx + centerDy * centerDy);
      var score = (edgeDistance * 1000) + centerDistance;

      if (score < bestScore) {
        bestScore = score;
        bestNode = node;
      }
    }

    var cGroups = Array.isArray(workflowState.groups) ? workflowState.groups : [];
    for (var gi = 0; gi < cGroups.length; gi++) {
      var g = cGroups[gi];
      if (!g.collapsed || g.id === sourceId) continue;
      var gRect = rectById[g.id];
      if (!gRect) continue;
      var gInZone =
        dropPoint.x >= gRect.left - capturePadding &&
        dropPoint.x <= gRect.right + capturePadding &&
        dropPoint.y >= gRect.top - capturePadding &&
        dropPoint.y <= gRect.bottom + capturePadding;
      var gEdge = distanceFromPointToRect(dropPoint, gRect);
      if (!gInZone && gEdge > snapDistance) continue;
      var gScore = (gEdge * 1000) + Math.sqrt(Math.pow(dropPoint.x - gRect.cx, 2) + Math.pow(dropPoint.y - gRect.cy, 2));
      if (gScore < bestScore) {
        bestScore = gScore;
        bestNode = { id: g.id, kind: 'workflow-node', icon: 'group', position: g.position };
      }
    }

    return bestNode;
  }

  // Resolve a group ID to its entry node or exit node
  function resolveGroupEndpoint(groupId, direction) {
    var group = groupById[groupId];
    if (!group) return groupId;
    var memberSet = {};
    for (var i = 0; i < group.nodeIds.length; i++) memberSet[group.nodeIds[i]] = true;
    var conns = workflowStateRef.current.connections || [];
    var hasInternalIncoming = {};
    var hasInternalOutgoing = {};
    for (var ci = 0; ci < conns.length; ci++) {
      var c = conns[ci];
      if (memberSet[c.from] && memberSet[c.to]) {
        hasInternalIncoming[c.to] = true;
        hasInternalOutgoing[c.from] = true;
      }
    }
    if (direction === 'entry') {
      for (var ei = 0; ei < group.nodeIds.length; ei++) {
        if (!hasInternalIncoming[group.nodeIds[ei]]) return group.nodeIds[ei];
      }
    } else {
      for (var xi = 0; xi < group.nodeIds.length; xi++) {
        if (!hasInternalOutgoing[group.nodeIds[xi]]) return group.nodeIds[xi];
      }
    }
    return direction === 'entry' ? group.nodeIds[0] : group.nodeIds[group.nodeIds.length - 1];
  }

  // Nodes list for the minimap
  var miniMapNodes = useMemo(function() {
    var filtered = nodes.filter(function(node) {
      return !nodeIdToGroupId[node.id];
    });
    collapsedGroups.forEach(function(g) {
      if (g.expanded) return;
      filtered.push({
        id: g.id,
        kind: 'workflow-node',
        nodeType: 'card',
        icon: 'group',
        title: g.name,
        position: g.position
      });
    });
    return filtered;
  }, [nodes, collapsedGroups, nodeIdToGroupId]);

  // Maps condition nodeId -> { input, yes, no } explicit anchors
  var conditionAxesMap = useMemo(function() {
    var map = {};
    Object.keys(nodeById).forEach(function(nodeId) {
      var toNode = nodeById[nodeId];
      if (!isConditionNode(toNode)) return;
      var incoming = (connections || []).find(function(conn) {
        return conn && String(conn.to) === String(nodeId);
      }) || null;
      var inputAnchor = incoming && incoming.toAnchor ? String(incoming.toAnchor) : null;
      map[nodeId] = getConditionBranchStateFromAnchor(toNode, inputAnchor);
    });
    return map;
  }, [connections, nodeById]);

  // Variable nodes ordered by workflow execution order (BFS from Start,
  // first-touch wins). Keeping a single sorted source of truth means the
  // side panel, the autocomplete dropdown, and the Set Variable picker all
  // list variables in the same workflow-meaningful order.
  var variableNodes = useMemo(function() {
    var raw = nodes.filter(function(n) {
      return n && n.kind === 'workflow-node' && n.icon === 'variables';
    });
    return sortVariableNodesByExecutionOrder(raw, nodes, connections);
  }, [nodes, connections]);

  // Existing variable names, in workflow order — offered by the output panel's
  // "set to variable" chooser so a value can target an existing variable.
  var variableNames = useMemo(function() {
    return variableNodes
      .map(function(n) { return String(n.title || '').trim(); })
      .filter(Boolean);
  }, [variableNodes]);

  function resolveSetVariableTemplate() {
    for (var i = 0; i < objectTemplates.length; i++) {
      if (objectTemplates[i].id === 'sys-set-variable' || objectTemplates[i].icon === 'set-variable') return objectTemplates[i];
    }
    return {
      id: 'sys-set-variable', kind: 'workflow-node', nodeType: 'card', category: 'system',
      icon: 'set-variable', title: 'Set Variable', subtitle: 'Update a variable at runtime',
      hyperparameters: [
        { id: 'setvar-variable', key: 'variable', type: 'string', default: '' },
        { id: 'setvar-source', key: 'source', type: 'boolean', default: 'false' },
        { id: 'setvar-value', key: 'value', type: 'string', default: '' }
      ]
    };
  }

  // Capture a node's output (at a dot-path) into a variable: drop a Set Variable
  // node wired FROM the source at that path, creating the target variable when
  // asked, then run the shared autofill so the value shows immediately (baked for
  // persistent vars, live preview for temporary ones). Called by NodeOutputPanel.
  function addSetVariableForOutput(sourceNodeId, payload) {
    if (!payload || !payload.target) return;
    var targetName = String(payload.target.name || '').trim();
    if (!targetName) return;
    var tempPreview = null;
    setWorkflowState(function(prev) {
      var built = buildSetVariableForOutput(prev.nodes || [], prev.connections || [], {
        sourceNodeId: sourceNodeId,
        propPath: payload.path,
        targetName: targetName,
        createVariable: payload.target.kind === 'new',
        sampleValue: payload.value,
        variableTemplate: resolveVariableTemplate(),
        setVariableTemplate: resolveSetVariableTemplate()
      });
      if (!built) return prev;
      var auto = computeSetVariableAutofill(built.nodes, built.connections, built.setVarId, nodeOutputByIdRef.current);
      tempPreview = auto.tempPreview;
      return Object.assign({}, prev, { nodes: auto.nextNodes, connections: built.connections });
    });
    if (tempPreview) onSetVariableLivePreview(tempPreview.varName, tempPreview.value);
  }

  function resolveVariableTemplate() {
    for (var i = 0; i < objectTemplates.length; i++) {
      if (objectTemplates[i].icon === 'variables') return objectTemplates[i];
    }
    return {
      id: 'sys-variables', kind: 'workflow-node', nodeType: 'card', category: 'system',
      icon: 'variables', title: 'Variable', subtitle: 'Store typed context values',
      hyperparameters: [
        { id: 'var-data-type', key: 'dataType', type: 'string', default: 'string' },
        { id: 'var-value', key: 'value', type: 'string', default: '' },
        { id: 'var-retain', key: 'retain', type: 'boolean', default: 'false' }
      ]
    };
  }

  function buildVariableNode(title, paramOverrides, position) {
    var newId = 'sys-variables-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);
    var newNode = cloneTemplateNode(resolveVariableTemplate(), newId, position || { left: 0, top: 0 });
    newNode.title = title;
    if (paramOverrides) {
      newNode.paramValues = Object.assign({}, newNode.paramValues || {}, paramOverrides);
    }
    return newNode;
  }

  function addVariableNode() {
    if (isEmbed) return;
    var existingNodes = (workflowStateRef.current && workflowStateRef.current.nodes) || [];
    // Pick a name that can't inherit a value the moment it's created: skip names
    // already used by a variable, targeted by a Set Variable, or still carrying
    // a leftover live runtime value (which the panel shows by name). Otherwise a
    // freshly added "Variable 1" can show stale data from a prior run.
    var taken = {};
    existingNodes.forEach(function(n) {
      if (!n) return;
      if (n.icon === 'variables') { var t = String(n.title || '').trim(); if (t) taken[t] = true; }
      if (n.templateId === 'sys-set-variable' || n.icon === 'set-variable') {
        var sv = String((n.paramValues || {}).variable || '').trim(); if (sv) taken[sv] = true;
      }
    });
    Object.keys(variableValuesByName || {}).forEach(function(k) { taken[k] = true; });
    var idx = 1;
    while (taken['Variable ' + idx]) idx++;
    var newNode = buildVariableNode('Variable ' + idx, null, { left: 0, top: 0 });
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, { nodes: (prev.nodes || []).concat(newNode) });
    });
  }

  function upsertVariableByName(name, value, options) {
    var normalizedName = String(name || '').trim();
    if (!normalizedName) return;
    var overrides = Object.assign({ value: value == null ? '' : String(value) }, (options && options.paramOverrides) || {});
    setWorkflowState(function(prev) {
      var nodes = prev.nodes || [];
      var existingIdx = -1;
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (n && n.icon === 'variables' && String(n.title || '').trim() === normalizedName) { existingIdx = i; break; }
      }
      if (existingIdx >= 0) {
        var updated = Object.assign({}, nodes[existingIdx]);
        updated.paramValues = Object.assign({}, updated.paramValues || {}, overrides);
        var nextNodes = nodes.slice();
        nextNodes[existingIdx] = updated;
        return Object.assign({}, prev, { nodes: nextNodes });
      }
      var newNode = buildVariableNode(normalizedName, overrides, (options && options.position) || { left: 40, top: 40 });
      return Object.assign({}, prev, { nodes: nodes.concat(newNode) });
    });
  }

  var upsertVariableRef = useRef(null);
  upsertVariableRef.current = upsertVariableByName;
  useEffect(function() {
    function handleSetVariable(event) {
      var detail = event.detail || {};
      if (!detail.name) return;
      if (upsertVariableRef.current) upsertVariableRef.current(detail.name, detail.value, detail.options);
    }
    window.addEventListener('set-workflow-variable', handleSetVariable);
    return function() { window.removeEventListener('set-workflow-variable', handleSetVariable); };
  }, []);

  function getEditorContextNodes(nodeId) {
    return buildEditorContextNodes(nodeId, nodes, connections, nodeOutputById, variableValuesByName);
  }

  // Auto-pan/zoom to running nodes
  var runningNodesKey = Object.keys(nodeStatusById)
    .filter(function(id) { return nodeStatusById[id] === 'running'; })
    .sort()
    .join(',');

  useEffect(function() {
    var rawIds = runningNodesKey ? runningNodesKey.split(',').filter(Boolean) : [];
    if (rawIds.length === 0) return;
    var seen = {};
    var ids = [];
    rawIds.forEach(function(id) {
      var mapped = nodeIdToGroupId[id] || id;
      if (!seen[mapped]) { seen[mapped] = true; ids.push(mapped); }
    });

    var canvas = canvasRef.current;
    if (!canvas) return;
    var canvasW = canvas.clientWidth;
    var canvasH = canvas.clientHeight;

    if (normalZoomRef.current === null) {
      normalZoomRef.current = zoomRef.current;
    }

    var currentZoom = zoomRef.current;
    var targetZoom, targetX, targetY;

    if (ids.length === 1) {
      var el = nodeRefs.current[ids[0]];
      if (el) {
        var canvasRect = canvas.getBoundingClientRect();
        var nodeRect   = el.getBoundingClientRect();
        var nodeCxCanvas = (nodeRect.left + nodeRect.right)   / 2 - canvasRect.left;
        var nodeCyCanvas = (nodeRect.top  + nodeRect.bottom)  / 2 - canvasRect.top;
        targetZoom = currentZoom;
        targetX = panPosRef.current.x + (canvasW / 2 - nodeCxCanvas);
        targetY = panPosRef.current.y + (canvasH / 2 - nodeCyCanvas);
      } else {
        var singleRect = rectByIdRef.current[ids[0]];
        if (!singleRect) return;
        targetZoom = currentZoom;
        targetX = canvasW / 2 - singleRect.cx * targetZoom;
        targetY = canvasH / 2 - singleRect.cy * targetZoom;
      }
    } else {
      var minLeft = Infinity, minTop = Infinity, maxRight = -Infinity, maxBottom = -Infinity;
      var validCount = 0;
      ids.forEach(function(id) {
        var rect = rectByIdRef.current[id];
        if (!rect) return;
        minLeft   = Math.min(minLeft,   rect.left);
        minTop    = Math.min(minTop,    rect.top);
        maxRight  = Math.max(maxRight,  rect.right);
        maxBottom = Math.max(maxBottom, rect.bottom);
        validCount++;
      });
      if (validCount === 0) return;
      var bboxCx = (minLeft + maxRight) / 2;
      var bboxCy = (minTop  + maxBottom) / 2;
      var padding = 140;
      var bboxW = maxRight - minLeft;
      var bboxH = maxBottom - minTop;
      var fitZoom = Math.min(
        (canvasW - padding * 2) / bboxW,
        (canvasH - padding * 2) / bboxH,
        1.5
      );
      targetZoom = Math.max(fitZoom * 0.65, 0.22);
      targetX = canvasW / 2 - bboxCx * targetZoom;
      targetY = canvasH / 2 - bboxCy * targetZoom;
    }

    autoPanAnimateTo(targetX, targetY, targetZoom, 750);

    return function() {
      if (autoPanAnimRef.current) { cancelAnimationFrame(autoPanAnimRef.current); autoPanAnimRef.current = null; }
    };
  }, [runningNodesKey]);

  // Restore zoom when execution finishes
  var execStatus = executionState ? executionState.status : 'idle';
  useEffect(function() {
    if (execStatus !== 'completed' && execStatus !== 'failed') return;
    if (normalZoomRef.current === null) return;

    var restoreZoom = normalZoomRef.current;
    normalZoomRef.current = null;

    var canvas = canvasRef.current;
    if (!canvas) return;
    var canvasW = canvas.clientWidth;
    var canvasH = canvas.clientHeight;
    var wCx = (canvasW / 2 - panPosRef.current.x) / zoomRef.current;
    var wCy = (canvasH / 2 - panPosRef.current.y) / zoomRef.current;
    autoPanAnimateTo(canvasW / 2 - wCx * restoreZoom, canvasH / 2 - wCy * restoreZoom, restoreZoom, 700);
  }, [execStatus]);

  function normalizeBendPoints(connection) {
    var points = Array.isArray(connection && connection.bendPoints) ? connection.bendPoints : [];
    return points
      .map(function(point) {
        var x = Number(point && point.x);
        var y = Number(point && point.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
        return { x: x, y: y };
      })
      .filter(Boolean);
  }

  var resolvedConnections = useMemo(function() {
    var seen = {};
    return connections
      .map(function(connection) {
        var fromId = connection.from;
        var toId = connection.to;
        var fromGrouped = nodeIdToGroupId[fromId];
        var toGrouped = nodeIdToGroupId[toId];

        if (fromGrouped && toGrouped && fromGrouped === toGrouped) return null;

        if (fromGrouped) fromId = fromGrouped;
        if (toGrouped) toId = toGrouped;

        // When grouping collapses both endpoints onto the same group rect, all
        // internal edges should render as a single group-to-group line — key on
        // ids only. For ungrouped edges (e.g. multiple router routes wired to
        // the same target), include the anchors so each per-anchor line renders
        // as its own connector instead of being silently dropped.
        var dedupKey = (fromGrouped || toGrouped)
          ? fromId + '>' + toId
          : fromId + '>' + toId + '|' + (connection.fromAnchor || '') + '>' + (connection.toAnchor || '');
        if (seen[dedupKey]) return null;
        seen[dedupKey] = true;

        var fromRect = rectById[fromId];
        var toRect = rectById[toId];
        if (!fromRect || !toRect) return null;
        var fromNode = nodeById[fromId];
        var toNode = nodeById[toId];
        var fromAnchor = normalizeSourceAnchorForNode(fromNode, connection.fromAnchor || 'bottom', fromRect, toRect, conditionAxesMap);
        var toAnchor = normalizeTargetAnchorForNode(toNode, connection.toAnchor || 'top', toRect, fromRect);
        var bendPoints = (fromGrouped || toGrouped) ? [] : normalizeBendPoints(connection);
        var pathPoints = [anchorPoint(fromRect, fromAnchor, fromNode, !isNodeDetailsExpanded(fromId))].concat(bendPoints).concat([anchorPoint(toRect, toAnchor, toNode, !isNodeDetailsExpanded(toId))]);

        var targetGroupFlowing = false;
        var flowGroup = toGrouped ? groupById[toGrouped] : groupById[connection.to];
        if (flowGroup && executionState && executionState.status === 'running') {
          var anyStarted = false;
          var allDone = true;
          for (var tgi = 0; tgi < flowGroup.nodeIds.length; tgi++) {
            var tgStatus = nodeStatusById[flowGroup.nodeIds[tgi]];
            if (tgStatus) anyStarted = true;
            if (tgStatus !== 'completed' && tgStatus !== 'failed') allDone = false;
          }
          if (anyStarted && !allDone) targetGroupFlowing = true;
        }
        // Tool wires (Tool Agent bottom anchor → a tool node) are NOT traversed
        // by the normal wave — the agent calls each tool inside its own loop. So
        // animate the wire whenever its target tool node is running, i.e. while
        // the agent is actively calling that tool (incoming → outgoing flow).
        var isToolWire = isToolAgentNode(fromNode) && fromAnchor === 'bottom';
        var isToolWireFlowing = isToolWire && nodeStatusById[connection.to] === 'running';

        var isFlowingIntoRunningNode = !!(
          executionState &&
          (targetGroupFlowing || isToolWireFlowing || (
            traversedConnectionIds[connection.id] &&
            (
              nodeStatusById[connection.to] === 'running' ||
              (nodeFixById[connection.to] && nodeFixById[connection.to].active)
            )
          ))
        );

        return {
          id: connection.id,
          className: getConnectionClassName(connection, fromNode, fromAnchor, conditionAxesMap, toNode) +
            (executionState && executionState.status === 'running' && isFlowingIntoRunningNode ? ' conn-line-flowing' : ''),
          fromId: fromId,
          toId: toId,
          fromAnchor: fromAnchor,
          toAnchor: toAnchor,
          from: pathPoints[0],
          to: pathPoints[pathPoints.length - 1],
          bendPoints: bendPoints,
          pathPoints: pathPoints,
          pathD: smoothPathThroughPoints(pathPoints, fromAnchor, toAnchor),
          propPath: connection.propPath || null,
          noWait: connection.noWait === true,
          noData: connection.noData === true,
          branch: connection.branch || null,
          // Tool wire (Tool Agent bottom anchor → tool node): the agent owns this
          // edge, so the connector action toolbar (Delete/Run & Wait/Cut Data
          // Flow/Select Field) is hidden for it — those actions don't apply.
          isToolWire: isToolWire
        };
      })
      .filter(Boolean);
  }, [connections, rectById, nodeById, nodeIdToGroupId, groupById, conditionAxesMap, traversedConnectionIds, executionState && executionState.status, nodeStatusById, nodeFixById, showNodeDetails, hoveredPreviewNodeId]);

  var downstreamFlowHighlight = useMemo(function() {
    return computeDownstreamFlow(hoveredFlowNodeId, connections);
  }, [hoveredFlowNodeId, connections]);

  // Per-node usage map: nodeId -> { reads: {Var:true}, writes: {Var:true} }.
  // Recomputed only when the nodes array changes — cheap regex pass.
  var varUsageByNodeId = useMemo(function() {
    var map = {};
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (!n || !n.id) continue;
      map[n.id] = collectVarRefsForNode(n);
    }
    return map;
  }, [nodes]);

  // Inverted: variable title -> array of node ids that reference it. Used by
  // the hover pan/zoom-fit math.
  var nodeIdsByVariableName = useMemo(function() {
    var byName = {};
    for (var id in varUsageByNodeId) {
      if (!Object.prototype.hasOwnProperty.call(varUsageByNodeId, id)) continue;
      var usage = varUsageByNodeId[id];
      var seen = {};
      var name;
      for (name in usage.reads) {
        if (Object.prototype.hasOwnProperty.call(usage.reads, name)) seen[name] = true;
      }
      for (name in usage.writes) {
        if (Object.prototype.hasOwnProperty.call(usage.writes, name)) seen[name] = true;
      }
      for (name in seen) {
        if (!Object.prototype.hasOwnProperty.call(seen, name)) continue;
        if (!byName[name]) byName[name] = [];
        byName[name].push(id);
      }
    }
    return byName;
  }, [varUsageByNodeId]);

  // Set of variable titles that no other node reads or writes. The variable
  // panel uses this to show a yellow exclamation badge on unused rows so the
  // user can spot dead variables at a glance. `nodeIdsByVariableName` already
  // excludes the Variable definition node itself (collectVarRefsForNode skips
  // icon === 'variables'), so a missing/empty entry means truly unused.
  var unusedVariableNames = useMemo(function() {
    var set = {};
    for (var i = 0; i < variableNodes.length; i++) {
      var v = variableNodes[i];
      if (!v) continue;
      var name = String(v.title || '').trim();
      if (!name) continue;
      var users = nodeIdsByVariableName[name];
      if (!users || users.length === 0) set[name] = true;
    }
    return set;
  }, [variableNodes, nodeIdsByVariableName]);

  // ── Hover preview hook (variable-side-panel pan/zoom) ─────────────────
  // Must be called AFTER nodeIdsByVariableName is computed above — the hook
  // reads it as a dep and would see `undefined` if called earlier in the
  // function body (var hoisting binds the name but not the useMemo result).
  var hoverPreview = useHoverPreview({
    canvasRef: canvasRef,
    panPosRef: panPosRef,
    zoomRef: zoomRef,
    rectByIdRef: rectByIdRef,
    hoveredVariableName: hoveredVariableName,
    setHoveredVariableName: setHoveredVariableName,
    nodeIdsByVariableName: nodeIdsByVariableName,
    autoPanAnimateTo: autoPanAnimateTo,
    focusNodeId: runLogFocusNodeId
  });
  var pinCurrentHoverView = hoverPreview.pinCurrentHoverView;

  // For the hovered variable: nodeId -> 'read' | 'set' | 'both'. Used by the
  // chip rendered next to highlighted nodes.
  var hoveredVariableUsageByNodeId = useMemo(function() {
    var map = {};
    if (!hoveredVariableName) return map;
    for (var id in varUsageByNodeId) {
      if (!Object.prototype.hasOwnProperty.call(varUsageByNodeId, id)) continue;
      var u = varUsageByNodeId[id];
      var hasRead = !!u.reads[hoveredVariableName];
      var hasWrite = !!u.writes[hoveredVariableName];
      if (hasRead && hasWrite) map[id] = 'both';
      else if (hasWrite) map[id] = 'set';
      else if (hasRead) map[id] = 'read';
    }
    return map;
  }, [hoveredVariableName, varUsageByNodeId]);

  // Quick lookup for the className builder below.
  var hoveredVariableNodeIdSet = useMemo(function() {
    var set = {};
    if (!hoveredVariableName) return set;
    for (var id in hoveredVariableUsageByNodeId) {
      if (Object.prototype.hasOwnProperty.call(hoveredVariableUsageByNodeId, id)) set[id] = true;
    }
    return set;
  }, [hoveredVariableName, hoveredVariableUsageByNodeId]);

  // Unified hover-focus for the highlight/dim effect: a variable hover lights
  // every node touching that variable; a Run Log line hover lights the single
  // matching node. Both share the same node-var-using / node-var-dim styling.
  var focusActive = !!hoveredVariableName || !!runLogFocusNodeId;
  var focusedNodeIdSet = useMemo(function() {
    if (runLogFocusNodeId) {
      var single = {};
      single[String(runLogFocusNodeId)] = true;
      return single;
    }
    return hoveredVariableNodeIdSet;
  }, [runLogFocusNodeId, hoveredVariableNodeIdSet]);

  // Inverse of the variable-hover dim: when the user hovers a NODE on the
  // canvas, the variables panel should fade rows whose variable this node
  // doesn't read or write. This map gives the panel the set of variable
  // titles touched by the hovered node — null when no node is hovered (or
  // when the hovered node is a Variable definition itself, which would
  // otherwise produce an empty set and hide every row in the panel).
  var hoveredNodeVariableNames = useMemo(function() {
    if (!hoveredFlowNodeId) return null;
    if (nodeDrag.draggingId) return null;
    var usage = varUsageByNodeId[hoveredFlowNodeId];
    if (!usage) return null;
    var names = {};
    var k;
    for (k in usage.reads) {
      if (Object.prototype.hasOwnProperty.call(usage.reads, k)) names[k] = true;
    }
    for (k in usage.writes) {
      if (Object.prototype.hasOwnProperty.call(usage.writes, k)) names[k] = true;
    }
    return names;
  }, [hoveredFlowNodeId, varUsageByNodeId, nodeDrag.draggingId]);
  // Suppress the hover highlight the moment any drag starts (node drag sets
  // nodeDrag.draggingId; canvas pan clears hoveredFlowNodeId directly in
  // onCanvasMouseDown) so the user can reposition without a dimmed backdrop.
  var hasFlowHighlight = hoveredFlowNodeId != null
    && !nodeDrag.draggingId
    && Object.keys(downstreamFlowHighlight.connIds).length > 0;

  var usedOutgoingAnchorsByNode = useMemo(function() {
    var map = {};
    connections.forEach(function(connection) {
      var fromId = connection && connection.from;
      var toId = connection && connection.to;
      if (!fromId || !toId) return;
      var fromRect = rectById[fromId];
      var toRect = rectById[toId];
      var fromNode = nodeById[fromId];
      if (!fromRect || !toRect || !fromNode) return;
      var normalizedAnchor = normalizeSourceAnchorForNode(fromNode, connection.fromAnchor || 'bottom', fromRect, toRect, conditionAxesMap);
      if (!map[fromId]) map[fromId] = {};
      map[fromId][normalizedAnchor] = true;
    });
    return map;
  }, [connections, rectById, nodeById, conditionAxesMap]);

  var futureConnectors = useMemo(function() {
    var gap = 170;
    var items = [];
    var execStatus = executionState && executionState.status ? executionState.status : 'idle';
    var nodeStatus = executionState && executionState.nodeStatusById ? executionState.nodeStatusById : {};
    var nodeTiming = executionState && executionState.nodeTimingById ? executionState.nodeTimingById : {};

    var eligibleNodeIds = {};

    // Tool nodes (wired from a Tool Agent's bottom anchor) are invoked BY the
    // agent, not part of the normal downstream flow — so they must never get a
    // "What's Next?" future connector even though they look terminal.
    var isToolWiredNode = function(nodeId) {
      return (connections || []).some(function(c) {
        return c && String(c.fromAnchor || '') === 'bottom'
          && String(c.to) === String(nodeId)
          && isToolAgentNode(nodeById[String(c.from)]);
      });
    };

    if (execStatus === 'completed') {
      var lastExecutedTerminalNodeId = null;
      var lastEndedAt = -1;
      nodes.forEach(function(node) {
        if (!node || !node.id) return;
        if (nodeIdToGroupId[node.id]) return;
        if (isToolWiredNode(node.id)) return;
        if (!isTerminalNode(node.id, connections)) return;
        if (nodeStatus[node.id] !== 'completed') return;
        var timing = nodeTiming[node.id] || {};
        var endedAt = Number(timing.endedAt || timing.startedAt || 0);
        if (endedAt > lastEndedAt) {
          lastEndedAt = endedAt;
          lastExecutedTerminalNodeId = node.id;
        }
      });
      if (lastExecutedTerminalNodeId) eligibleNodeIds[lastExecutedTerminalNodeId] = true;
    }

    nodes.forEach(function(node) {
      if (!node || !node.id) return;
      if (node.icon !== 'start') return;
      if (nodeIdToGroupId[node.id]) return;
      if (!isTerminalNode(node.id, connections)) return;
      eligibleNodeIds[node.id] = true;
    });

    if (!Object.keys(eligibleNodeIds).length) return items;

    nodes.forEach(function(node) {
      if (!node || !eligibleNodeIds[node.id]) return;
      var rect = rectById[node.id];
      if (!rect) return;
      var candidates = getFutureCandidateAnchors(node);
      if (isClassifierLikeNode(node)) {
        var preferredClassifierAnchor = getHighestConfidenceClassifierAnchor(node, nodeOutputById[node.id]);
        candidates = preferredClassifierAnchor ? [preferredClassifierAnchor] : candidates;
      }
      if (!candidates.length) return;
      var usedAnchors = usedOutgoingAnchorsByNode[node.id] || {};

      candidates.forEach(function(anchor) {
        if (usedAnchors[anchor]) return;
        var from = anchorPoint(rect, anchor, node, !isNodeDetailsExpanded(node.id));
        var dir = anchorDir(anchor);
        var to = {
          x: Math.round(from.x + (dir.x * gap)),
          y: Math.round(from.y + (dir.y * gap))
        };
        var buttonLeft = Math.round(to.x);
        var buttonTop = Math.round(to.y - (FUTURE_CONNECTOR_BUTTON_HEIGHT / 2));
        items.push({
          id: node.id + '-future-' + anchor,
          nodeId: node.id,
          nodeTitle: node.title || 'Node',
          anchor: anchor,
          from: from,
          to: to,
          buttonLeft: buttonLeft,
          buttonTop: buttonTop,
          pathD: smoothPath(from, to, anchor, anchor)
        });
      });
    });

      return items;
    }, [nodes, rectById, usedOutgoingAnchorsByNode, executionState, connections, showNodeDetails, hoveredPreviewNodeId, nodeIdToGroupId, nodeById]);

  // Node ids that are wired as a tool (target of a Tool Agent's bottom anchor).
  // Used to render them smaller + in a distinct color.
  var toolNodeIds = useMemo(function() {
    var set = {};
    (connections || []).forEach(function(c) {
      if (!c || String(c.fromAnchor || '') !== 'bottom') return;
      var fromNode = nodeById[String(c.from)];
      if (isToolAgentNode(fromNode)) set[String(c.to)] = true;
    });
    return set;
  }, [connections, nodeById]);

  // Max tools wired to one Tool Calling node's bottom anchor.
  // NOTE: declared BEFORE the memo below that reads it — a `var` declared after
  // the useMemo would be hoisted-but-undefined when the memo factory runs during
  // render, so the cap check (toolCount >= undefined) would never fire.
  var TOOL_AGENT_MAX_TOOLS = 10;
  // Size of the round "+" add-tool button, used to center it (by its left/top
  // edge) on the wire endpoint below the bottom anchor. Keep in sync with the
  // .tool-add-btn width/height in WorkflowCanvas.css.
  var TOOL_ADD_BTN_WIDTH = 32;
  var TOOL_ADD_BTN_HEIGHT = 32;

  // "+ add tool" connector: one per Tool Calling node with fewer than the max
  // tools wired to its bottom anchor. A dashed wire drops straight down from the
  // bottom anchor to the button, which sits CENTERED below the node. The
  // descriptor matches the future-connector shape so it can drive
  // handleGenerateFlowWithAi (the custom-node generator).
  var toolConnectorButtons = useMemo(function() {
    var items = [];
    nodes.forEach(function(node) {
      if (!node || !isToolAgentNode(node)) return;
      if (nodeIdToGroupId[node.id]) return;
      var rect = rectById[node.id];
      if (!rect) return;
      var toolCount = (connections || []).filter(function(c) {
        return c && String(c.from) === String(node.id) && String(c.fromAnchor || '') === 'bottom';
      }).length;
      if (toolCount >= TOOL_AGENT_MAX_TOOLS) return;
      var from = anchorPoint(rect, 'bottom', node, !isNodeDetailsExpanded(node.id));
      // Sit the round button CENTERED directly below the bottom anchor (straight
      // drop, no diagonal). The wire endpoint is on the anchor's x; the button's
      // left/top edges are shifted by half its size so its CENTER lands on the
      // wire endpoint. Stays put as tools are added (generated tool nodes are
      // laid out separately by the generator).
      var to = { x: Math.round(from.x), y: Math.round(from.y + 78) };
      var buttonLeft = Math.round(from.x - (TOOL_ADD_BTN_WIDTH / 2));
      var buttonTop = Math.round(to.y - (TOOL_ADD_BTN_HEIGHT / 2));
      items.push({
        id: node.id + '-tool-add',
        nodeId: node.id,
        from: from,
        to: to,
        pathD: smoothPath(from, to, 'bottom', 'top'),
        buttonLeft: buttonLeft,
        buttonTop: buttonTop,
        // Descriptor consumed by handleGenerateFlowWithAi → createNodeFromAiTemplate.
        futureConnector: {
          id: node.id + '-tool-future',
          nodeId: node.id,
          nodeTitle: node.title || 'Tool Calling',
          anchor: 'bottom',
          from: from,
          to: to,
          buttonLeft: buttonLeft,
          buttonTop: buttonTop
        }
      });
    });
    return items;
  }, [nodes, rectById, connections, showNodeDetails, nodeIdToGroupId]);

  function nodeFlowLabel(node, incomingConn) {
    if (!node) return null;
    var icon = String(node.icon || '').trim();
    var title = String(node.title || '').trim();
    if (icon === 'variables') return null;
    if (icon === 'start') {
      var paramKeys = Array.isArray(node.hyperparameters) && node.hyperparameters.length
        ? node.hyperparameters.map(function(p) { return p.key; }).join(', ')
        : '';
      return 'Start' + (paramKeys ? ' [inputs: ' + paramKeys + ']' : '');
    }
    if (icon === 'set-variable') {
      var pv = node.paramValues || {};
      var targetVar = String(pv.variable || '').trim();
      var isFromInput = String(pv.source || 'false') === 'true';
      var propPath = incomingConn && incomingConn.propPath ? '.' + String(incomingConn.propPath).trim() : '';
      if (targetVar) {
        return 'Set Variable [→ "' + targetVar + '"' + (isFromInput ? ', from input' + propPath : ', value: ' + String(pv.value || '').trim()) + ']';
      }
      return 'Set Variable';
    }
    if (icon === 'condition') {
      var cpv = node.paramValues || {};
      var condVar = String(cpv.variable || '').trim();
      var rule = node.conditionRule || {};
      var conds = Array.isArray(rule.conditions)
        ? rule.conditions.map(function(c) { return (c.operator || '') + (c.value !== undefined && c.value !== '' ? ' ' + c.value : ''); }).join(', ')
        : '';
      return 'Condition [' + (condVar ? condVar + ' ' : '') + conds + ']';
    }
    return title || node.id;
  }

  function buildUpstreamNodeFlow(nodeId) {
    var normalizedTargetId = String(nodeId || '').trim();
    if (!normalizedTargetId) return [];

    var incomingByTarget = {};
    var incomingConnByTarget = {};
    (connections || []).forEach(function(connection) {
      if (!connection || !connection.to || !connection.from) return;
      var toId = String(connection.to);
      if (!incomingByTarget[toId]) incomingByTarget[toId] = [];
      incomingByTarget[toId].push(String(connection.from));
      incomingConnByTarget[toId] = connection;
    });

    var seen = {};

    function visit(currentId) {
      var normalizedId = String(currentId || '').trim();
      if (!normalizedId || seen[normalizedId]) return [];
      seen[normalizedId] = true;

      var upstreamIds = incomingByTarget[normalizedId] || [];
      var bestPath = [];

      for (var i = 0; i < upstreamIds.length; i += 1) {
        var candidatePath = visit(upstreamIds[i]);
        if (candidatePath.length > bestPath.length) {
          bestPath = candidatePath;
        }
      }

      var currentNode = nodeById[normalizedId];
      var incomingConn = incomingConnByTarget[normalizedId] || null;
      var label = nodeFlowLabel(currentNode, incomingConn);
      if (label === null) return bestPath;
      return bestPath.concat([label]);
    }

    return visit(normalizedTargetId);
  }

  // ── AI Generation hook ──────────────────────────────────────────────────
  var aiFlow = useAiFlowGeneration({
    canvasRef: canvasRef,
    panPosRef: panPosRef,
    zoomRef: zoomRef,
    worldRef: worldRef,
    autoPanAnimRef: autoPanAnimRef,
    applyWorldStyle: applyWorldStyle,
    nodes: nodes,
    nodeById: nodeById,
    nodeOutputByIdRef: nodeOutputByIdRef,
    variableValuesByNameRef: variableValuesByNameRef,
    workflowStateRef: workflowStateRef,
    executionState: executionState,
    setWorkflowState: setWorkflowState,
    setSelectedNodeIds: setSelectedNodeIds,
    setSelectedConnectionId: setSelectedConnectionId,
    buildUpstreamNodeFlow: buildUpstreamNodeFlow,
    props: props
  });
  var aiPromptState = aiFlow.aiPromptState;
  var aiCodingState = aiFlow.aiCodingState;
  var setAiPromptState = aiFlow.setAiPromptState;
  var setAiCodingState = aiFlow.setAiCodingState;
  var handleGenerateFlowWithAi = aiFlow.handleGenerateFlowWithAi;
  var handleInsertNodeOnConnection = aiFlow.handleInsertNodeOnConnection;
  var closeAiPrompt = aiFlow.closeAiPrompt;
  var updateAiPrompt = aiFlow.updateAiPrompt;
  var updateAiAgentMode = aiFlow.updateAiAgentMode;
  var updateAiModelTier = aiFlow.updateAiModelTier;
  var updateAiResponseFields = aiFlow.updateAiResponseFields;
  var updateAiNodeKind = aiFlow.updateAiNodeKind;
  var buildGeneratedTemplate = aiFlow.buildGeneratedTemplate;
  var createNodeFromAiTemplate = aiFlow.createNodeFromAiTemplate;
  var autoRunNewNode = aiFlow.autoRunNewNode;
  var autoPanToCodeCard = aiFlow.autoPanToCodeCard;
  var generateAiNodeFromPrompt = aiFlow.generateAiNodeFromPrompt;
  var finalizeSingleNode = aiFlow.finalizeSingleNode;

  // A future connector ("What's Next?" pill + its wire) disappears while the
  // AI prompt balloon or the "Coding…" card is open for it — the balloon draws
  // its own wire from the source node, so the pill would just double up.
  function isFutureConnectorClaimed(future) {
    return [aiPromptState, aiCodingState].some(function(state) {
      return !!state && !!state.futureConnector &&
        state.futureConnector.nodeId === future.nodeId &&
        state.futureConnector.anchor === future.anchor;
    });
  }

  // ── Auto-build / planner-orchestration hook ──────────────────────────
  var autoBuild = useAutoBuildWorkflow({
    workflowStateRef: workflowStateRef,
    setWorkflowState: setWorkflowState,
    setCodingNodeIds: setCodingNodeIds,
    setQaConfirmOpen: setQaConfirmOpen,
    setQaMissingFields: setQaMissingFields,
    setAiCodingState: setAiCodingState,
    canvasFooterRef: canvasFooterRef,
    handleFormatLayout: handleFormatLayout,
    findTemplateById: findTemplateById,
    nodeFlowLabel: nodeFlowLabel,
    autoPanToCodeCard: autoPanToCodeCard,
    objectTemplates: objectTemplates,
    onRunWorkflow: onRunWorkflow,
    isRunDisabled: isRunDisabled,
    props: props
  });
  var autoBuildWorkflow = autoBuild.autoBuildWorkflow;
  var handleConfirmStartQa = autoBuild.handleConfirmStartQa;
  // ── Drag & Drop helpers ──────────────────────────────────────────────────

  function findTemplateById(templateId) {
    for (var i = 0; i < objectTemplates.length; i += 1) {
      if (objectTemplates[i].id === templateId) return objectTemplates[i];
    }
    return null;
  }

  function findTemplateByIcon(icon) {
    for (var i = 0; i < objectTemplates.length; i += 1) {
      if (objectTemplates[i].icon === icon) return objectTemplates[i];
    }
    return null;
  }

  function appendUsedVariables(nextNodes, template) {
    if (!template || !Array.isArray(template.usedVariables) || template.usedVariables.length === 0) {
      return nextNodes;
    }

    var existingVarNames = {};
    nextNodes.forEach(function(n) {
      if (n && n.icon === 'variables' && n.title) existingVarNames[n.title] = true;
    });

    var varTemplate = findTemplateByIcon('variables');
    if (!varTemplate) {
      varTemplate = {
        id: 'sys-variables', kind: 'workflow-node', nodeType: 'card', category: 'system',
        icon: 'variables', title: 'Variable', subtitle: 'Store typed context values',
        hyperparameters: [
          { id: 'var-data-type', key: 'dataType', type: 'string', default: 'string' },
          { id: 'var-value', key: 'value', type: 'string', default: '' },
          { id: 'var-retain', key: 'retain', type: 'boolean', default: 'false' }
        ]
      };
    }

    template.usedVariables.forEach(function(varName) {
      if (existingVarNames[varName]) return;
      var varId = 'sys-variables-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);
      var varNode = cloneTemplateNode(varTemplate, varId, { left: 0, top: 0 });
      varNode.title = varName;
      nextNodes = nextNodes.concat(varNode);
      existingVarNames[varName] = true;
    });

    return nextNodes;
  }

  function buildNodesForTemplateDrop(prevNodes, template, newId, dropPosition) {
    return appendUsedVariables((prevNodes || []).concat(cloneTemplateNode(template, newId, dropPosition)), template);
  }

  // ── Canvas drop hook ─────────────────────────────────────────────────────
  var canvasDrop = useCanvasDrop({
    panPosRef: panPosRef,
    zoomRef: zoomRef,
    setWorkflowState: setWorkflowState,
    setDropActive: setDropActive,
    setFutureDropTargetId: setFutureDropTargetId,
    setSelectedNodeIds: setSelectedNodeIds,
    setSelectedConnectionId: setSelectedConnectionId,
    setPendingAgentDrop: setPendingAgentDrop,
    setPendingClassifierDrop: setPendingClassifierDrop,
    dropActive: dropActive,
    workflowId: workflowId,
    findTemplateById: findTemplateById,
    buildNodesForTemplateDrop: buildNodesForTemplateDrop,
    addVariableNode: addVariableNode,
    autoRunNewNode: autoRunNewNode,
    showAlert: showAlert
  });
  // Live (read-only structure) mode: no dropping new nodes onto the canvas.
  var onCanvasDragOver = readOnly ? undefined : canvasDrop.onCanvasDragOver;
  var onCanvasDragLeave = readOnly ? undefined : canvasDrop.onCanvasDragLeave;
  var onCanvasDrop = readOnly ? undefined : canvasDrop.onCanvasDrop;
  var dropTemplateOnFutureConnector = readOnly ? function() {} : canvasDrop.dropTemplateOnFutureConnector;

  // ── Canvas interaction handlers ────────────────────────────────────────────

  function getNodeStyle(node) {
    var pos = node.position || { left: 0, top: 0 };
    var snapped = { left: Math.round(pos.left), top: Math.round(pos.top) };
    // Only resizable (wide-card) nodes honour a saved width. Tile nodes ignore a
    // stale node.size so the wrapper hugs the visible tile — otherwise the wrapper
    // stays at the old width and the edge-anchored connector handles float off to
    // the side (mirrors the same guard in getRect).
    if (isResizableNode(node) && node.size && node.size.width) {
      return Object.assign({}, snapped, { width: node.size.width, maxWidth: 'none' });
    }
    return snapped;
  }

  function getConditionEditorPosition(node) {
    var nodeRect = node ? rectById[node.id] : null;
    if (!nodeRect) {
      return { left: 16, top: 16, side: 'right' };
    }

    var normalizedRule = normalizeConditionRule(node.conditionRule);
    var conditionCount = Math.max(1, normalizedRule.conditions.length);
    var canvasWidth = canvasRef.current ? canvasRef.current.clientWidth : 1200;
    var canvasHeight = canvasRef.current ? canvasRef.current.clientHeight : 700;
    var popoverWidth = 380;
    var popoverHeight = Math.min(560, 280 + (conditionCount * 54));
    var gap = 18;
    var pan = panPosRef.current;
    var zoom = zoomRef.current || 1;
    var minLeft = 10;

    if (typeof document !== 'undefined' && document.querySelector) {
      var varsPanel = document.querySelector('.vars-panel');
      if (varsPanel && typeof varsPanel.getBoundingClientRect === 'function') {
        var varsRect = varsPanel.getBoundingClientRect();
        if (varsRect && Number.isFinite(varsRect.right)) {
          minLeft = Math.max(minLeft, Math.round(varsRect.right) + 12);
        }
      }
    }

    var targetRight = (nodeRect.right * zoom) + pan.x;
    var targetLeft = (nodeRect.left * zoom) + pan.x;
    var targetTop = (nodeRect.top * zoom) + pan.y;
    var targetMidY = targetTop + ((nodeRect.height * zoom) / 2);

    var side = 'left';
    var left = targetRight + gap;
    if (left + popoverWidth > canvasWidth - 10) {
      side = 'right';
      left = targetLeft - popoverWidth - gap;
    }

    left = Math.max(minLeft, Math.min(left, canvasWidth - popoverWidth - 10));
    var top = Math.max(10, Math.min(targetMidY - 78, canvasHeight - popoverHeight - 10));

    if (left + popoverWidth > canvasWidth - 10) {
      left = Math.max(minLeft, canvasWidth - popoverWidth - 10);
    }

    return {
      left: Math.round(left),
      top: Math.round(top),
      side: side
    };
  }

  function blurActiveField() {
    var active = document.activeElement;
    if (!active) return;
    var tag = String(active.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'select' || tag === 'textarea') {
      active.blur();
    }
  }

  function shouldIgnoreCanvasPanStart(target) {
    if (!target || typeof target.closest !== 'function') return false;
    // The output panel (and its fullscreen modal) is portaled, but React
    // bubbles its touch/mouse events up through the canvas tree — so a
    // touch-scroll inside the panel would otherwise start a canvas pan and
    // swallow the scroll. Treat any touch originating inside these scrollable
    // surfaces as "not a pan" so native scrolling works on touchscreens.
    return !!target.closest(
      '.planner-overlay, .node-output-fullscreen-overlay, .node-output-body'
    );
  }

  function onCanvasMouseDown(event) {
    if (event.target.closest('button')) return;
    if (shouldIgnoreCanvasPanStart(event.target)) return;
    setHoveredFlowNodeId(null);
    // A manual pan commits the current view — drop the saved pre-hover view
    // so we don't snap back when the variables panel hover ends.
    pinCurrentHoverView();
    if (!event.shiftKey) {
      setSelectedConnectionId(null);
      setPropPickerState(null);
    }
    // The AI Flow Builder balloon deliberately survives canvas clicks and pans —
    // closing it here would discard whatever prompt the user had typed.
    blurActiveField();
    if (event.button !== 0) return;
    if (!canvasRef.current) return;
    event.preventDefault();

    var isPointerMode = canvasToolRef.current === 'pointer';
    var wantMarquee = isPointerMode
      ? !spaceHeldRef.current
      : event.shiftKey;

    if (wantMarquee) {
      startMarquee(event, collapseAllExpandedGroups);
      return;
    }

    startPanning(event, function() {
      setSelectedNodeIds({});
      setExpandedCardNodeIds({});
      collapseAllExpandedGroups();
    });
  }

  function onCanvasTouchStart(event) {
    if (event.target.closest('button')) return;
    if (shouldIgnoreCanvasPanStart(event.target)) return;
    if (event.touches.length === 2) {
      nodeDrag.cancelTouchDrag();
      event.preventDefault();
      startPinch(event);
      return;
    }
    if (event.touches.length === 1) {
      setSelectedConnectionId(null);
      setPropPickerState(null);
      blurActiveField();
      event.preventDefault();
      startPanningTouch(event, function() {
        setSelectedNodeIds({});
        setExpandedCardNodeIds({});
        collapseAllExpandedGroups();
      });
    }
  }

  // Per-connection branch label for condition node outputs.
  // Each connection independently carries branch = 'yes' | 'no'; toggling one
  // never affects the other connectors. This replaces the old per-node anchor
  // swap that auto-toggled when multiple connectors shared an anchor.
  function setConnectionBranch(connectionId, nextBranch) {
    var normalized = String(nextBranch || '').trim().toLowerCase();
    if (normalized !== 'yes' && normalized !== 'no') return;
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        connections: (prev.connections || []).map(function(conn) {
          if (conn.id !== connectionId) return conn;
          if (isNodeLocked(conn.from)) return conn;
          return Object.assign({}, conn, { branch: normalized });
        })
      });
    });
  }

  var openCustomNodeEditor = nodeEditing.openCustomNodeEditor;
  var deleteSelectedNodes = nodeEditing.deleteSelectedNodes;

  // ── Connection drag useEffect ──────────────────────────────────────────────
  useEffect(function() {
    if (!connectionDrag) return undefined;

    function onMove(event) {
      if (!canvasRef.current) return;
      var rect = canvasRef.current.getBoundingClientRect();
      var z = zoomRef.current;
      var wx = (event.clientX - rect.left - panPosRef.current.x) / z;
      var wy = (event.clientY - rect.top  - panPosRef.current.y) / z;
      setConnectionDrag(function(prev) {
        if (!prev) return prev;
        return Object.assign({}, prev, { x: wx, y: wy });
      });
      var hoveredTarget = findTargetNodeAtPoint({ x: wx, y: wy }, connectionDrag.from);
      connectionTargetIdRef.current = hoveredTarget ? hoveredTarget.id : null;
      setConnectionTargetNodeId(hoveredTarget ? hoveredTarget.id : null);
    }

    function onUp(event) {
      if (!canvasRef.current) {
        setConnectionDrag(null);
        return;
      }

      var rect = canvasRef.current.getBoundingClientRect();
      var z = zoomRef.current;
      var dropPoint = {
        x: (event.clientX - rect.left - panPosRef.current.x) / z,
        y: (event.clientY - rect.top  - panPosRef.current.y) / z
      };
      var sourceId = connectionDrag.from;
      var sourceAnchor = connectionDrag.fromAnchor;
      var startX = Number(connectionDrag.startX);
      var startY = Number(connectionDrag.startY);
      var sourceNode = nodeById[sourceId];
      var isConditionClick = !!(
        sourceNode &&
        isConditionNode(sourceNode) &&
        Number.isFinite(startX) &&
        Number.isFinite(startY) &&
        Math.sqrt(Math.pow(dropPoint.x - startX, 2) + Math.pow(dropPoint.y - startY, 2)) < 10 &&
        connectionDrag.mode !== 'reconnect'
      );

      if (isConditionClick) {
        // No-op: clicking a condition anchor without dragging used to swap
        // which side was YES vs NO. With per-connection branch labels, that
        // swap is no longer needed — toggling is done per connector.
        setConnectionDrag(null);
        connectionTargetIdRef.current = null;
        setConnectionTargetNodeId(null);
        return;
      }

      var targetNode = findTargetNodeAtPoint(dropPoint, sourceId);
      if (!targetNode && connectionTargetIdRef.current && connectionTargetIdRef.current !== sourceId) {
        targetNode = nodeById[connectionTargetIdRef.current] || null;
      }

      var resolvedTargetId = targetNode && groupById[targetNode.id] ? resolveGroupEndpoint(targetNode.id, 'entry') : null;
      var resolvedSourceId = groupById[sourceId] ? resolveGroupEndpoint(sourceId, 'exit') : null;

      if (targetNode) {
        var targetRect = rectById[targetNode.id];
        var storeTargetId = resolvedTargetId || targetNode.id;
        var storeSourceId = resolvedSourceId || sourceId;
        // Router/Classifier per-label outputs wired to the same target node
        // should land on different sides of the target so each line stays
        // visually distinct. For all other source types, fall back to the
        // plain nearest-anchor pick.
        var toAnchor = isClassifierLikeNode(sourceNode)
          ? pickFreeIncomingAnchor(
              targetRect,
              dropPoint,
              getIncomingAnchors(targetNode),
              workflowStateRef.current.connections || [],
              storeSourceId,
              storeTargetId
            )
          : nearestAnchorForPoint(targetRect, dropPoint, getIncomingAnchors(targetNode));

        if (connectionDrag.mode === 'reconnect') {
          var reconnectTempPreview = null;
          setWorkflowState(function(prev) {
            var nextConns = (prev.connections || []).map(function(conn) {
              if (conn.id !== connectionDrag.connectionId) return conn;
              return Object.assign({}, conn, {
                to: storeTargetId,
                toAnchor: toAnchor,
                bendPoints: [],
                propPath: null
              });
            });
            // Re-pointing onto a Set Variable behaves like a fresh wire: switch
            // it to "From input" and pull the new source's output into the var.
            var withSource = autoSetVariableSourceForTarget(prev.nodes || [], storeTargetId, false);
            var auto = computeSetVariableAutofill(withSource, nextConns, storeTargetId, nodeOutputByIdRef.current);
            reconnectTempPreview = auto.tempPreview;
            return Object.assign({}, prev, { connections: nextConns, nodes: auto.nextNodes });
          });
          // Temporary target → ephemeral live value (shown, not saved).
          if (reconnectTempPreview) onSetVariableLivePreview(reconnectTempPreview.varName, reconnectTempPreview.value);
        } else {
          var newConnectionId = 'manual-conn-' + Date.now().toString(36);
          var alreadyExists = (workflowStateRef.current.connections || []).some(function(conn) {
            return (
              conn.from === storeSourceId &&
              conn.to === storeTargetId &&
              conn.fromAnchor === sourceAnchor &&
              conn.toAnchor === toAnchor
            );
          });

          if (!alreadyExists) {
            var connectTempPreview = null;
            setWorkflowState(function(prev) {
              var stillExists = (prev.connections || []).some(function(conn) {
                return (
                  conn.from === storeSourceId &&
                  conn.to === storeTargetId &&
                  conn.fromAnchor === sourceAnchor &&
                  conn.toAnchor === toAnchor
                );
              });
              if (stillExists) return prev;
              var newConn = {
                id: newConnectionId,
                from: storeSourceId,
                to: storeTargetId,
                fromAnchor: sourceAnchor,
                toAnchor: toAnchor
              };
              // Seed the per-connection branch label so future toggles can flip
              // this connector independently. Default is inferred from which
              // anchor it left from (the visual yes/no hint on the source node).
              if (sourceNode && isConditionNode(sourceNode)) {
                var sourceAxes = getConditionAxes(sourceNode.id, conditionAxesMap);
                newConn.branch = (sourceAnchor === sourceAxes.yes) ? 'yes' : 'no';
              }
              var withSource = autoSetVariableSourceForTarget(prev.nodes || [], storeTargetId, false);
              var nextConns = (prev.connections || []).concat(newConn);
              // Instantly push the source's existing output into the target
              // variable so the canvas shows real data without a run.
              var auto = computeSetVariableAutofill(withSource, nextConns, storeTargetId, nodeOutputByIdRef.current);
              connectTempPreview = auto.tempPreview;
              return Object.assign({}, prev, {
                nodes: auto.nextNodes,
                connections: nextConns
              });
            });
            // Temporary target → ephemeral live value (shown, not saved).
            if (connectTempPreview) onSetVariableLivePreview(connectTempPreview.varName, connectTempPreview.value);

            var sourceOutput = nodeOutputByIdRef.current[storeSourceId];
            if (
              sourceOutput !== null &&
              sourceOutput !== undefined &&
              typeof sourceOutput === 'object' &&
              (Array.isArray(sourceOutput) ? sourceOutput.length > 0 : Object.keys(sourceOutput).length > 0)
            ) {
              setPropPickerState({
                connectionId: newConnectionId,
                output: sourceOutput,
                sourceId: storeSourceId,
                targetNodeId: storeTargetId
              });
            }
          }
        }
      } else if (connectionDrag.mode !== 'reconnect') {
        var sourceOutputForAi = nodeOutputByIdRef.current[sourceId];
        if (sourceOutputForAi === null || sourceOutputForAi === undefined) {
          // No run output yet → the AI builder can't see this node's output
          // shape. Explain via a popup (with a Run action) instead of silently
          // snapping the connection back.
          var srcForPrompt = nodeById[sourceId];
          setBranchNeedsRunTitle((srcForPrompt && srcForPrompt.title) ? srcForPrompt.title : 'this node');
          setConnectionDrag(null);
          connectionTargetIdRef.current = null;
          setConnectionTargetNodeId(null);
          return;
        }

        var sourceRect = rectById[sourceId];
        var sourceNode = nodeById[sourceId];
        var fromPoint = sourceRect ? anchorPoint(sourceRect, sourceAnchor, sourceNode, !isNodeDetailsExpanded(sourceId)) : dropPoint;
        var futureFromDrag = {
          id: sourceId + '-future-drag-' + Date.now().toString(36),
          nodeId: sourceId,
          nodeTitle: (sourceNode && sourceNode.title) ? sourceNode.title : 'Node',
          anchor: sourceAnchor,
          from: fromPoint,
          to: dropPoint,
          buttonLeft: Math.round(dropPoint.x),
          buttonTop: Math.round(dropPoint.y - (FUTURE_CONNECTOR_BUTTON_HEIGHT / 2)),
          pathD: smoothPath(fromPoint, dropPoint, sourceAnchor, sourceAnchor)
        };
        handleGenerateFlowWithAi(futureFromDrag, event);
      }

      setConnectionDrag(null);
      connectionTargetIdRef.current = null;
      setConnectionTargetNodeId(null);
    }

    function onTouchMove(event) {
      if (!event.touches.length) return;
      event.preventDefault();
      var t = event.touches[0];
      onMove({ clientX: t.clientX, clientY: t.clientY });
    }

    function onTouchUp(event) {
      var t = event.changedTouches && event.changedTouches.length ? event.changedTouches[0] : { clientX: 0, clientY: 0 };
      onUp({ clientX: t.clientX, clientY: t.clientY });
    }

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchUp);
    return function() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchUp);
    };
  }, [connectionDrag, nodes, rectById, nodeById, setWorkflowState]);

  // ── Derived editor state ───────────────────────────────────────────────────

  var editingAgentNode = editingAgentNodeId ? nodeById[editingAgentNodeId] : null;
  var agentEditorPosition = getConditionEditorPosition(editingAgentNode);

  var editingClassifierNode = editingClassifierNodeId ? nodeById[editingClassifierNodeId] : null;
  var classifierEditorPosition = getConditionEditorPosition(editingClassifierNode);

  var propPickerPosition = null;
  if (propPickerState && propPickerState.sourceId && propPickerState.targetNodeId) {
    var ppSourceRect = rectById[propPickerState.sourceId];
    var ppTargetRect = rectById[propPickerState.targetNodeId];
    if (ppSourceRect && ppTargetRect) {
      var ppan = panPosRef.current;
      var pickerWidth = 240;
      var midX = (ppSourceRect.cx + ppTargetRect.cx) / 2 + ppan.x;
      var midY = (ppSourceRect.cy + ppTargetRect.cy) / 2 + ppan.y;
      var pLeft = Math.round(midX - pickerWidth / 2);
      var pTop = Math.round(midY - 20);
      var pCanvasWidth = canvasRef.current ? canvasRef.current.clientWidth : 1200;
      var pCanvasHeight = canvasRef.current ? canvasRef.current.clientHeight : 700;
      pLeft = Math.max(10, Math.min(pLeft, pCanvasWidth - pickerWidth - 10));
      pTop = Math.max(10, Math.min(pTop, pCanvasHeight - 120));
      propPickerPosition = { left: pLeft, top: pTop };
    }
  }

  // Direct hook references — no wrappers needed (hooks use refs for shared state)
  // In read-only (Live) mode, dragging/resizing must not move the published
  // snapshot. Selection (clicking to view) still works; only the move/resize
  // starts are neutered, at this single chokepoint.
  var noop = function() {};
  var startDragging = readOnly ? noop : nodeDrag.startDragging;
  var startDraggingTouch = readOnly ? noop : nodeDrag.startDraggingTouch;
  var startGroupDrag = readOnly ? noop : nodeDrag.startGroupDrag;
  // startTouchDrag stays live in read-only: it carries touch tap-select and
  // double-tap (params panel). The hook's isReadOnly flag keeps the
  // long-press move from ever arming there.
  var startTouchDrag = nodeDrag.startTouchDrag;
  var startNodeResize = readOnly ? noop : nodeDrag.startNodeResize;
  var startNodeResizeTouch = readOnly ? noop : nodeDrag.startNodeResizeTouch;
  var draggingId = nodeDrag.draggingId;
  var resizingId = nodeDrag.resizingId;

  return h(
    'div',
    {
      ref: canvasRef,
      className: 'workflow-canvas' +
        (dropActive ? ' workflow-canvas-drop-active' : '') +
        (plannerOpen ? ' workflow-canvas-modal-open' : '') +
        (canvasTool === 'pointer' ? ' workflow-canvas-pointer' : ''),
      onMouseDown: onCanvasMouseDown,
      onTouchStart: onCanvasTouchStart,
      onDragOver: onCanvasDragOver,
      onDragLeave: onCanvasDragLeave,
      onDrop: onCanvasDrop
    },
    isLoading || isRendering
      ? h('div', { className: 'wf-canvas-loading' },
          h('div', { className: 'wf-canvas-loading-spinner' }),
          h('div', { className: 'wf-canvas-loading-label' }, isLoading ? 'Opening flow…' : 'Rendering…')
        )
      : null,
    // Remote Support: other session members' live cursors. Overlay on the
    // canvas root (self-positions from the pan/zoom refs each frame).
    h(RemoteCursorsLayer, {
      members: props.collabRoster,
      selfSessionId: props.collabSelfSessionId,
      cursorsRef: props.collabCursorsRef,
      panPosRef: panPosRef,
      zoomRef: zoomRef
    }),
    h(
      'div',
      { ref: worldRef, className: 'canvas-world' },
    h('div', { ref: zoomLayerRef, className: 'canvas-zoom-layer' },
    h(
      'svg',
      { className: 'connections-svg' },
      h('defs', null,
        h('marker', {
          id: 'conn-end-dot',
          viewBox: '0 0 10 10',
          refX: '9',
          refY: '5',
          markerWidth: '10',
          markerHeight: '10',
          orient: 'auto',
          markerUnits: 'userSpaceOnUse'
        },
          h('path', { d: 'M1,1.5 Q5,5 1,8.5 L10,5 Z', fill: 'context-stroke' })
        ),
        h('marker', {
          id: 'conn-dot',
          viewBox: '0 0 8 8',
          refX: '4',
          refY: '4',
          markerWidth: '8',
          markerHeight: '8',
          orient: 'auto',
          markerUnits: 'userSpaceOnUse'
        },
          h('circle', { cx: '4', cy: '4', r: '3', fill: 'context-stroke' })
        ),
        renderStandbyFlowGradient(h)
      ),
      h('filter', { id: 'group-blob-filter', x: '-20%', y: '-20%', width: '140%', height: '140%', colorInterpolationFilters: 'sRGB' },
        h('feGaussianBlur', { in: 'SourceGraphic', stdDeviation: '18', result: 'blur' }),
        h('feColorMatrix', { in: 'blur', type: 'matrix', values: '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -7', result: 'blob' })
      ),
      // Render hull backgrounds for expanded groups
      collapsedGroups.filter(function(g) { return g.expanded; }).map(function(g) {
        var groupColor = normalizeGroupColor(g.color);
        var groupNodeIds = {};
        for (var gi = 0; gi < g.nodeIds.length; gi++) groupNodeIds[g.nodeIds[gi]] = true;
        var nodeRects = [];
        for (var ni = 0; ni < g.nodeIds.length; ni++) {
          var r = rectById[g.nodeIds[ni]];
          if (r) nodeRects.push(r);
        }
        if (!nodeRects.length) return null;
        var isAnimating = (collapsingAnim && g.nodeIds.some(function(id) { return collapsingAnim.nodeIds.indexOf(id) !== -1; })) ||
                          (expandingAnim && g.nodeIds.some(function(id) { return expandingAnim.nodeIds.indexOf(id) !== -1; }));
        if (isAnimating) return null;
        var pad = 28;
        var centers = nodeRects.map(function(rc) {
          return { x: (rc.left + rc.right) / 2, y: (rc.top + rc.bottom) / 2 };
        });
        var visited = [0];
        var mstEdges = [];
        while (visited.length < centers.length) {
          var bestDist = Infinity, bestFrom = -1, bestTo = -1;
          for (var vi = 0; vi < visited.length; vi++) {
            var fc = centers[visited[vi]];
            for (var ci = 0; ci < centers.length; ci++) {
              if (visited.indexOf(ci) !== -1) continue;
              var tc = centers[ci];
              var d = Math.sqrt((tc.x - fc.x) * (tc.x - fc.x) + (tc.y - fc.y) * (tc.y - fc.y));
              if (d < bestDist) { bestDist = d; bestFrom = visited[vi]; bestTo = ci; }
            }
          }
          if (bestTo !== -1) { mstEdges.push([bestFrom, bestTo]); visited.push(bestTo); }
          else break;
        }
        var connThickness = 24;
        return h('g', { key: 'group-hull-' + g.id, filter: 'url(#group-blob-filter)', opacity: '0.07' },
          nodeRects.map(function(rc, idx) {
            return h('rect', {
              key: 'n' + idx,
              x: rc.left - pad, y: rc.top - pad,
              width: rc.right - rc.left + pad * 2,
              height: rc.bottom - rc.top + pad * 2,
              rx: 20, ry: 20,
              fill: groupColor
            });
          }).concat(mstEdges.map(function(edge, idx) {
            var f = centers[edge[0]], t = centers[edge[1]];
            var dx = t.x - f.x, dy = t.y - f.y;
            var len = Math.sqrt(dx * dx + dy * dy);
            var angle = Math.atan2(dy, dx) * 180 / Math.PI;
            return h('rect', {
              key: 'e' + idx,
              x: f.x, y: f.y - connThickness / 2,
              width: len, height: connThickness,
              rx: connThickness / 2, ry: connThickness / 2,
              fill: groupColor,
              transform: 'rotate(' + angle + ',' + f.x + ',' + f.y + ')'
            });
          }))
        );
      }),
      resolvedConnections.map(function(connection) {
        var animatingIds = (collapsingAnim ? collapsingAnim.nodeIds : []).concat(expandingAnim ? expandingAnim.nodeIds : []);
        if (animatingIds.length && (animatingIds.indexOf(connection.fromId) !== -1 || animatingIds.indexOf(connection.toId) !== -1)) return null;
        var pathD = connection.pathD || smoothPath(connection.from, connection.to, connection.fromAnchor, connection.toAnchor);
        var connectionHoverText = formatOutputHoverText(nodeOutputById[connection.fromId], connection.propPath);
        var handleConnectionDoubleClick = function(event) {
          event.preventDefault();
          event.stopPropagation();
          if (!canvasRef.current) return;
          var rect = canvasRef.current.getBoundingClientRect();
          var z = zoomRef.current;
          toggleConnectionBendAtPoint(connection.id, {
            x: (event.clientX - rect.left - panPosRef.current.x) / z,
            y: (event.clientY - rect.top - panPosRef.current.y) / z
          });
          setSelectedConnectionId(connection.id);
        };
        return h(React.Fragment, { key: connection.id },
          h('path', {
            d: pathD,
            className: 'conn-line-hitbox',
            onDoubleClick: handleConnectionDoubleClick,
            onMouseDown: function(event) {
              event.stopPropagation();
              selectConnectionAtEvent(connection.id, event);
            },
            onTouchEnd: function(event) {
              event.stopPropagation();
              selectConnectionAtEvent(connection.id, event);
            },
            onMouseEnter: function() { setHoveredConnectionId(connection.id); },
            onMouseLeave: function() {
              setHoveredConnectionId(function(prev) { return prev === connection.id ? null : prev; });
            },
            title: connectionHoverText
          }),
          h('path', {
            d: pathD,
            className: connection.className
              + (selectedConnectionId === connection.id ? ' conn-line-selected' : '')
              + (hasFlowHighlight && downstreamFlowHighlight.connIds[connection.id] ? ' conn-flow-highlight' : '')
              + (hasFlowHighlight && !downstreamFlowHighlight.connIds[connection.id] ? ' conn-flow-dim' : '')
              + (focusActive && !(focusedNodeIdSet[connection.fromId] && focusedNodeIdSet[connection.toId]) ? ' conn-var-dim' : ''),
            markerEnd: 'url(#conn-end-dot)',
            markerStart: 'url(#conn-dot)',
            title: connectionHoverText,
            onMouseDown: function(event) {
              event.stopPropagation();
              selectConnectionAtEvent(connection.id, event);
            },
            onTouchEnd: function(event) {
              event.stopPropagation();
              selectConnectionAtEvent(connection.id, event);
            },
            onDoubleClick: handleConnectionDoubleClick
          })
        );
      }),
      // Standby flow cue: a colored glow dot travels each connection
      // source->target while idle, hinting flow direction. Hidden the moment a
      // run starts (so it never competes with the gold "flowing" animation) and
      // while a node/variable is focused (the dim + marching-ants highlight
      // already conveys the focused path, so ambient dots would just be noise).
      !isExecutionRunning && !hasFlowHighlight && !focusActive
        ? renderStandbyFlowDots(h, resolvedConnections)
        : null,
      connectionDrag && rectById[connectionDrag.from]
        ? h('path', {
            key: 'connection-preview',
            d: smoothPath(
              anchorPoint(rectById[connectionDrag.from], connectionDrag.fromAnchor, nodeById[connectionDrag.from], !isNodeDetailsExpanded(connectionDrag.from)),
              { x: connectionDrag.x, y: connectionDrag.y },
              connectionDrag.fromAnchor
            ),
            className: getConnectionPreviewClassName(nodeById[connectionDrag.from], connectionDrag.fromAnchor, conditionAxesMap),
            markerEnd: 'url(#conn-end-dot)'
          })
        : null
      ,
      (readOnly ? [] : futureConnectors).filter(function(future) {
        return !isFutureConnectorClaimed(future);
      }).map(function(future) {
        return h('path', {
          key: future.id,
          d: future.pathD,
          className: 'conn-line conn-line-future',
          markerEnd: 'url(#conn-end-dot)'
        });
      }),
      // Dashed wire from a Tool Agent's bottom anchor to its "+ add tool" button.
      (readOnly ? [] : toolConnectorButtons).map(function(btn) {
        return h('path', {
          key: 'tool-wire-' + btn.id,
          d: btn.pathD,
          className: 'conn-line conn-line-tool',
          markerEnd: 'url(#conn-end-dot)'
        });
      }),
      aiCodingState && rectById[aiCodingState.futureConnector.nodeId]
        ? h('path', {
            key: 'coding-connector',
            d: smoothPath(
              anchorPoint(rectById[aiCodingState.futureConnector.nodeId], aiCodingState.futureConnector.anchor, nodeById[aiCodingState.futureConnector.nodeId], !isNodeDetailsExpanded(aiCodingState.futureConnector.nodeId)),
              { x: aiCodingState.position.left, y: aiCodingState.position.top + 70 },
              aiCodingState.futureConnector.anchor,
              'left'
            ),
            className: 'conn-line conn-line-custom',
            markerEnd: 'url(#conn-end-dot)',
            markerStart: 'url(#conn-dot)'
          })
        : null
    ),

    h(
      'div',
      { className: 'nodes-layer' },
      nodes.filter(function(node) { return !isVariablesNode(node) && !nodeIdToGroupId[node.id]; }).map(function(node) {
        var nodeStatus = nodeStatusById[node.id] || null;
        var nodeFixState = nodeFixById[node.id] || null;
        var silentCheckEntry = (executionState && executionState.silentCheckingByNodeId && executionState.silentCheckingByNodeId[node.id]) || null;
        var isSilentChecking = !!silentCheckEntry;
        var silentCheckLiveText = formatSilentCheckStream(silentCheckEntry && silentCheckEntry.text);
        var silentCheckSkippedEntry = (executionState && executionState.silentCheckSkippedByNodeId && executionState.silentCheckSkippedByNodeId[node.id]) || null;
        var silentCheckSkippedLabel = silentCheckSkippedEntry
          ? (String(silentCheckSkippedEntry.reason || '').indexOf('set_variable_autoselect_fixed:') === 0
              ? 'Edge auto-fixed — now sends "' + String(silentCheckSkippedEntry.reason).slice('set_variable_autoselect_fixed:'.length) + '"'
              : String(silentCheckSkippedEntry.reason || '').indexOf('set_variable_autoselect:') === 0
              ? 'Stored "' + String(silentCheckSkippedEntry.reason).slice('set_variable_autoselect:'.length) + '" field from incoming object'
              : (silentCheckSkippedEntry.reason === 'payload_too_large'
                  ? 'Checking skipped — data too long'
                  : (silentCheckSkippedEntry.reason === 'no_input_or_output'
                      ? 'Checking skipped — nothing to audit'
                      : (silentCheckSkippedEntry.reason === 'check_timeout'
                          ? 'Check timed out — moved on'
                          : (silentCheckSkippedEntry.reason === 'check_failed'
                              ? 'Check failed — ' + (silentCheckSkippedEntry.detail || 'auditor error')
                              : 'Checking skipped')))))
          : '';
        var nodeLocked = isNodeLocked(node.id);
        var timing = nodeTimingById[node.id] || null;
        var nodeTokens = nodeTokensById[node.id] || null;
        var badgeLabel = null;
        var nodeCallCount = nodeCallCountById[node.id] || 0;
        if (timing && nodeStatus === 'running' && Number.isFinite(Number(timing.startedAt))) {
          badgeLabel = formatDurationMs(timerNowMs - Number(timing.startedAt));
        } else if (timing && nodeStatus === 'completed' && Number.isFinite(Number(timing.durationMs))) {
          badgeLabel = formatDurationMs(timing.durationMs);
        }

        var content = node.kind === 'badge'
          ? h(ActionBadge, { label: node.label, color: node.color })
          : h(WorkflowNode, {
              type: node.nodeType || 'card',
              hasExplicitWidth: !!(node.size && node.size.width),
              title: node.title,
              displayTitle: node.icon === 'set-variable' ? compactSetVariableTitle(node) : node.icon === 'condition' ? compactConditionTitle(node) : '',
              subtitle: node.subtitle,
              showDetails: showNodeDetails,
              detailPreviewActive: !showNodeDetails && hoveredPreviewNodeId === node.id,
              icon: node.icon,
              category: node.category,
              isToolNode: !!toolNodeIds[node.id],
              logoUrl: node.logoUrl,
              onLogoDoubleClick: (!readOnly && props.onLogoDoubleClick) ? function() { props.onLogoDoubleClick(node.id); } : undefined,
              options: node.options,
              stats: node.stats,
              borderColor: node.borderColor,
              hyperparameters: node.hyperparameters,
              paramValues: node.paramValues || {},
              conditionRule: node.conditionRule || null,
              runtimeOutput: node.icon === 'variables' && Object.prototype.hasOwnProperty.call(variableValuesByName, String(node.title || '').trim()) ? variableValuesByName[String(node.title || '').trim()] : nodeOutputById[node.id],
              variableNodes: getEditorContextNodes(node.id),
              onConditionValueChange: function(conditionId, newValue) {
                if (nodeLocked || readOnly) return;
                setWorkflowState(function(prev) {
                  return Object.assign({}, prev, {
                    nodes: (prev.nodes || []).map(function(n) {
                      if (n.id !== node.id) return n;
                      var rule = normalizeConditionRule(n.conditionRule);
                      var updatedConditions = rule.conditions.map(function(c) {
                        if (c.id !== conditionId) return c;
                        return Object.assign({}, c, { value: newValue });
                      });
                      return Object.assign({}, n, {
                        conditionRule: Object.assign({}, rule, { conditions: updatedConditions })
                      });
                    })
                  });
                });
              },
              onParamChange: function(key, value) { handleParamChange(node.id, key, value); },
              onTitleChange: nodeLocked ? function() {} : function(value) { handleNodeTitleChange(node.id, value); },
              isDragging: draggingId === node.id,
              nodeStatus: nodeStatus,
              nodeCallCount: nodeCallCount,
              isWorkflowRunning: isExecutionRunning,
              eyeLookDir: (function() {
                if (!nodeStatus || (nodeStatus !== 'running' && nodeStatus !== 'completed')) return null;
                var myRect = rectById[node.id];
                if (!myRect) return null;
                var targetId = null;
                if (nodeStatus === 'running') {
                  var inConn = connections.find(function(c) { return c && String(c.to) === String(node.id); });
                  if (inConn) targetId = String(inConn.from);
                } else {
                  var outConn = connections.find(function(c) { return c && String(c.from) === String(node.id); });
                  if (outConn) targetId = String(outConn.to);
                }
                if (!targetId) return null;
                var targetRect = rectById[targetId];
                if (!targetRect) return null;
                var dx = targetRect.cx - myRect.cx;
                var dy = targetRect.cy - myRect.cy;
                var dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < 1) return null;
                return { x: dx / dist, y: dy / dist };
              })(),
              category: node.category,
              isExpanded: !!expandedCardNodeIds[node.id],
              onNodeHover: function() {
                if (!expandedCardNodeIds[node.id]) {
                  setExpandedCardNodeIds(function(prev) { var next = Object.assign({}, prev); next[node.id] = true; return next; });
                }
              },
              readOnly: nodeLocked || readOnly,
              // Live mode (canvas readOnly) hides the node ⋮ menu too — no
              // structural edits there, only variable values via the sidebar.
              onMenuClick: (nodeLocked || readOnly) ? null : (isConditionNode(node)
                // Condition's editor lives in the right sidebar now — no menu
                // button needed; clicking the node selects it and the sidebar
                // opens automatically.
                ? null
                : isAgentNode(node)
                ? openAgentEditor.bind(null, node.id)
                : isClassifierNode(node)
                ? openClassifierEditor.bind(null, node.id)
                : isRouterNode(node)
                ? openClassifierEditor.bind(null, node.id)
                // Start's parameter editor also lives in the right sidebar now —
                // no menu button; selecting the node opens it automatically.
                : (node.icon === 'start')
                ? null
                : (isCustomNode(node) ? openCustomNodeEditor.bind(null, node.id) : null))
            });

        var derivedNodeOutput = node.icon === 'start' ? getStartNodeOutput(node) : undefined;
        var runtimeNodeOutput = Object.prototype.hasOwnProperty.call(nodeOutputById, node.id)
          ? nodeOutputById[node.id]
          : derivedNodeOutput;

        // Remote Support: another session member has this node selected —
        // ring it in their cursor color.
        var remoteSelection = props.collabSelections ? props.collabSelections[node.id] : null;

        return h(
          'div',
          {
            key: node.id,
            title: remoteSelection ? 'Selected by ' + (remoteSelection.username || 'support') : undefined,
            className:
              nodeClass(node.id) +
              (remoteSelection ? ' node-remote-selected' : '') +
              (nodeLocked ? ' node-locked-dependent' : '') +
              (selectedNodeIds[node.id] ? ' node-selected' : '') +
              (selectedNodeIds[node.id] && Object.keys(selectedNodeIds).length > 1 ? ' node-multi-selected' : '') +
              (connectionTargetNodeId === node.id ? ' node-connect-target' : '') +
              (resizingId === node.id ? ' node-resizing' : '') +
              (nodeFixState && nodeFixState.active ? ' node-fixing node-exec-failed' : '') +
              (nodeStatus === 'running' && !(nodeFixState && nodeFixState.active) ? ' node-exec-running' : '') +
              (nodeStatus === 'completed' ? ' node-exec-completed' : '') +
              (nodeStatus === 'failed' ? ' node-exec-failed' : '') +
              (executionState && executionState.status === 'running' && !nodeStatus && !isVariablesNode(node) ? ' node-exec-pending' : '') +
              (collapsingAnim && collapsingAnim.nodeIds.indexOf(node.id) !== -1 ? ' node-collapsing' : '') +
              (expandingAnim && expandingAnim.nodeIds.indexOf(node.id) !== -1 ? ' node-expanding' : '') +
              (repellingNodeIds && repellingNodeIds.indexOf(node.id) !== -1 ? ' node-repelling' : '') +
              (hasFlowHighlight && hoveredFlowNodeId === node.id ? ' node-flow-source' : '') +
              (hasFlowHighlight && downstreamFlowHighlight.nodeIds[node.id] ? ' node-flow-highlight' : '') +
              (hasFlowHighlight && hoveredFlowNodeId !== node.id && !downstreamFlowHighlight.nodeIds[node.id] ? ' node-flow-dim' : '') +
              (focusActive && focusedNodeIdSet[node.id] ? ' node-flow-highlight node-var-using' : '') +
              (focusActive && !focusedNodeIdSet[node.id] ? ' node-var-dim' : '') +
              (!isNodeDetailsExpanded(node.id) ? ' node-compact' : ''),
            style: (function() {
              var baseStyle = getNodeStyle(node);
              if (collapsingAnim && collapsingAnim.nodeIds.indexOf(node.id) !== -1) {
                var nodeRect = getRect(node);
                var nodeCx = nodeRect.left + nodeRect.width / 2;
                var nodeCy = nodeRect.top + nodeRect.height / 2;
                var tx = collapsingAnim.centerX - nodeCx;
                var ty = collapsingAnim.centerY - nodeCy;
                return Object.assign({}, baseStyle, {
                  transform: 'translate(' + tx + 'px, ' + ty + 'px) scale(0)',
                  opacity: 0,
                  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s ease',
                  pointerEvents: 'none',
                  zIndex: 100
                });
              }
              if (expandingAnim && expandingAnim.nodeIds.indexOf(node.id) !== -1) {
                var eRect = getRect(node);
                var eCx = eRect.left + eRect.width / 2;
                var eCy = eRect.top + eRect.height / 2;
                var eTx = expandingAnim.fromX - eCx;
                var eTy = expandingAnim.fromY - eCy;
                return Object.assign({}, baseStyle, {
                  '--expand-from-tx': eTx + 'px',
                  '--expand-from-ty': eTy + 'px',
                  animation: 'nodeExpandOut 0.45s cubic-bezier(0.22, 1, 0.36, 1) both'
                });
              }
              if (remoteSelection) {
                return Object.assign({}, baseStyle, { '--remote-sel-color': remoteSelection.color || '#7c5cff' });
              }
              return baseStyle;
            })(),
            onMouseDown: function(event) {
              event.stopPropagation();
              // Clicking a node while a variable hover is active commits the
              // current pan/zoom — don't snap the canvas back when the user
              // eventually leaves the variables panel.
              pinCurrentHoverView();
              if (!readOnly) {
                var expandedGroups = (workflowStateRef.current.groups || []).filter(function(g) { return g.expanded; });
                for (var eg = 0; eg < expandedGroups.length; eg++) {
                  if (expandedGroups[eg].nodeIds.indexOf(node.id) === -1) {
                    toggleExpandGroup(expandedGroups[eg].id);
                  }
                }
              }
              if (event.shiftKey) {
                setSelectedNodeIds(function(prev) {
                  var next = Object.assign({}, prev);
                  if (next[node.id]) { delete next[node.id]; } else { next[node.id] = true; }
                  return next;
                });
              } else if (!selectedNodeIds[node.id]) {
                var clickSel = {}; clickSel[node.id] = true;
                setSelectedNodeIds(clickSel);
              }
              setLastSelectedNodeId(node.id);
              setSelectedConnectionId(null);
              startDragging(node.id, event);
            },
            onTouchStart: function(event) {
              startTouchDrag(node.id, event);
            },
            onMouseEnter: function() {
              if (!showNodeDetails) setHoveredPreviewNodeId(node.id);
              setHoveredFlowNodeId(node.id);
            },
            onMouseLeave: function() {
              setHoveredPreviewNodeId(function(current) {
                return current === node.id ? null : current;
              });
              setHoveredFlowNodeId(function(current) {
                return current === node.id ? null : current;
              });
            },
            ref: setNodeRef(node.id)
          },
          h('div', { className: 'node-exec-badges-row' },
            // Suppress the execution duration badge when a variable-usage
            // chip is rendered for this node — they share the top-right slot
            // and otherwise visually collide with the READ / SET pill.
            (hoveredVariableName && hoveredVariableUsageByNodeId[node.id])
              ? null
              : h(ExecutionBadge, {
                  status: nodeFixState && nodeFixState.active ? null : (nodeStatus === 'running' ? 'running' : (nodeStatus === 'completed' ? 'completed' : null)),
                  label: nodeFixState && nodeFixState.active ? null : badgeLabel
                }),
            // Agent-node token spend (↓ in / ↑ out). Only shows once the node
            // has finished and reported a non-zero tally; hidden mid-fix so it
            // doesn't crowd the fix indicator.
            (nodeStatus === 'completed' && !(nodeFixState && nodeFixState.active))
              ? h(NodeTokenBadge, { tokens: nodeTokens })
              : null,
            // Yellow warning chip: only when the autopilot/silent-error checker
            // is enabled AND this node finished AND its output sentiment looks
            // like an error/unsuccessful response (e.g. { error: true, message: ... }).
            // Gated by checkSilentError so users who turned the checker off
            // don't see surprise warnings on outputs they accept as-is.
            (!readOnly && workflowState && workflowState.checkSilentError
              && nodeStatus === 'completed'
              && !(nodeFixState && nodeFixState.active)
              && looksLikeErrorOutput(nodeOutputById[node.id]))
              ? h(NodeWarningBadge, { label: 'Error?' })
              : null,
            nodeCallCount > 1
              ? h('div', {
                  className: 'node-call-count-chip' + (nodeStatus === 'running' ? ' node-call-count-running' : ''),
                  title: 'Called ' + nodeCallCount + ' times'
                },
                '\u00d7' + nodeCallCount
              )
              : null
          ),
          (function() {
            if (isEmbed || isExecutionRunning || !nodeStatus || !onRunNodeIsolated || !isCustomNode(node)) return null;
            if (connections.filter(function(c) { return c && String(c.to) === String(node.id); }).length > 1) return null;
            var outConn = connections.find(function(c) { return c && String(c.from) === String(node.id); });
            var outAnchor = outConn ? (outConn.fromAnchor || 'bottom') : 'right';
            if (outConn && rectById[outConn.from] && rectById[outConn.to]) {
              outAnchor = normalizeSourceAnchorForNode(nodeById[outConn.from], outAnchor, rectById[outConn.from], rectById[outConn.to], conditionAxesMap);
            }
            var side = outAnchor === 'left' ? 'left' : outAnchor === 'right' ? 'right' : outAnchor === 'top' ? 'top' : 'bottom';
            return h('button', {
              className: 'node-run-isolated-chip node-run-isolated-' + side,
              title: 'Run from this node (fresh, no prior state)',
              onMouseDown: function(e) {
                e.preventDefault();
                e.stopPropagation();
                onRunNodeIsolated(node.id);
              }
            },
              h('svg', { width: 10, height: 10, viewBox: '0 0 24 24', fill: 'currentColor' },
                h('polygon', { points: '6 3 20 12 6 21 6 3' })
              )
            );
          })(),
          // Variable-hover chip — shows whether this node READS, SETS, or
          // does both for the variable currently hovered in the side panel.
          (function() {
            if (!hoveredVariableName) return null;
            var usage = hoveredVariableUsageByNodeId[node.id];
            if (!usage) return null;
            var label = usage === 'set' ? 'SET' : usage === 'both' ? 'READ + SET' : 'READ';
            return h('div', {
              className: 'node-var-usage-chip node-var-usage-chip-' + usage,
              title: label + ' "' + hoveredVariableName + '"'
            }, label);
          })(),
          nodeFixState && nodeFixState.succeeded
            ? h('div', {
                className: 'node-fix-succeeded-chip',
                title: 'Fix succeeded!'
              },
                h('span', { className: 'node-fix-burst', 'aria-hidden': 'true' },
                  h('span', { className: 'node-fix-ray node-fix-ray-1' }),
                  h('span', { className: 'node-fix-ray node-fix-ray-2' }),
                  h('span', { className: 'node-fix-ray node-fix-ray-3' }),
                  h('span', { className: 'node-fix-ray node-fix-ray-4' }),
                  h('span', { className: 'node-fix-ray node-fix-ray-5' }),
                  h('span', { className: 'node-fix-ray node-fix-ray-6' }),
                  h('span', { className: 'node-fix-dot node-fix-dot-1' }),
                  h('span', { className: 'node-fix-dot node-fix-dot-2' }),
                  h('span', { className: 'node-fix-dot node-fix-dot-3' }),
                  h('span', { className: 'node-fix-dot node-fix-dot-4' })
                ),
                h('span', { className: 'node-fix-success-icon', 'aria-hidden': 'true' },
                  h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 3, strokeLinecap: 'round', strokeLinejoin: 'round' },
                    h('polyline', { points: '20 6 9 17 4 12' })
                  )
                ),
                h('span', { className: 'node-fix-succeeded-label' }, 'Fixed!')
              )
            : nodeFixState && nodeFixState.active
            ? h('div', {
                className: 'node-fix-indicator',
                title: nodeFixState.phase === 'node.fix.retry'
                  ? ((nodeFixState.message || 'Retrying with fixed code.') + ' (attempt ' + (nodeFixState.attempt || 1) + ')')
                  : (nodeFixState.phase === 'node.fix.applied'
                    ? ('Fix applied. Running node again (attempt ' + (nodeFixState.attempt || 1) + ')')
                    : ('Auto-fixing code (attempt ' + (nodeFixState.attempt || 1) + ')'))
              },
                h('span', { className: 'node-fix-robot', 'aria-hidden': 'true' },
                  h('span', { className: 'node-fix-eye node-fix-eye-left' }),
                  h('span', { className: 'node-fix-eye node-fix-eye-right' })
                ),
                h('span', { className: 'node-fix-label' },
                  nodeFixState.phase === 'node.fix.retry'
                    ? 'Retrying'
                    : (nodeFixState.phase === 'node.fix.applied'
                      ? 'Fix applied'
                      : (nodeFixState.message || 'Fixing...'))
                )
              )
            : isSilentChecking
            ? h('div', {
                className: 'node-silent-check-indicator',
                title: silentCheckLiveText || 'Analyzing output for silent errors'
              },
                h('span', { className: 'node-fix-robot', 'aria-hidden': 'true' },
                  h('span', { className: 'node-fix-eye node-fix-eye-left' }),
                  h('span', { className: 'node-fix-eye node-fix-eye-right' })
                ),
                h('span', { className: 'node-fix-label' }, silentCheckLiveText || 'Checking...')
              )
            : silentCheckSkippedEntry
            ? h('div', {
                className: 'node-silent-check-indicator node-silent-check-indicator-skipped',
                title: silentCheckSkippedLabel
              },
                h('span', { className: 'node-silent-check-skip-icon', 'aria-hidden': 'true' }, '⊘'),
                h('span', { className: 'node-fix-label' }, silentCheckSkippedLabel)
              )
            : null,
          codingNodeIds[node.id]
            ? h('div', { className: 'node-coding-card-inline' },
                h('div', { className: 'ai-coding-node-header' },
                  h('span', { className: 'ai-flow-popover-coding-spinner' }),
                  h('span', { className: 'ai-flow-popover-coding-label' }, 'Coding')
                ),
                h(CodingLines, null)
              )
            : content,
          runtimeNodeOutput !== null && runtimeNodeOutput !== undefined
            ? h(NodeOutputPanel, {
                output: runtimeNodeOutput,
                isConnectionDragging: !!connectionDrag,
                showFixButton: !readOnly && isCustomNode(node) && !nodeLocked,
                isFixing: !!(nodeFixState && nodeFixState.active),
                onOpenEditModal: !readOnly && isCustomNode(node) && !nodeLocked ? handleOpenEditWithOutput.bind(null, node.id) : null,
                onRerunFromNode: !isEmbed && onRerunFromNode ? function() { onRerunFromNode(node.id); } : null,
                variableNames: variableNames,
                onSetToVariable: (readOnly || nodeLocked) ? null : function(payload) { addSetVariableForOutput(node.id, payload); }
              })
            : null,
          getNodeAnchors(node).map(function(anchor) {
            var nodeOutgoing = isConditionNode(node) ? connections.filter(function(c) { return c && String(c.from) === String(node.id); }) : null;
            var canStartConnection = canStartConnectionFromAnchor(node, anchor, conditionAxesMap, nodeOutgoing);
            var classifierHandleStyle = getClassifierHandleStyle(node, anchor);
            return h(
              'button',
              {
                key: node.id + '-handle-' + anchor,
                className: 'connector-handle connector-handle-' + anchor + getAnchorClass(node, anchor, conditionAxesMap),
                style: classifierHandleStyle,
                onMouseDown: function(event) {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!canStartConnection) return;
                  startConnectionDrag(node.id, anchor, event);
                },
                onTouchStart: function(event) {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!canStartConnection) return;
                  startConnectionDragTouch(node.id, anchor, event);
                },
                title: getAnchorTitle(node, anchor, conditionAxesMap)
              },
              h('span', { className: 'connector-handle-core' }),
              getAnchorLabel(node, anchor, conditionAxesMap)
                ? h('span', { className: 'connector-handle-label' }, getAnchorLabel(node, anchor, conditionAxesMap))
                : null
            );
          }),
          // Tool nodes (wired to a Tool Agent's bottom anchor) also show the
          // node action toolbar so they can be deleted directly. deleteNode
          // still cascades the agent's tools when the agent itself is removed.
          // Hidden entirely in read-only (Live) mode — no structural edits.
          !readOnly && selectedNodeIds[node.id] && Object.keys(selectedNodeIds).length === 1
          ? h('div', { className: 'node-action-toolbar', onMouseDown: function(e) { e.stopPropagation(); } },
                isDuplicableNode(node) && !nodeLocked
                  ? h('button', {
                      className: 'node-action-icon-btn node-duplicate-btn',
                      'aria-label': 'Duplicate node',
                      onMouseDown: duplicateCustomNode.bind(null, node.id),
                      onClick: duplicateCustomNode.bind(null, node.id),
                      title: 'Duplicate node'
                    },
                      h('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                        h('rect', { x: 9, y: 9, width: 12, height: 12, rx: 2 }),
                        h('path', { d: 'M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1' })
                      )
                    )
                  : null,
                isCustomNode(node) && nodeLocked
                  ? h('button', {
                      className: 'node-action-icon-btn node-lock-btn',
                      'aria-label': 'Unlock node',
                      onMouseDown: unlockDuplicateNode.bind(null, node.id),
                      onClick: unlockDuplicateNode.bind(null, node.id),
                      title: 'Locked to source. Click to unlock.'
                    }, h(LockIcon))
                  : null,
                isCustomNode(node) && !nodeLocked && onSaveNodeAsTemplate
                  ? h('button', {
                      className: 'node-action-icon-btn node-save-template-btn',
                      'aria-label': 'Save as reusable preset node',
                      title: 'Save as reusable preset node',
                      onClick: function(e) {
                        e.stopPropagation();
                        onSaveNodeAsTemplate({
                          kind: node.kind || 'workflow-node',
                          nodeType: node.nodeType || 'card',
                          category: 'custom',
                          title: node.title || node.label || 'Custom Node',
                          subtitle: node.subtitle || 'Custom node',
                          label: node.label || node.title || 'Custom Node',
                          icon: node.icon || 'custom',
                          code: node.code || '',
                          hyperparameters: Array.isArray(node.hyperparameters) ? node.hyperparameters : [],
                          generationPrompt: node.generationPrompt || '',
                          changeHistory: Array.isArray(node.changeHistory) ? node.changeHistory : [],
                          logoUrl: node.logoUrl || ''
                        });
                      }
                    },
                      h('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                        h('path', { d: 'M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z' })
                      )
                    )
                  : null,
                h('button', {
                  className: 'node-action-icon-btn node-delete-btn',
                  'aria-label': 'Delete node',
                  onMouseDown: deleteNode.bind(null, node.id),
                  onClick: deleteNode.bind(null, node.id),
                  title: 'Delete object'
                },
                  h('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                    h('polyline', { points: '3 6 5 6 21 6' }),
                    h('path', { d: 'M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6' }),
                    h('path', { d: 'M10 11v6M14 11v6' }),
                    h('path', { d: 'M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2' })
                  )
                )
              )
            : null
          ,
          selectedNodeIds[node.id] && Object.keys(selectedNodeIds).length === 1 && isResizableNode(node)
            ? ['right', 'left'].map(function(edge) {
                return h('button', {
                  key: node.id + '-resize-' + edge,
                  className: 'node-resize-handle node-resize-handle-' + edge,
                  onMouseDown: startNodeResize.bind(null, node, edge),
                  onTouchStart: startNodeResizeTouch.bind(null, node, edge),
                  title: 'Resize from ' + edge
                });
              })
            : null
        );
      }),
      resolvedConnections.map(function(connection) {
        var animatingIds2 = (collapsingAnim ? collapsingAnim.nodeIds : []).concat(expandingAnim ? expandingAnim.nodeIds : []);
        if (animatingIds2.length && (animatingIds2.indexOf(connection.fromId) !== -1 || animatingIds2.indexOf(connection.toId) !== -1)) return null;
        if (selectedConnectionId !== connection.id) return null;
        if (!Array.isArray(connection.bendPoints) || !connection.bendPoints.length) return null;
        return connection.bendPoints.map(function(point, index) {
          return h('button', {
            key: 'conn-bend-' + connection.id + '-' + index,
            className: 'connection-bend-handle',
            style: {
              left: (point.x - 8) + 'px',
              top: (point.y - 8) + 'px'
            },
            onMouseDown: startBendDrag.bind(null, connection.id, index),
            onTouchStart: startBendDragTouch.bind(null, connection.id, index),
            onDoubleClick: removeConnectionBend.bind(null, connection.id, index),
            title: 'Drag to shape curve (double-click to remove)'
          }, h('span', { className: 'connection-bend-handle-core' }));
        });
      }),
      // Selected-connection action toolbar (Delete / Run & Wait / Cut Data Flow /
      // Select Field) + reconnect handle are all structural edits — hidden in
      // Live mode (read-only; only variable values are editable).
      (readOnly ? [] : resolvedConnections).map(function(connection) {
        if (selectedConnectionId !== connection.id) return null;
        // Tool wires have no connector actions — the Tool Agent owns the edge.
        if (connection.isToolWire) return null;
        return h(
          React.Fragment,
          { key: 'selected-connection-ui-' + connection.id },
          h(
            'button',
            {
              className: 'connection-end-handle connection-end-handle-active',
              style: {
                left: (connection.to.x - 7) + 'px',
                top: (connection.to.y - 7) + 'px'
              },
              onMouseDown: startReconnectDrag.bind(null, connection),
              onTouchStart: startReconnectDragTouch.bind(null, connection),
              title: 'Drag to transfer connector'
            },
            h('span', { className: 'connector-handle-core' })
          ),
          h(
            'div',
            {
              className: 'connection-action-group',
              style: (connectionClickPos && selectedConnectionId === connection.id)
                ? { left: Math.round(connectionClickPos.x) + 'px', top: Math.round(connectionClickPos.y - 10) + 'px' }
                : getConnectionActionGroupStyle(connection)
            },
            h(
              'button',
              {
                className: 'connection-action-btn connection-delete-btn',
                onClick: deleteConnection.bind(null, connection.id),
                title: 'Delete connector'
              },
              'Delete'
            ),
            h(
              'button',
              {
                className: 'connection-action-btn connection-nowait-btn' + (connection.noWait ? ' connection-nowait-btn-active' : ''),
                onClick: toggleConnectionNoWait.bind(null, connection.id),
                title: connection.noWait ? 'This step runs in the background — click to wait for it to finish' : 'Click to run this step in the background without waiting',
                'aria-pressed': connection.noWait ? 'true' : 'false'
              },
              connection.noWait ? 'Don\'t Wait' : 'Run & Wait'
            ),
            h(
              'button',
              {
                className: 'connection-action-btn connection-nodata-btn' + (connection.noData ? ' connection-nodata-btn-active' : ''),
                onClick: toggleConnectionNoData.bind(null, connection.id),
                title: connection.noData ? 'This connector now sends an empty string to the next node' : 'Send an empty string instead of this connector output',
                'aria-pressed': connection.noData ? 'true' : 'false'
              },
              'Cut Data Flow'
            ),
            h(
              'button',
              {
                className: 'connection-action-btn connection-field-btn' + (connection.propPath ? ' connection-field-btn-active' : ''),
                onClick: function() {
                  var sourceOutput = nodeOutputById[connection.fromId];
                  if (sourceOutput !== null && sourceOutput !== undefined && typeof sourceOutput === 'object') {
                    setPropPickerState({
                      connectionId: connection.id,
                      output: sourceOutput,
                      sourceId: connection.fromId,
                      targetNodeId: connection.toId
                    });
                  }
                },
                title: connection.propPath ? ('Filtering: .' + connection.propPath + ' — click to change') : 'Select which field flows through this connector'
              },
              connection.propPath ? '\u00b7' + connection.propPath.split('.').pop() : 'Select Field'
            )
          )
        );
      }),
      resolvedConnections.map(function(connection) {
        if (!connection.propPath) return null;
        var mid = bezierMidpoint(connection.from, connection.to, connection.fromAnchor, connection.toAnchor);
        var midX = mid.x;
        var midY = mid.y;
        var connectionHoverText = formatOutputHoverText(nodeOutputById[connection.fromId], connection.propPath);
        return h('button', {
          key: 'prop-label-' + connection.id,
          className: 'conn-prop-label'
            + (hasFlowHighlight && downstreamFlowHighlight.connIds[connection.id] ? ' conn-flow-highlight' : '')
            + (hasFlowHighlight && !downstreamFlowHighlight.connIds[connection.id] ? ' conn-flow-dim' : '')
            + (focusActive && !(focusedNodeIdSet[connection.fromId] && focusedNodeIdSet[connection.toId]) ? ' conn-var-dim' : ''),
          style: { left: (midX - 40) + 'px', top: (midY - 10) + 'px' },
          title: connectionHoverText,
          // Live mode is read-only — the field label stays VISIBLE but clicking it
          // no longer opens the prop picker (changing the field is structural).
          onMouseDown: readOnly ? undefined : function(e) {
            e.stopPropagation();
            var sourceOutput = nodeOutputById[connection.fromId];
            if (sourceOutput !== null && sourceOutput !== undefined && typeof sourceOutput === 'object') {
              setPropPickerState({
                connectionId: connection.id,
                output: sourceOutput,
                sourceId: connection.fromId,
                targetNodeId: connection.toId
              });
            }
          }
        }, h('span', { style: connection.noData ? { textDecoration: 'line-through', opacity: 0.6 } : null }, '.' + connection.propPath));
      }),
      // Insert-node-on-connection "+" buttons are a structural edit — hidden in
      // Live mode (read-only; only variable VALUES are editable there).
      (readOnly ? [] : resolvedConnections).map(function(connection) {
        if (aiPromptState && aiPromptState.insertConnectionId === connection.id) return null;
        var isHovered = hoveredConnectionId === connection.id;
        var isSelected = selectedConnectionId === connection.id;
        if (!isHovered && !isSelected) return null;
        var insertMid = bezierMidpoint(connection.from, connection.to, connection.fromAnchor, connection.toAnchor);
        // A condition's YES/NO label also lives at the midpoint, so on those
        // connectors slide the insert button further along the curve (toward the
        // target) to clear the label instead of stacking on top of it. The
        // inserted node still drops at the true midpoint (insertMid).
        var insertBtnPos = insertMid;
        var insertFromNode = nodeById[connection.fromId];
        if (insertFromNode && isConditionNode(insertFromNode)
          && String(connection.fromAnchor || '').trim().toLowerCase() !== getConditionAxes(insertFromNode.id, conditionAxesMap).input) {
          var ahead = bezierPointAt(connection.from, connection.to, connection.fromAnchor, connection.toAnchor, 0.62);
          var adx = ahead.x - insertMid.x;
          var ady = ahead.y - insertMid.y;
          var alen = Math.sqrt(adx * adx + ady * ady) || 1;
          insertBtnPos = { x: Math.round(insertMid.x + (adx / alen) * 30), y: Math.round(insertMid.y + (ady / alen) * 30) };
        }
        return h('button', {
          key: 'connection-insert-btn-' + connection.id,
          className: 'connection-insert-btn'
            + (isSelected ? ' connection-insert-btn-selected' : '')
            + (hasFlowHighlight && !downstreamFlowHighlight.connIds[connection.id] ? ' conn-flow-dim' : ''),
          style: { left: (insertBtnPos.x - 10) + 'px', top: (insertBtnPos.y - 10) + 'px' },
          title: 'Insert node here',
          'aria-label': 'Insert node on this connection',
          onMouseEnter: function() { setHoveredConnectionId(connection.id); },
          onMouseLeave: function() {
            setHoveredConnectionId(function(prev) { return prev === connection.id ? null : prev; });
          },
          onMouseDown: function(event) {
            event.preventDefault();
            event.stopPropagation();
            handleInsertNodeOnConnection({
              connection: connection,
              midpoint: insertMid,
              event: event
            });
          }
        },
          h('svg', {
            viewBox: '0 0 24 24',
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 2.4,
            strokeLinecap: 'round'
          },
            h('path', { d: 'M12 5v14' }),
            h('path', { d: 'M5 12h14' })
          )
        );
      }),
      resolvedConnections.map(function(connection) {
        var fromNode = nodeById[connection.fromId];
        if (!fromNode || !isConditionNode(fromNode)) return null;
        var axes = getConditionAxes(fromNode.id, conditionAxesMap);
        var fromAnchor = String(connection.fromAnchor || '').trim().toLowerCase();
        if (fromAnchor === axes.input) return null;
        // Per-connection branch wins over the per-node anchor hint, so siblings
        // sharing an anchor can carry different labels (e.g. all "no").
        var explicitBranch = connection.branch ? String(connection.branch).trim().toLowerCase() : '';
        var isYes = explicitBranch === 'yes' || (!explicitBranch && fromAnchor === axes.yes);
        var mid = bezierMidpoint(connection.from, connection.to, connection.fromAnchor, connection.toAnchor);
        return h('button', {
          key: 'cond-toggle-' + connection.id,
          className: 'conn-branch-toggle'
            + (isYes ? ' conn-branch-toggle-yes' : ' conn-branch-toggle-no')
            + (hasFlowHighlight && downstreamFlowHighlight.connIds[connection.id] ? ' conn-flow-highlight' : '')
            + (hasFlowHighlight && !downstreamFlowHighlight.connIds[connection.id] ? ' conn-flow-dim' : '')
            + (focusActive && !(focusedNodeIdSet[connection.fromId] && focusedNodeIdSet[connection.toId]) ? ' conn-var-dim' : ''),
          style: { left: (mid.x - 16) + 'px', top: (mid.y - 10) + 'px' },
          title: isYes ? 'Yes branch — click to flip this connector to NO' : 'No branch — click to flip this connector to YES',
          onMouseDown: function(e) {
            e.stopPropagation();
            e.preventDefault();
            setConnectionBranch(connection.id, isYes ? 'no' : 'yes');
          }
        }, isYes ? 'yes' : 'no');
      }),
      (readOnly ? [] : futureConnectors).filter(function(future) {
        return !isFutureConnectorClaimed(future);
      }).map(function(future) {
        var isFutureDropTarget = futureDropTargetId === future.id;
        return h('button', {
          key: 'future-btn-' + future.id,
          className: 'future-connector-btn' + (isFutureDropTarget ? ' future-connector-btn-drop-target' : ''),
          style: {
            left: future.buttonLeft + 'px',
            top: future.buttonTop + 'px'
          },
          onMouseDown: handleGenerateFlowWithAi.bind(null, future),
          onDragOver: function(event) {
            if (!dragData.hasType(event.dataTransfer, dragData.TEMPLATE_TYPE)) return;
            event.preventDefault();
            try { event.dataTransfer.dropEffect = 'copy'; } catch (err) {}
            setFutureDropTargetId(future.id);
          },
          onDragEnter: function(event) {
            if (!dragData.hasType(event.dataTransfer, dragData.TEMPLATE_TYPE)) return;
            event.preventDefault();
            setFutureDropTargetId(future.id);
          },
          onDragLeave: function(event) {
            if (event.currentTarget.contains(event.relatedTarget)) return;
            setFutureDropTargetId(function(current) {
              return current === future.id ? null : current;
            });
          },
          onDrop: function(event) {
            var templateId = dragData.getData(event.dataTransfer, dragData.TEMPLATE_TYPE);
            var template = findTemplateById(templateId);
            setFutureDropTargetId(null);
            if (!template) return;
            dropTemplateOnFutureConnector(template, future, event);
          },
          title: 'Generate next flow with AI'
        },
          h('span', { className: 'future-connector-btn-icon', 'aria-hidden': 'true' },
            h(NodeIcon, { type: 'agent', size: 14, strokeWidth: 1.8 })
          ),
          h('span', { className: 'future-connector-btn-label' }, "What's Next?")
        );
      }),
      // "+ add tool" connector buttons on Tool Agent bottom anchors. Clicking
      // opens the AI Flow Builder (custom-node generator) seeded for a tool —
      // the generated node wires from the bottom anchor, so it becomes a tool.
      (readOnly ? [] : toolConnectorButtons).map(function(btn) {
        return h('button', {
          key: 'tool-add-' + btn.id,
          className: 'tool-add-btn',
          style: { left: btn.buttonLeft + 'px', top: btn.buttonTop + 'px' },
          title: 'Add a tool node',
          onMouseDown: function(event) {
            event.preventDefault();
            event.stopPropagation();
            handleGenerateFlowWithAi(btn.futureConnector, event, { toolMode: true });
          }
        },
          h('span', { className: 'tool-add-btn-plus', 'aria-hidden': 'true' },
            h('svg', { width: 14, height: 14, viewBox: '0 0 12 12', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' },
              h('path', { d: 'M6 2v8M2 6h8' })
            )
          )
        );
      }),
      aiCodingState
        ? h(CodingNodeCard, { position: aiCodingState.position })
        : null,

      // Marquee selection rectangle
      marquee
        ? h('div', {
            className: 'marquee-selection',
            style: {
              left: marquee.x + 'px',
              top: marquee.y + 'px',
              width: marquee.width + 'px',
              height: marquee.height + 'px'
            }
          })
        : null,

      // Collapsed group cards (act like nodes)
      collapsedGroups.map(function(group) {
        var gPos = group.position || { left: 0, top: 0 };
        var gIcons = Array.isArray(group.icons) ? group.icons : [];
        var groupColor = normalizeGroupColor(group.color);
        var isGroupSelected = !!selectedNodeIds[group.id];
        var runningNodeInGroup = null;
        if (executionState && executionState.status === 'running') {
          for (var gi = 0; gi < group.nodeIds.length; gi++) {
            var nid = group.nodeIds[gi];
            if (nodeStatusById[nid] === 'running') {
              runningNodeInGroup = nodeById[nid] || null;
              break;
            }
          }
        }
        var anyGroupNodeCompleted = false;
        var anyGroupNodeFailed = false;
        var allGroupNodesCompleted = true;
        var groupTotalDurationMs = 0;
        if (!runningNodeInGroup && executionState) {
          for (var gj = 0; gj < group.nodeIds.length; gj++) {
            var gnid = group.nodeIds[gj];
            var st = nodeStatusById[gnid];
            if (st === 'completed') {
              anyGroupNodeCompleted = true;
              var gTiming = nodeTimingById[gnid];
              if (gTiming && Number.isFinite(Number(gTiming.durationMs))) {
                groupTotalDurationMs += Number(gTiming.durationMs);
              }
            } else {
              allGroupNodesCompleted = false;
            }
            if (st === 'failed') anyGroupNodeFailed = true;
          }
        } else {
          allGroupNodesCompleted = false;
        }
        var groupBadgeStatus = runningNodeInGroup ? 'running' : (anyGroupNodeFailed ? 'failed' : (allGroupNodesCompleted && anyGroupNodeCompleted ? 'completed' : null));
        var groupBadgeLabel = null;
        if (runningNodeInGroup) {
          var runTiming = nodeTimingById[runningNodeInGroup.id];
          if (runTiming && Number.isFinite(Number(runTiming.startedAt))) {
            groupBadgeLabel = formatDurationMs(timerNowMs - Number(runTiming.startedAt));
          }
        } else if (allGroupNodesCompleted && anyGroupNodeCompleted && groupTotalDurationMs > 0) {
          groupBadgeLabel = formatDurationMs(groupTotalDurationMs);
        }
        return h('div', {
          key: 'group-card-' + group.id,
          className: 'node-position node-draggable node-group-card' +
            (isGroupSelected ? ' node-selected' : '') +
            (isGroupSelected && Object.keys(selectedNodeIds).length > 1 ? ' node-multi-selected' : '') +
            (group.expanded ? ' node-group-expanded' : '') +
            (runningNodeInGroup ? ' node-group-running' : '') +
            (!runningNodeInGroup && anyGroupNodeFailed ? ' node-group-failed' : '') +
            (!runningNodeInGroup && !anyGroupNodeFailed && anyGroupNodeCompleted ? ' node-group-completed' : '') +
            (repellingNodeIds && repellingNodeIds.indexOf('__group__' + group.id) !== -1 ? ' node-group-repelling' : ''),
          style: {
            left: gPos.left + 'px',
            top: gPos.top + 'px',
            '--group-color': groupColor,
            '--group-color-strong': hexToRgba(groupColor, 0.42),
            '--group-color-soft': hexToRgba(groupColor, 0.22),
            '--group-color-faint': hexToRgba(groupColor, 0.14),
            '--group-color-border': hexToRgba(groupColor, 0.52),
            '--group-color-text': hexToRgba(groupColor, 0.95)
          },
          onMouseDown: function(e) {
            e.stopPropagation();
            setGroupColorMenuId(null);
            if (!readOnly) {
              var expandedGroups = (workflowStateRef.current.groups || []).filter(function(g) { return g.expanded && g.id !== group.id; });
              for (var eg = 0; eg < expandedGroups.length; eg++) {
                toggleExpandGroup(expandedGroups[eg].id);
              }
            }
            if (e.shiftKey) {
              setSelectedNodeIds(function(prev) {
                var next = Object.assign({}, prev);
                if (next[group.id]) { delete next[group.id]; } else { next[group.id] = true; }
                return next;
              });
            } else {
              var gs = {}; gs[group.id] = true;
              setSelectedNodeIds(gs);
            }
            setSelectedConnectionId(null);
            startGroupDrag(group.id, e);
          },
          onDoubleClick: function(e) {
            e.stopPropagation();
            if (!readOnly && !group.expanded) toggleExpandGroup(group.id);
          },
          ref: setNodeRef(group.id)
        },
          h(ExecutionBadge, {
            status: groupBadgeStatus,
            label: groupBadgeLabel
          }),
          group.expanded
            ? null
            : h('div', { className: 'node-group-card-inner' },
                h('div', { className: 'node-group-icons' },
                  [0, 1, 2, 3].map(function(idx) {
                    var icon = gIcons[idx];
                    if (!icon) return h('span', { key: idx, className: 'node-group-icon-square node-group-icon-empty' });
                    return h('span', {
                      key: idx,
                      className: 'node-group-icon-square',
                      style: { background: icon.color || '#6f8fb2' }
                    }, h(NodeIcon, { type: icon.type, size: 14, strokeWidth: 1.8 }));
                  })
                ),
                h('div', { className: 'node-group-info' },
                  editingGroupId === group.id
                    ? h('input', {
                        className: 'node-group-name-input',
                        type: 'text',
                        value: group.name || '',
                        autoFocus: true,
                        onMouseDown: function(e) { e.stopPropagation(); },
                        onChange: function(e) { renameGroup(group.id, e.target.value); },
                        onBlur: function() { setEditingGroupId(null); },
                        onKeyDown: function(e) {
                          if (e.key === 'Enter' || e.key === 'Escape') {
                            setEditingGroupId(null);
                            e.target.blur();
                          }
                        }
                      })
                    : h('span', {
                        className: 'node-group-name',
                        onDoubleClick: function(e) {
                          e.stopPropagation();
                          if (!readOnly) setEditingGroupId(group.id);
                        }
                      }, group.name),
                  runningNodeInGroup
                    ? h('span', { className: 'node-group-running-label' },
                        h('span', { className: 'node-group-running-dot' }),
                        runningNodeInGroup.title || 'Running...'
                      )
                    : h('span', { className: 'node-group-count' }, group.nodeIds.length + ' nodes')
                )
              ),
          !readOnly && isGroupSelected && !group.expanded
            ? h('div', { className: 'node-action-toolbar', onMouseDown: function(e) { e.stopPropagation(); } },
                h('div', { className: 'node-group-color-dropdown' },
                  h('button', {
                    type: 'button',
                    className: 'node-group-color-trigger',
                    title: 'Change group color',
                    onMouseDown: function(e) { e.stopPropagation(); },
                    onClick: function(e) {
                      e.stopPropagation();
                      setGroupColorMenuId(function(current) {
                        return current === group.id ? null : group.id;
                      });
                    }
                  },
                    h('span', { className: 'node-group-color-trigger-label' }, 'Border Color'),
                    h('span', { className: 'node-group-color-swatch node-group-color-swatch-trigger', style: { background: groupColor } })
                  ),
                  groupColorMenuId === group.id
                    ? h('div', {
                        className: 'node-group-color-menu',
                        onMouseDown: function(e) { e.stopPropagation(); }
                      },
                        GROUP_COLOR_OPTIONS.map(function(color) {
                          var isActive = normalizeGroupColor(color) === groupColor;
                          return h('button', {
                            key: color,
                            type: 'button',
                            className: 'node-group-color-option' + (isActive ? ' node-group-color-option-active' : ''),
                            title: color,
                            onClick: function(e) {
                              e.stopPropagation();
                              setGroupColor(group.id, color);
                              setGroupColorMenuId(null);
                            }
                          }, h('span', {
                            className: 'node-group-color-swatch node-group-color-swatch-option',
                            style: { background: color }
                          }));
                        })
                      )
                    : null
                ),
                h('button', {
                  className: 'node-group-ungroup-btn-inline',
                  onClick: ungroupNodes.bind(null, group.id),
                  title: 'Ungroup and restore nodes'
                }, 'Ungroup')
              )
            : null,
          !group.expanded
            ? ['top', 'bottom', 'left', 'right'].map(function(anchor) {
                return h('button', {
                  key: group.id + '-handle-' + anchor,
                  className: 'connector-handle connector-handle-' + anchor,
                  onMouseDown: function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    startConnectionDrag(group.id, anchor, e);
                  }
                },
                  h('span', { className: 'connector-handle-core' })
                );
              })
            : null
        );
      }),

      // Multi-selection toolbar
      (function() {
        var selIds = Object.keys(selectedNodeIds);
        if (selIds.length < 2) return null;
        var anchorId = lastSelectedNodeId && selectedNodeIds[lastSelectedNodeId] ? lastSelectedNodeId : selIds[0];
        var anchorRect = rectById[anchorId];
        if (!anchorRect) return null;
        var tbLeft = anchorRect.left + anchorRect.width / 2;
        var tbTop = anchorRect.top - 48;
        return h('div', {
          className: 'multi-select-toolbar',
          style: {
            left: tbLeft + 'px',
            top: tbTop + 'px'
          },
          onMouseDown: function(e) { e.stopPropagation(); }
        },
          h('button', {
            className: 'multi-select-btn multi-select-group-btn',
            onClick: groupSelectedNodes,
            title: 'Group selected nodes'
          }, 'Group'),
          h('button', {
            className: 'multi-select-btn multi-select-delete-btn',
            onClick: deleteSelectedNodes,
            title: 'Delete selected nodes'
          }, 'Delete')
        );
      })()
    )
    ) // canvas-zoom-layer
    ), // canvas-world

    // Empty canvas state - show "Plan a Workflow" when no nodes exist.
    // Suppressed while loading so the placeholder never flashes before the
    // workflow has hydrated — the loading spinner covers that window instead.
    // Also hidden in read-only (Live) mode: its Plan-with-AI / drag-a-Start
    // prompts are editing actions that don't apply to the published view (an
    // empty live version shows the "Publish" call-to-action in the LIVE banner).
    showEmptyState
      ? h('div', { className: 'canvas-empty-state' },
          h('div', { className: 'canvas-empty-inner' },
            // Decorative workflow illustration
            h('div', { className: 'canvas-empty-illustration' },
              h('svg', { width: 120, height: 64, viewBox: '0 0 120 64', fill: 'none' },
                // Node 1 (start)
                h('rect', { x: 2, y: 22, width: 28, height: 20, rx: 6, fill: '#e8f5e9', stroke: '#66bb6a', strokeWidth: 1.5 }),
                h('circle', { cx: 16, cy: 32, r: 3, fill: '#66bb6a' }),
                // Connector 1->2
                h('line', { x1: 30, y1: 32, x2: 42, y2: 20, stroke: '#cbd5e1', strokeWidth: 1.5 }),
                h('line', { x1: 30, y1: 32, x2: 42, y2: 44, stroke: '#cbd5e1', strokeWidth: 1.5 }),
                // Node 2a (top branch)
                h('rect', { x: 42, y: 6, width: 28, height: 20, rx: 6, fill: '#e3f2fd', stroke: '#64b5f6', strokeWidth: 1.5 }),
                h('rect', { x: 50, y: 12, width: 12, height: 2, rx: 1, fill: '#90caf9' }),
                h('rect', { x: 50, y: 17, width: 8, height: 2, rx: 1, fill: '#90caf9' }),
                // Node 2b (bottom branch)
                h('rect', { x: 42, y: 38, width: 28, height: 20, rx: 6, fill: '#e3f2fd', stroke: '#64b5f6', strokeWidth: 1.5 }),
                h('rect', { x: 50, y: 44, width: 12, height: 2, rx: 1, fill: '#90caf9' }),
                h('rect', { x: 50, y: 49, width: 8, height: 2, rx: 1, fill: '#90caf9' }),
                // Connector 2->3
                h('line', { x1: 70, y1: 20, x2: 82, y2: 32, stroke: '#cbd5e1', strokeWidth: 1.5 }),
                h('line', { x1: 70, y1: 44, x2: 82, y2: 32, stroke: '#cbd5e1', strokeWidth: 1.5 }),
                // Node 3 (end)
                h('rect', { x: 82, y: 22, width: 28, height: 20, rx: 6, fill: '#fff3e0', stroke: '#ffb74d', strokeWidth: 1.5 }),
                h('rect', { x: 90, y: 28, width: 12, height: 2, rx: 1, fill: '#ffcc80' }),
                h('rect', { x: 90, y: 33, width: 8, height: 2, rx: 1, fill: '#ffcc80' })
              )
            ),
            h('div', { className: 'canvas-empty-title' }, 'Build your workflow'),
            h('div', { className: 'canvas-empty-desc' }, 'Describe what you want to automate in your own words, or start manually.'),
            h('div', { className: 'canvas-empty-actions' },
              h('button', {
                className: 'canvas-empty-plan-btn',
                onClick: function() { setPlannerOpen(true); }
              },
                h('svg', { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                  h('path', { d: 'M12 2L2 7l10 5 10-5-10-5z' }),
                  h('path', { d: 'M2 17l10 5 10-5' }),
                  h('path', { d: 'M2 12l10 5 10-5' })
                ),
                'Automate a task'
              ),
              h('div', { className: 'canvas-empty-hint' }, 'or drag a Start node to begin manually')
            )
          )
        )
      : null,

    // One-time "how to" intro over the empty canvas (first visit only).
    wizardIntroOpen
      ? h(WizardIntroModal, { onClose: function() { setWizardIntroOpen(false); } })
      : null,

    h(EditorModals, {
      aiPromptState: aiPromptState,
      updateAiPrompt: updateAiPrompt,
      updateAiAgentMode: updateAiAgentMode,
      updateAiModelTier: updateAiModelTier,
      updateAiResponseFields: updateAiResponseFields,
      updateAiNodeKind: updateAiNodeKind,
      generateAiNodeFromPrompt: generateAiNodeFromPrompt,
      finalizeSingleNode: finalizeSingleNode,
      closeAiPrompt: closeAiPrompt,
      subscribeToPan: subscribeToPan,
      pendingAgentDrop: pendingAgentDrop,
      confirmAgentDrop: confirmAgentDrop,
      cancelAgentDrop: cancelAgentDrop,
      pendingClassifierDrop: pendingClassifierDrop,
      confirmClassifierDrop: confirmClassifierDrop,
      cancelClassifierDrop: cancelClassifierDrop,
      editingAgentNode: editingAgentNode,
      agentEditorPosition: agentEditorPosition,
      closeAgentEditor: closeAgentEditor,
      saveAgentParams: saveAgentParams,
      getEditorContextNodes: getEditorContextNodes,
      editingClassifierNode: editingClassifierNode,
      classifierEditorPosition: classifierEditorPosition,
      closeClassifierEditor: closeClassifierEditor,
      saveClassifierParams: saveClassifierParams,
      propPickerState: propPickerState,
      propPickerPosition: propPickerPosition,
      handlePropPickerSelect: handlePropPickerSelect,
      closePropPicker: closePropPicker,
      plannerOpen: plannerOpen,
      setPlannerOpen: setPlannerOpen,
      autoBuildWorkflow: autoBuildWorkflow,
      handleManualFix: handleManualFix,
      variableNodes: variableNodes
    }),

    // Hide the variables side panel and minimap while the UI iframe overlay
    // is showing — they're workflow-graph affordances and look out of place
    // floating on top of the rendered UI page.
    viewMode === 'ui' ? null : h(VariablesSidePanel, {
      variables: variableNodes,
      variableValuesByName: variableValuesByName,
      accessedVariableNames: accessedVariableNames,
      accessedVariableNodeId: accessedVariableNodeId,
      nodeStatusById: nodeStatusById,
      onAdd: addVariableNode,
      onParamChange: handleParamChange,
      onTitleChange: handleNodeTitleChange,
      onDelete: deleteNode,
      readOnly: isEmbed,
      collapsed: isVariablesPanelCollapsed,
      isRunning: isExecutionRunning,
      // Value field stays locked while the run is mid-flight (running,
      // paused, or failed) so the live snapshot can't desync from the runtime.
      // Editable again once the workflow finishes cleanly.
      isValueLocked: !!(executionState && (executionState.status === 'running' || executionState.status === 'paused' || executionState.status === 'failed')),
      onToggleCollapse: function() {
        setVariablesPanelCollapsedByUser(function(current) { return !current; });
      },
      onVariableHover: setHoveredVariableName,
      // Inverse highlight: when the user hovers a node on the canvas, fade
      // every row whose variable name isn't in this set. null = no node is
      // hovered, panel renders normally.
      highlightedVariableNames: hoveredNodeVariableNames,
      unusedVariableNames: unusedVariableNames
    }),

    viewMode === 'ui' ? null : h(WorkflowMiniMap, {
      nodes: miniMapNodes,
      rectById: rectById,
      canvasRef: canvasRef,
      panPosRef: panPosRef,
      zoomRef: zoomRef,
      connections: resolvedConnections,
      onNavigate: function(worldX, worldY) {
        var canvas = canvasRef.current;
        if (!canvas) return;
        var canvasW = canvas.clientWidth;
        var canvasH = canvas.clientHeight;
        var zoom = zoomRef.current || 1;
        var targetPanX = canvasW / 2 - worldX * zoom;
        var targetPanY = canvasH / 2 - worldY * zoom;
        autoPanAnimateTo(targetPanX, targetPanY, zoom, 400);
      }
    }),

    // The Run Log only appears once the run has finished (completed / failed /
    // stopped / paused) — it stays hidden while the workflow is running.
    viewMode !== 'ui'
      && executionState
      && (executionState.status === 'completed'
        || executionState.status === 'failed'
        || executionState.status === 'stopped'
        || executionState.status === 'paused')
      ? h(RunLogPanel, {
          lines: props.runLogLines || [],
          open: !!props.runLogOpen,
          onToggle: props.onToggleRunLog,
          onHoverLine: function(nodeId) { setRunLogFocusNodeId(nodeId || null); },
          onHoverLineLeave: function() { setRunLogFocusNodeId(null); }
        })
      : null,

    h(CanvasFooter, {
      ref: canvasFooterRef,
      user: props.user || null,
      historyOpen: historyOpen,
      setHistoryOpen: setHistoryOpen,
      showNodeDetails: showNodeDetails,
      setShowNodeDetails: setShowNodeDetails,
      logChatOpen: logChatOpen,
      setLogChatOpen: setLogChatOpen,
      workflowId: workflowId,
      executionState: executionState,
      publicEventStreamEnabled: publicEventStreamEnabled,
      listenIndicatorTone: listenIndicatorTone,
      listenIndicatorLabel: listenIndicatorLabel,
      listenIndicatorDetail: listenIndicatorDetail,
      historyEntries: historyEntries,
      historyPos: historyPos,
      onUndo: onUndo,
      onRedo: onRedo,
      onJumpHistory: onJumpHistory,
      onRunWorkflow: onRunWorkflow,
      onPauseWorkflow: onPauseWorkflow,
      isRunDisabled: isRunDisabled,
      isExecutionRunning: isExecutionRunning,
      isExecutionPaused: isExecutionPaused,
      canvasTool: canvasTool,
      setCanvasTool: setCanvasTool,
      zoomLevel: zoomLevel,
      changeZoom: changeZoom,
      zoomRef: zoomRef,
      onOpenUiPlanner: props.onOpenUiPlanner || null,
      existingUiPageId: props.existingUiPageId || '',
      onEditUiPage: props.onEditUiPage || null,
      viewMode: viewMode,
      onSetViewMode: setViewMode,
      uiWorkflowRunning: uiWorkflowRunning,
      onCheckSilentErrorChange: props.onCheckSilentErrorChange || null,
      checkSilentError: !!(workflowState && workflowState.checkSilentError),
      onFormatLayout: handleFormatLayout,
      copilotOpen: copilotOpen,
      onToggleCopilot: function() {
        // Footer toggle opens with a clean slate — never replay a first
        // message left over from a previous launcher-pill open.
        copilotInitialTextRef.current = '';
        setCopilotOpen(function(open) { return !open; });
      },
      isCodingInProgress: Object.keys(codingNodeIds).length > 0,
      // Live mode is read-only — lock structural footer controls (+ UI Page,
      // Format layout, History) the same way the canvas itself is locked.
      readOnly: readOnly,
      isEmbed: isEmbed
    }),

    // Branch-out blocked: the source node hasn't run, so the AI builder has no
    // output shape to build the new node from. Offer to run the workflow first.
    branchNeedsRunTitle
      ? h(ConfirmDialog, {
          title: 'Run the workflow first',
          message: 'The AI needs ' + branchNeedsRunTitle + '’s output shape to build the next node. Run the workflow once, then branch from it.',
          confirmLabel: 'Run workflow',
          cancelLabel: 'Cancel',
          danger: false,
          onConfirm: function() {
            setBranchNeedsRunTitle(null);
            if (typeof onRunWorkflow === 'function') onRunWorkflow();
          },
          onCancel: function() { setBranchNeedsRunTitle(null); }
        })
      : null,

    // QA confirmation popup — opens automatically when the auto-builder
    // finishes coding. Confirm starts the QA pass (Check Silent Errors on +
    // run); cancel just leaves the freshly-built workflow as-is.
    qaConfirmOpen
      ? h(ConfirmDialog, {
          title: 'Start QA on the new workflow?',
          message: 'I\'ll turn on Check Silent Errors and run the workflow so you can spot any issues right away.',
          confirmLabel: 'Start QA',
          cancelLabel: 'Not now',
          danger: false,
          onConfirm: handleConfirmStartQa,
          onCancel: function() { setQaConfirmOpen(false); }
        })
      : null,

    // UI-page delete confirmation (styled — never a native confirm).
    uiDeleteConfirmOpen
      ? h(ConfirmDialog, {
          title: 'Delete UI page?',
          message: 'This cannot be undone.',
          confirmLabel: 'Delete',
          cancelLabel: 'Cancel',
          danger: true,
          onConfirm: function() {
            setUiDeleteConfirmOpen(false);
            props.onDeleteUiPage(props.existingUiPageId);
            setUiOverlayPage(null);
            setViewMode('workflow');
          },
          onCancel: function() { setUiDeleteConfirmOpen(false); }
        })
      : null,

    // Missing-fields popup — shown when the user starts QA but at least one
    // required Start or custom-node hyperparameter is empty AND has no
    // default. Renders an input per field inline so the user fills them in
    // place; "Save & Start QA" persists the values via handleParamChange and
    // resumes the QA run.
    qaMissingFields && qaMissingFields.length > 0
      ? h(MissingFieldsDialog, {
          fields: qaMissingFields,
          onCancel: function() { setQaMissingFields(null); },
          onSubmit: function(values) {
            qaMissingFields.forEach(function(f) {
              var v = values[f.nodeId + '::' + f.paramKey];
              if (v == null) return;
              try { handleParamChange(f.nodeId, f.paramKey, v); } catch (e) {}
            });
            setQaMissingFields(null);
            // Re-enter the QA confirmation flow on the next tick so the
            // updated paramValues are committed before the validator re-runs.
            setTimeout(function() { setQaConfirmOpen(true); }, 50);
          }
        })
      : null,

    // Loading placeholder — shown only while fetching AND the user has
    // toggled to UI mode (first load or after planner save).
    (props.existingUiPageId && viewMode === 'ui' && uiOverlayLoading && !uiOverlayPage)
      ? h('div', { className: 'workflow-canvas-ui-overlay' },
          h('div', { className: 'workflow-canvas-ui-overlay-status' }, 'Loading UI page\u2026')
        )
      : null,

    // The actual iframe. Once loaded, it stays mounted (hidden via
    // display:none when viewMode !== 'ui') so toggling back and forth
    // preserves form state, workflow results, and scroll position.
    (props.existingUiPageId && uiOverlayPage)
      ? h('div', {
          className: 'workflow-canvas-ui-overlay',
          style: viewMode === 'ui' ? {} : { display: 'none' }
        },
          props.onDeleteUiPage
            ? h('button', {
                className: 'ui-overlay-delete-btn',
                title: 'Delete UI page',
                onClick: function() { setUiDeleteConfirmOpen(true); }
              },
                h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                  h('polyline', { points: '3 6 5 6 21 6' }),
                  h('path', { d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }),
                  h('line', { x1: 10, y1: 11, x2: 10, y2: 17 }),
                  h('line', { x1: 14, y1: 11, x2: 14, y2: 17 })
                ),
                'Delete'
              )
            : null,
          h('button', {
            className: 'ui-overlay-public-link',
            onClick: function() {
              if (uiOverlayPage.isPublic && uiOverlayPage.publicSlug) {
                window.open(getApiBasePath() + '/public/ui-page/' + encodeURIComponent(uiOverlayPage.publicSlug) + '.html', '_blank');
              } else {
                uiPageStorage.publishUiPage(props.existingUiPageId).then(function(published) {
                  if (published && published.publicSlug) {
                    setUiOverlayPage(function(prev) {
                      return Object.assign({}, prev, { isPublic: true, publicSlug: published.publicSlug });
                    });
                    window.open(getApiBasePath() + '/public/ui-page/' + encodeURIComponent(published.publicSlug) + '.html', '_blank');
                  }
                });
              }
            }
          },
            h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
              h('path', { d: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' }),
              h('polyline', { points: '15 3 21 3 21 9' }),
              h('line', { x1: 10, y1: 14, x2: 21, y2: 3 })
            ),
            'View In Public'
          ),
          h(UiPageRenderer, { value: uiOverlayPage.value, name: uiOverlayPage.name, streamCode: props.publicEventStreamCode || '' })
        )
      : null,
    logChatOpen && workflowId
      ? h(LogChatPopup, {
          workflowId: workflowId,
          onClose: function() { setLogChatOpen(false); },
          onFix: typeof props.onFixNode === 'function'
            ? function(nodeId, message) {
                setLogChatOpen(false);
                props.onFixNode(nodeId, message);
              }
            : null,
          onLocate: function(nodeId) {
            if (!nodeId) return;
            var canvas = canvasRef.current;
            if (!canvas) return;
            var canvasW = canvas.clientWidth;
            var canvasH = canvas.clientHeight;
            var mappedId = nodeIdToGroupId[nodeId] || nodeId;
            var targetZoom = Math.max(zoomRef.current || 1, 1);
            var targetX, targetY;
            var rect = rectByIdRef.current[mappedId];
            if (rect) {
              targetX = canvasW / 2 - rect.cx * targetZoom;
              targetY = canvasH / 2 - rect.cy * targetZoom;
            } else {
              var el = nodeRefs.current[mappedId];
              if (!el) return;
              var canvasRect = canvas.getBoundingClientRect();
              var nodeRect = el.getBoundingClientRect();
              var cxCanvas = (nodeRect.left + nodeRect.right) / 2 - canvasRect.left;
              var cyCanvas = (nodeRect.top + nodeRect.bottom) / 2 - canvasRect.top;
              var currentZoom = zoomRef.current || 1;
              var worldX = (cxCanvas - panPosRef.current.x) / currentZoom;
              var worldY = (cyCanvas - panPosRef.current.y) / currentZoom;
              targetX = canvasW / 2 - worldX * targetZoom;
              targetY = canvasH / 2 - worldY * targetZoom;
            }
            autoPanAnimateTo(targetX, targetY, targetZoom, 500);
          }
        })
      : null,

    // Workflow Doctor Chat — agentic popup. Reads the LIVE local
    // workflowState (possibly unsaved) so the agent sees what the user is
    // currently editing, and the latest executionLog for runtime hints.
    doctorChatOpen
      ? h(DoctorChatPopup, {
          workflowState: workflowState,
          executionLog: executionState && executionState.log ? executionState.log : null,
          // The live canvas DOM ref. Doctor uses it to capture a PNG
          // snapshot of the current canvas on the first user message so the
          // model has a visual of the layout/connections in addition to the
          // workflow JSON. Pass-through ref — never mutated by the popup.
          canvasRef: canvasRef,
          // Live canvas selection — when the user has node(s) selected, the
          // popup renders them as removable context chips above the input
          // and prepends them to the next message so the agent knows which
          // nodes the user is asking about.
          selectedNodeIds: selectedNodeIds,
          onClose: function() { setDoctorChatOpen(false); },
          onApplyWithAskCode: typeof props.onFixNode === 'function'
            ? function(payload) {
                if (!payload || !payload.nodeId) return;
                // Keep the doctor sidebar open so it can listen for the
                // AskCode result and post a completion summary when done.
                props.onFixNode(payload.nodeId, payload.askcodePrompt || '');
              }
            : null,
          // Single-button "Approve" path: hand the whole ordered list to
          // App.js, which opens AskCode for each step in turn. Doctor
          // sidebar stays open and watches; App.js posts a completion
          // summary back via doctorPendingNotice when the queue drains.
          onApplyAllWithAskCode: typeof props.onApplyFixPlanSteps === 'function'
            ? function(steps) {
                if (!Array.isArray(steps) || steps.length === 0) return;
                props.onApplyFixPlanSteps(steps);
              }
            : null,
          // Doctor's runWorkflow client tool — App.js owns the actual
          // start-execution + wait-for-terminal-status logic; we just hand
          // the function down to the popup, which packages it into the
          // tool ctx after the user confirms via chip.
          onRunWorkflow: typeof props.onDoctorRunWorkflow === 'function'
            ? props.onDoctorRunWorkflow
            : null,
          // Push-driven completion notice from App.js. When the AskCode
          // queue finishes, App.js sets a small payload here describing
          // what happened; the popup converts it into a chat message and
          // calls onConsumePendingNotice to clear it. This avoids needing
          // a ref/imperative handle to reach into the popup.
          pendingNotice: props.doctorPendingNotice || null,
          onConsumePendingNotice: typeof props.onConsumeDoctorPendingNotice === 'function'
            ? props.onConsumeDoctorPendingNotice
            : null,
          fixStatus: props.doctorFixStatus || null,
          // Click a <node> chip in a doctor reply → pan+zoom+select the
          // node on canvas. Same logic as the LogChatPopup onLocate path.
          onLocateNode: function(nodeId) {
            if (!nodeId) return;
            var canvas = canvasRef.current;
            if (!canvas) return;
            var canvasW = canvas.clientWidth;
            var canvasH = canvas.clientHeight;
            var mappedId = nodeIdToGroupId[nodeId] || nodeId;
            var targetZoom = Math.max(zoomRef.current || 1, 1);
            var targetX, targetY;
            var rect = rectByIdRef.current[mappedId];
            if (rect) {
              targetX = canvasW / 2 - rect.cx * targetZoom;
              targetY = canvasH / 2 - rect.cy * targetZoom;
            } else {
              var el = nodeRefs.current[mappedId];
              if (!el) return;
              var canvasRect = canvas.getBoundingClientRect();
              var nodeRect = el.getBoundingClientRect();
              var cxCanvas = (nodeRect.left + nodeRect.right) / 2 - canvasRect.left;
              var cyCanvas = (nodeRect.top + nodeRect.bottom) / 2 - canvasRect.top;
              var currentZoom = zoomRef.current || 1;
              var worldX = (cxCanvas - panPosRef.current.x) / currentZoom;
              var worldY = (cyCanvas - panPosRef.current.y) / currentZoom;
              targetX = canvasW / 2 - worldX * targetZoom;
              targetY = canvasH / 2 - worldY * targetZoom;
            }
            autoPanAnimateTo(targetX, targetY, targetZoom, 500);
            // Visual highlight: select the node so the existing selection
            // ring shows. Clears any previous selection so only the
            // mentioned node is emphasised.
            var selSet = {};
            selSet[mappedId] = true;
            setSelectedNodeIds(selSet);
            setLastSelectedNodeId(mappedId);
          },
          // Click a <variable> chip → highlight every node that reads or
          // writes the variable (uses the existing hover-highlight state).
          // Auto-clears after a few seconds so the canvas doesn't stay
          // dimmed if the user forgets about it.
          onHighlightVariable: function(name) {
            var clean = String(name || '').trim();
            if (!clean) return;
            setHoveredVariableName(clean);
            setTimeout(function() {
              setHoveredVariableName(function(cur) {
                return cur === clean ? null : cur;
              });
            }, 4000);
          }
        })
      : null,

    // Floating "Ask PopJP…" launcher pill — the copilot's resting state at
    // center-bottom. Clicking it opens the docked sidebar chat below. Hidden
    // while the copilot is open, in read-only (Live), and in UI view. Shown
    // ONLY after the workflow has actually RUN and produced outputs — there is
    // nothing for the copilot to explore until a run exists, so a fresh/empty
    // canvas ("Build your workflow" / Plan with AI) owns the screen instead.
    !copilotOpen && !readOnly && viewMode !== 'ui'
    && executionState
    && (executionState.status === 'completed' || executionState.status === 'failed'
        || executionState.status === 'stopped' || executionState.status === 'paused')
    && Object.keys(nodeOutputById).length > 0
      ? h(WorkflowCopilotLauncher, {
          onOpen: function(initialText) {
            copilotInitialTextRef.current = initialText || '';
            setCopilotOpen(true);
          }
        })
      : null,

    // Workflow Copilot — docked right-side sidebar chat that edits the workflow.
    // Applies the model's <edits> ops straight to workflowState; the existing
    // App.js effects handle dirty-marking, local cache and undo history.
    copilotOpen && !readOnly
      ? h(WorkflowCopilotPopup, {
          docked: true,
          initialText: copilotInitialTextRef.current,
          workflowState: workflowState,
          // Live canvas DOM ref — the screenshotWorkflow tool captures the
          // full graph from it (pass-through, never mutated by the popup).
          canvasRef: canvasRef,
          // Ephemeral live variable values (last run / Set Variable previews)
          // — lets copilot read tools see what each variable CURRENTLY holds.
          onGetVariableValues: function() { return variableValuesByNameRef.current; },
          // "Run QA test" button clicks the real play button in the footer, so
          // the run path is identical to a manual click (same disabled checks).
          onRunWorkflow: function() {
            if (canvasFooterRef.current) canvasFooterRef.current.clickRun();
          },
          onClose: function() {
            copilotInitialTextRef.current = '';
            setCopilotOpen(false);
          },
          // Custom/agent node creation runs the single-node planner discovery
          // (probe + Q&A) INSIDE the copilot sheet first, then codegen: the
          // popup builds the planner context via onBuildSingleNodeContext,
          // mounts AiFlowChatPanel to probe/ask, and only then calls
          // onGenerateCustomNode (generateNodeAfterSource) with the finished
          // spec to generate REAL code wired after the source node.
          onBuildSingleNodeContext: aiFlow.buildSingleNodeContextForSource,
          onGenerateCustomNode: aiFlow.generateNodeAfterSource,
          // Code edits on existing custom/agent nodes open AskCode on the
          // node with the copilot's instructions injected — the same
          // onFixNode path the Doctor chat uses to apply fixes.
          onEditNodeWithAskCode: typeof props.onFixNode === 'function'
            ? function(nodeId, prompt) { return props.onFixNode(nodeId, prompt, { autoClose: true }); }
            : null,
          // Lets the editNodeCode tool see whether the AskCode editor is
          // still open (and on which node) so it can report a dead edit
          // instead of polling blind until the timeout.
          onGetAskCodeEditorState: typeof props.getAskCodeEditorState === 'function'
            ? props.getAskCodeEditorState
            : null,
          onApplyEdits: function(edits) {
            // Read through the ref so a handler captured before/while the
            // model streamed still applies against the CURRENT state.
            var current = workflowStateRef.current || workflowState;
            var result = applyCopilotEdits(current, edits);
            if (result.applied.length > 0) {
              setWorkflowState(result.state);
              // Sync the ref NOW: setWorkflowState is async, but the copilot
              // compresses its resume snapshot immediately after the tool runs
              // — without this the model's overview lags one edit behind its
              // own change (it re-deletes/re-adds edges it can't see).
              workflowStateRef.current = result.state;
            }
            // state rides along so the popup can sync ITS ref synchronously too.
            return { applied: result.applied, errors: result.errors, state: result.state };
          }
        })
      : null,

    // Alert popup
    alertMsg
      ? h('div', { className: 'canvas-alert-popup' + (alertMsg.variant === 'success' ? ' canvas-alert-popup--success' : '') },
          h('div', { className: 'canvas-alert-icon' }, alertMsg.variant === 'success' ? '✓' : '!'),
          h('span', { className: 'canvas-alert-text' }, alertMsg.text),
          h('button', {
            className: 'canvas-alert-close',
            onClick: function() { setAlertMsg(null); if (alertTimerRef.current) { clearTimeout(alertTimerRef.current); alertTimerRef.current = null; } }
          }, '\u00d7')
        )
      : null,

    // Autopilot status pill \u2014 visible step indicator while the autopilot
    // driver runs (apply fix \u2192 close editor \u2192 rerun node).
    props.autopilotStatus
      ? h('div', { className: 'canvas-autopilot-status' },
          h('span', { className: 'canvas-autopilot-status-dot' }),
          h('span', { className: 'canvas-autopilot-status-label' }, 'Autopilot'),
          h('span', { className: 'canvas-autopilot-status-text' }, props.autopilotStatus)
        )
      : null
  );
}

module.exports = WorkflowCanvas;
